/* ============================================================================
   ANALYSE STRUCTURÉE (architecture v2 — « l'IA comprend, le code dessine »)
   ----------------------------------------------------------------------------
   L'Edge Function renvoie, EN PLUS de la scène, un champ `analyse` décrivant
   la situation : famille filet (format + coups), invasion (effectif + phase)
   ou parcours (éléments gymniques). Ce module VALIDE strictement l'analyse
   (enums fermés, cohérences) puis la convertit en scène via les moteurs de
   placement locaux. Toute analyse invalide renvoie null -> le pipeline actuel
   reprend la main (jamais de schéma bancal).
   Voir docs/architecture-v2-analyse-structuree.md.
   ========================================================================== */
import { racketFromAnalysis, buildRacketFeederDrill, buildTTDouble, buildPairExchange, buildCircuitLoop, invasionFromAnalysis, gymFromAnalysis, ateliersFromList, buildDribbleShootRelay, buildTargetThrow,
  dispositifFromAnalysis, zonesFromAnalysis, targetZonesFromAnalysis, buildShootingZones, pushObserver, observerFromText, defiActorsCount, cerceauxPerZoneCount, helperPartnerInText, decoupageFromAnalysis, defenseFromAnalysis, applyDiagonalMirror, parallelGroupsCount, applyParallelGroups, parallelCourtsInvasion, labelParallelCourts, multiCourtsFilet, coreSituationText, addWaitingColumns, addNetColumnSwap, addPostServiceRun, addPostReceptionRun, localParse, recognizedTemplate, preferAiAnalysis, isIncoherentScene, parseCourtDims, plotColorFromText, wantsStade, PISTE, buildRelaisAthle, laneAt, laneSpan, LANE_LAP } from "./parser.js";
// Garde-fou de fidélité (import circulaire analysis <-> fidelity assumé : les
// deux usages sont à l'INTÉRIEUR de fonctions, résolus à l'appel, jamais au
// chargement du module).
import { enforceRegulation } from "./regulation.js";
import { fidelityIssues, enforceShotDepth, enforceBackwardPass, enforceStrikeOrigin, enforceShotTarget, enforceShotOrigin, enforceCrossedRuns, expectedRoster, missingGear } from "./fidelity.js";

/* Heuristique : la réponse de l'IA est-elle inexploitable (à compléter par le
   moteur local) ? Peu/pas de joueurs, aucun mouvement, ou joueurs confondus.
   Déplacée depuis main.js pour que toute la DÉCISION DE ROUTAGE vive au même
   endroit, testable hors DOM. */
export function isWeakScene(d) {
  const ps = Array.isArray(d?.players) ? d.players : [];
  const as = Array.isArray(d?.actions) ? d.actions : [];
  if (ps.length < 2) return true;
  if (as.length === 0) return true;
  let bad = 0, total = 0;
  for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) {
    total++;
    if (Math.hypot((+ps[i].x) - (+ps[j].x), (+ps[i].y) - (+ps[j].y)) < 3) bad++;
  }
  return total > 0 && bad / total > 0.3;
}

/* POINT DE DÉCISION UNIQUE — quel interprète dessine la scène finale ?
   Reproduit À L'IDENTIQUE l'arbitrage historique du flux client (main.js),
   mais SANS effet de bord (ni meta, ni rendu) : renvoie seulement la scène
   choisie et la RAISON. Testable hors-ligne (banc test/eval-routes.mjs) ;
   destiné à devenir l'unique aiguillage (architecture v2, étape 3).
     reason ∈ "analysis" | "template" | "hybrid" | "edge"
   - "analysis" : l'analyse IA structurée a primé (preferAiAnalysis) ;
   - "template" : un template déterministe local a primé ;
   - "hybrid"   : repli local car la scène IA est faible/incohérente ;
   - "edge"     : on rend la scène brute de l'IA telle quelle.
   `result` est la réponse Edge brute (players[], actions[], analyse, …). */
// Templates à GÉOMÉTRIE SPÉCIALISÉE : leur placement local est la valeur déterministe
// et doit gagner MÊME face à une analyse IA « atelier/dispositif » à plusieurs cellules
// (ex. l'IA voit l'archerie comme « 3 ateliers », mais le gabarit local dessine la vraie
// cible ; idem agrès de saut/lancer, sprint balisé, vagues). Tout AUTRE template
// (squelettes de rotation, mais surtout set-plays invasion faussement déclenchés par un
// SOUS-TEXTE — « jeu à deux » dans le nom d'un atelier — qui écrasaient la rotation) cède
// à une disposition IA plus riche.
const SPECIALIZED_GEOMETRY_TEMPLATES = new Set([
  "cible", "lancer_cible",
  "athle_field_event", "sprint_balise", "course_vitesse", "relais_vitesse",
  "gym_parcours", "deplacement_drill", "progression_passes", "defi_tir_dribble",
  "portes_passes", "conduite_reactive", "rugby_touche", "volley_tournante", "tirs_multi_postes",
  "tir_penalty", "football_touche", "basket_remise", "acrosport_figure", "hand_kungfu",
  "zones_cibles", "tir_depuis_zones", "panier_de_balles",
  "tir_rebond_colonnes", "passe_et_suit_colonnes",
  "passe_tir_colonnes", "volley_passe_colonnes", "lancer_profondeur_colonnes", "gardien_tournant",
  "trio_colonnes_roles", "duel_plaquage_colonnes", "defenseurs_entrants_portes",
  "defense_differee", "vagues_attaquants", "reaction_dos_a_dos", "beret_ballon",
  "ca_relance_gardien", "duel_poursuite", "zones_1c1_successifs", "repli_apres_tir",
  "filet_service_dominant",
]);
// NB : volley_tournante figure déjà dans le set — l'exemption des gardes filet
// (rally) vaut pour tous les gabarits filet spécialisés.
export function routeDecision(result, { text, sport, parseOpts = {} } = {}) {
  const aiOpts = { ...parseOpts, sport: sport || parseOpts.sport, famille: result?.analyse?.famille,
    attaquants: result?.analyse?.effectif?.attaquants, defenseurs: result?.analyse?.effectif?.defenseurs };
  const decide = () => {
    // RELAIS D'ATHLÉTISME — gabarit DÉDIÉ, prioritaire sur tout le reste : ni
    // l'analyse « dispositif », ni les gabarits d'atelier ne savaient dessiner
    // un relais (un seul segment de course, une seule zone, coureurs hors
    // couloir). Réservé à l'athlétisme, aux VRAIS relais (le « relais en N
    // ateliers » reste un atelier — 👎 PROD40/PROD111) et aux textes qui
    // parlent de témoin ou de transmission : un « relais à deux » de jeu
    // collectif n'y tombe pas (le sport filtre).
    if (sport === "athletisme" && !/atelier/i.test(text || "")
      && /\brelais\b/i.test(text || "") && /t[ée]moin|transmission/i.test(text || "")) {
      const m = String(text || "").match(/(\d)\s*[x×]\s*\d+|relais\s+[àa]\s+(deux|trois|quatre|\d)/i);
      const mots = { deux: 2, trois: 3, quatre: 4 };
      const n = m ? (parseInt(m[1], 10) || mots[String(m[2] || "").toLowerCase()] || parseInt(m[2], 10) || 4) : 4;
      const relais = buildRelaisAthle(text, n);
      if (relais) return { reason: "template", scene: relais };
    }
    // On construit la scène d'analyse une fois (sert au routage préféré ET à la
    // comparaison de richesse ci-dessous). null si pas d'analyse exploitable.
    const analysisScene = sceneFromAnalysis(result?.analyse, sport, text);
    // CONTRE-ATTAQUE SQUELETTIQUE — l'analyse IA se réduit à des passes + tir
    // (AUCUNE course, AUCUN acteur situé) alors que le texte décrit un
    // placement de départ (« partent de leur zone de 9 m », « défenseurs 2 m
    // derrière eux ») et/ou une poursuite (« éviter le retour des défenseurs »).
    // Le gabarit local (buildTeamAttack) capte tout cela — départ groupé,
    // défenseurs derrière, courses de poursuite — là où l'analyse étale les
    // attaquants sur tout le terrain et pose un défenseur près du but. On lui
    // laisse la main (et on supprime le « flash » aperçu -> rendu final pauvre).
    const thinCounter = (() => {
      const a = result?.analyse;
      if (!a || a.famille !== "invasion" || !["contre_attaque", "transition"].includes(a.type)) return false;
      const phase = Array.isArray(a.phase) ? a.phase : [];
      if (!phase.length || phase.some((s) => s && (s.action === "course" || s.action === "ecran"))) return false;
      if (Array.isArray(a.acteurs) && a.acteurs.length) return false;
      const t2 = String(text || "").toLowerCase();
      return /zone (?:de |des? )?\d+\s*m|partent? de|derri[èe]re (?:eux|le|leur)|retour des? d[ée]fenseurs?|reviennent|poursuiv|se replient/.test(t2);
    })();
    if (thinCounter) {
      const local = localParse(text, parseOpts);
      if (local && (local.players || []).some((p) => p.team === "B")) return { reason: "template", scene: local };
    }
    if (preferAiAnalysis(text, aiOpts) && analysisScene) return { reason: "analysis", scene: analysisScene };
    const family = recognizedTemplate(text, parseOpts);
    if (family || isWeakScene(result) || isIncoherentScene(result, text)) {
      const local = localParse(text, parseOpts);
      // RICHESSE : si l'analyse IA (invasion) décrit une séquence à NETTEMENT plus de
      // joueurs DE CHAMP que le template local (≥ 2 de plus, gardien exclu), on la
      // préfère — sinon un mot-clé (« une-deux », « pivot », « fixe ») réduit un jeu à
      // N contre N à un petit 2c1 figé. On compte A+B : un 3v3 (6) bat un 2c1 (3).
      // Les templates à GÉOMÉTRIE SPÉCIALISÉE (set-plays de remise en jeu, touche,
      // penalty…) gardent la main même face à une analyse invasion « plus riche » :
      // leur placement réglementaire (lanceur SUR la ligne, tireur AU point) est
      // la valeur — l'IA replace ces acteurs en pleine phase de jeu générique.
      if (analysisScene && result?.analyse?.famille === "invasion" && !SPECIALIZED_GEOMETRY_TEMPLATES.has(family)) {
        const outfield = (sc) => (sc.players || []).filter((p) => p.team === "A" || p.team === "B").length;
        if (outfield(analysisScene) >= outfield(local) + 2) return { reason: "analysis", scene: analysisScene };
        // CONCLUSION : si l'analyse IA conclut par un TIR (phase offensive finalisée
        // — contre-attaque, surnombre…) alors que le template local n'en a aucun
        // (ex. « sortie de balle » = construction sans finition), la leçon est la
        // phase offensive : on préfère l'analyse plutôt que de la réduire au build-up.
        const hasShot = (sc) => (sc.actions || []).some((a) => a.type === "tir");
        if (hasShot(analysisScene) && !hasShot(local)) return { reason: "analysis", scene: analysisScene };
      }
      // DISPOSITIF : le pendant, pour les AMÉNAGEMENTS (athlé, gym, cible…).
      // Un gabarit d'atelier dessine son geste-type avec DEUX acteurs (donneur/
      // receveur, lanceur/cible) ; quand l'analyse décrit un EFFECTIF plus
      // large (« 4 coureurs », « groupes de quatre », « un observateur qui
      // relève »), le gabarit les PERD — et c'est mot pour mot la plainte des
      // 👎 : « on ne voit pas l'organisation avec les 4 élèves » (PROD113),
      // « où est le juge qui mesure ? » (PROD114), « il n'y a pas
      // l'observateur » (PROD3). On bascule sur l'analyse UNIQUEMENT si elle
      // dessine réellement plus de monde que le gabarit.
      // Mesuré sur les 31 dispositifs du corpus qui reconnaissent aussi un
      // template : 3 bascules, TOUTES des 👎 réels ; 28 inchangés — dont la
      // montante-descendante (12 coureurs au gabarit, 4 à l'analyse : le
      // gabarit garde la main) et tous les ateliers de cible/pétanque.
      if (analysisScene && result?.analyse?.famille === "dispositif" && local) {
        const decrits = (Array.isArray(result.analyse.roles) ? result.analyse.roles : [])
          .reduce((n, r) => n + (parseInt(r?.n, 10) || 1), 0);
        const nb = (sc) => (sc.players || []).length;
        if (decrits > nb(local) && nb(analysisScene) > nb(local))
          return { reason: "analysis", scene: analysisScene };
      }
      // FILET : sur un texte VERBEUX (situation d'apprentissage), l'extraction des
      // frappes du LOCAL dégénère en rally générique (« Frappe côté gauche/droit »).
      // Si l'analyse IA séquence ≥ 2 frappes NOMMÉES, on la préfère au rally.
      // EXCLU : les gabarits filet à géométrie SPÉCIALISÉE (tournante, panier de
      // balles) — leurs « frappes » ne sont pas un rally à corriger, et les
      // coups IA comptent la distribution de l'enseignant (fausse « couverture »).
      if (analysisScene && result?.analyse?.famille === "filet" && !SPECIALIZED_GEOMETRY_TEMPLATES.has(family)) {
        const lStrokes = (local.actions || []).filter((a) => a.type !== "course");
        const localGeneric = lStrokes.length > 0 && lStrokes.every((a) => /frappe c[ôo]t[ée]|contre.?pied/i.test(a.label || ""));
        const aStrokes = (analysisScene.actions || []).filter((a) => a.type !== "course").length;
        // SPORT DE FILET MAL DÉTECTÉ LOCALEMENT (abréviation ou faute : « TT »,
        // « voley »…) : le repli local n'a produit AUCUNE frappe de filet (pas
        // un rally — ex. couloirs de passe-et-suit). L'analyse filet, qui a le
        // BON sport, prime dès qu'elle séquence un échange.
        if (aStrokes >= 2 && !(local.actions || []).some((a) => a.type === "renvoi")) {
          return { reason: "analysis", scene: analysisScene };
        }
        // VOLLEY à plusieurs : l'analyse décrit un ATTAQUANT DÉDIÉ (acteur distinct
        // du receveur) que le moteur local réduit au receveur qui monte attaquer.
        // L'analyse est alors plus fidèle (3 acteurs : réception / passe / attaque)
        // -> on la préfère dès que l'effectif laisse la place (≥3 d'un côté).
        const cps0 = Array.isArray(result?.analyse?.coups) ? result.analyse.coups : [];
        const recvA = cps0.find((c) => /reception/i.test(c?.coup || ""))?.acteur;
        const atkA = cps0.find((c) => /attaque|smash|kill|vol[ée]e|topspin/i.test(c?.coup || ""))?.acteur;
        const eff0 = result?.analyse?.effectif || {};
        const dedicatedAtk = recvA && atkA && atkA !== recvA && Math.max(+eff0.gauche || 0, +eff0.droite || 0) >= 3;
        if (dedicatedAtk && aStrokes >= 2) return { reason: "analysis", scene: analysisScene };
        // ... ou quand l'analyse décrit un SYSTÈME à bien plus de joueurs que le
        // local (ex. volley à 6 : le local ne montre qu'un échange à 2).
        const richer = (analysisScene.players || []).length >= (local.players || []).length + 2;
        // L'IA a-t-elle COMPRESSÉ l'échange (« 10 échanges » -> un coup, repetitions:10) ?
        // Si oui, le local développe LÉGITIMEMENT plus de frappes : on ne le corrige pas.
        const coups = Array.isArray(result?.analyse?.coups) ? result.analyse.coups : [];
        const compressed = coups.some((c) => Number(c?.repetitions) > 1);
        // COLLAPSE GÉNÉRIQUE — le rally local a réduit des coups VARIÉS décrits en une
        // même frappe générique RÉPÉTÉE (« Échange → fond » ×3+, « Frappe côté » ×3+),
        // alors que l'IA séquence des coups distincts. Quand l'IA n'a pas compressé et
        // décrit plus de coups que le local n'a de libellés DISTINCTS, elle est plus fidèle.
        const counts = {};
        for (const a of lStrokes) { const L = a.label || ""; if (/^(?:[ée]change|frappe c[ôo]t[ée])/i.test(L)) counts[L] = (counts[L] || 0) + 1; }
        const maxRepeat = Math.max(0, ...Object.values(counts));
        const distinctLocal = new Set(lStrokes.map((a) => a.label || a.type)).size;
        const localCollapsed = maxRepeat >= 3 && !compressed && coups.length >= 3 && coups.length > distinctLocal;
        if ((localGeneric || richer || localCollapsed) && aStrokes >= 2) return { reason: "analysis", scene: analysisScene };
        // COUVERTURE — l'IA séquence PLUS de coups que le local n'en a extrait :
        // le texte sous-entend des retours adverses (« dégagement croisé, puis
        // amorti côté opposé » = deux coups du MÊME joueur, avec un renvoi
        // adverse implicite entre les deux). L'alternance aveugle du local
        // attribue alors les coups au MAUVAIS joueur — l'analyse, qui a
        // séquencé le renvoi manquant, est plus fidèle.
        if (!compressed && coups.length >= 3 && coups.length > lStrokes.length && aStrokes >= 2)
          return { reason: "analysis", scene: analysisScene };
        // SUR-DÉCOMPOSITION INVERSE — le moteur de rally local INVENTE des frappes
        // pour « remplir » l'échange (alternances remise/dégagement fantômes) là où
        // l'IA a séquencé proprement les coups DÉCRITS. Si l'IA n'a PAS compressé et que
        // le local produit nettement PLUS de frappes que de coups décrits (≥ 2 de trop),
        // l'analyse est plus FIDÈLE au texte : on la préfère. Même verdict à ÉCART
        // FRANC (≥ 4 frappes inventées) dès 2 coups décrits — un drill de défense à
        // 2 coups partait en rally de 11 frappes fantômes.
        if (!compressed && (coups.length >= 3 && lStrokes.length >= coups.length + 2
          || coups.length >= 2 && aStrokes >= 2 && lStrokes.length >= coups.length + 4)) {
          return { reason: "analysis", scene: analysisScene };
        }
      }
      // DISPOSITION IA À PLUSIEURS CELLULES vs template local — l'IA a structuré une
      // famille atelier/dispositif/parcours avec ≥ 2 cellules (ateliers/zones/postes).
      // Si un template local (squelette de rotation générique, OU set-play invasion
      // faussement déclenché par un SOUS-TEXTE — « jeu à deux » dans un nom d'atelier)
      // l'intercepte, on préfère l'analyse dès qu'elle est PLUS RICHE : soit
      // STRICTEMENT plus de cellules, soit — à nombre égal — des cellules NOMMÉES
      // (« Atelier 1 — Tir ») que le local laisse génériques (« Atelier 1 »).
      // Exclu : les templates à géométrie spécialisée (cf. SPECIALIZED_GEOMETRY_TEMPLATES)
      // dont le placement local EST la valeur (archerie, agrès athlé, vagues…). Une
      // VRAIE phase de jeu, elle, revient en famille « invasion » (gardée plus haut),
      // jamais en « atelier » : ce garde ne mord donc que sur les faux déclenchements.
      if (family && !SPECIALIZED_GEOMETRY_TEMPLATES.has(family)
        && analysisScene && ["atelier", "dispositif", "parcours"].includes(result?.analyse?.famille)) {
        const cellRe = /^(?:atelier|poste|station|couloir|zone)\b/i;
        const cells = (sc) => (Array.isArray(sc?.zones) ? sc.zones : []).filter((z) => cellRe.test(z.label || ""));
        const named = (arr) => arr.filter((z) => /[—–]\s*\S/.test(z.label || "")).length;   // libellé « … — Nom »
        const a = cells(analysisScene), l = cells(local);
        if (a.length >= 2 && (a.length > l.length || named(a) > named(l))) return { reason: "analysis", scene: analysisScene };
      }
      return { reason: family ? "template" : "hybrid", scene: local };
    }
    return { reason: "edge", scene: result };
  };
  // MULTI-COURTS (filet) + OVERLAYS GÉNÉRIQUES — appliqués à la scène choisie
  // QUELLE QUE SOIT la route (et à la scène alternative du garde-fou, pour que
  // la comparaison de fidélité se fasse à armes égales).
  const finish = (dd) => {
    // « 3 terrains dans la largeur », « 4 tables », « montante-descendante » :
    // duplication de l'exercice dans N bandes (la route analyse filet l'a déjà
    // fait -> no-op). Si la scène routée était inadaptée (atelier générique),
    // la cellule est reconstruite : la route devient « hybrid ».
    if (dd.scene && SPORTS_FILET.has(sport)) {
      const mc = multiCourtsFilet(dd.scene, text, sport, parseOpts.kind);
      if (mc) dd = { reason: mc === dd.scene ? dd.reason : "hybrid", scene: mc };
    }
    // « 2 groupes en parallèle » en INVASION (terrain coupé dans la largeur) :
    // la petite scène (2c1, duel…) est dupliquée dans N bandes, comme au filet.
    // Réservé aux scènes SANS gardien (un gardien par bande n'a pas de sens).
    if (dd.scene && SPORTS_INVASION.has(sport)) {
      const npar = parallelGroupsCount(text);
      const ps0 = dd.scene.players || [];
      const field0 = ps0.filter((p) => p.team === "A" || p.team === "B").length;
      // MINI-TERRAINS annoncés avec leur effectif (« 3 bandes, 4 contre 4 sur
      // chacun ») : la cellule est un match complet, le plafond historique de 5
      // joueurs ne s'applique pas — sinon rien n'était dupliqué.
      const miniCourts = parallelCourtsInvasion(text) >= 2;
      const cap = miniCourts ? 10 : 5;
      if (npar >= 2 && field0 >= 2 && field0 <= cap && !ps0.some((p) => p.team === "k") && !ps0.some((p) => /_g\d/.test(p.id))) {
        applyParallelGroups(dd.scene, npar);
        // Bandes ANNONCÉES comme des terrains : elles sont nommées, pour qu'on
        // lise « 3 mini-terrains » et non « un terrain en couloirs ».
        if (miniCourts) labelParallelCourts(dd.scene, npar);
      }
    }
    if (dd.scene) addObserverConsigne(dd.scene, text);    // observateur/arbitre qui annonce un signal
    // « Chacun rejoint l'autre colonne » (filet) : AVANT addWaitingColumns — il pose
    // files ET arcs de rotation ; sinon les files seules le court-circuitent.
    if (dd.scene && SPORTS_FILET.has(sport)) addNetColumnSwap(dd.scene, text);
    if (dd.scene && SPORTS_FILET.has(sport)) addPostServiceRun(dd.scene, text, sport);   // tâche APRÈS le service (3 m, fond…)
    if (dd.scene && SPORTS_FILET.has(sport)) addPostReceptionRun(dd.scene, text, sport); // …et APRÈS la réception
    if (dd.scene && SPORTS_FILET.has(sport)) addGroundTarget(dd.scene, text);            // « cible au sol » visée par le renvoi
    if (dd.scene) addWaitingColumns(dd.scene, text);      // colonnes/files d'attente derrière les acteurs
    if (dd.scene) addLateDefender(dd.scene, text, sport); // « +1 » : défenseur retardataire décrit
    if (dd.scene) addZoneGapHoops(dd.scene, text, sport); // cerceaux dans les intervalles de la zone
    if (dd.scene) addJokers(dd.scene, text, sport);       // jokers offensifs nommés
    if (dd.scene) addPassGates(dd.scene, text);           // portes matérialisées SUR les passes décrites
    if (dd.scene) addCourtDelimitation(dd.scene, text);   // terrain DÉLIMITÉ par des plots + dimensions cotées
    if (dd.scene) addMovementPlots(dd.scene, text, sport); // plots-repères de déplacement (sans dimensions cotées)
    if (dd.scene) addStartPlot(dd.scene, text);           // départ matérialisé par un plot/cône
    // EN DERNIER : mise en conformité RÉGLEMENTAIRE des placements (surface de
    // but interdite, service engagé d'où le règlement l'impose…) — sauf quand
    // l'enseignant a explicitement demandé une autre organisation.
    // TENNIS DE TABLE : personne SUR la table (👎 prod « les déplacements sont
    // sur la table !! ») — le moteur historique visait la RETOMBÉE de balle
    // avec ses courses « sur la balle », dessinant des déplacements qui
    // finissent au milieu de la table. Correction DÉTERMINISTE avant le
    // contrôle réglementaire.
    if (dd.scene && sport === "tennis_de_table") enforceTableExterior(dd.scene);
    // REPLACEMENT ENTRE LES FRAPPES — posé ICI et pas dans `filetScene` : les
    // routes template (gabarits qui bâtissent leur propre scène) et analysis
    // passent toutes deux par `finish()`, alors qu'un seul des deux chemins
    // traverse `filetScene`. Avant `dropStillMoves`, qui balaiera un
    // replacement dégénéré, et avant `enforceRegulation`, qui a le dernier mot
    // sur les placements.
    if (dd.scene) applyStrikeApproach(dd.scene, sport, text);
    if (dd.scene) applyReplacement(dd.scene, sport, text);
    // COURSES CROISÉES : permute deux arrivées pour que les trajectoires se
    // coupent vraiment. AVANT `dropStillMoves` (il balaierait une course
    // devenue nulle — le correcteur s'en garde lui-même) et AVANT les
    // correcteurs de TIR, dont l'origine dépend de la position ATTEINTE.
    if (dd.scene) enforceCrossedRuns(dd.scene, sport, text);
    if (dd.scene) dropStillMoves(dd.scene);               // flèches de déplacement qui ne vont nulle part
    // APRÈS dropStillMoves, qui peut retirer une action et créer un trou dans
    // la numérotation : les replacements s'insèrent sur des ordres
    // FRACTIONNAIRES (1.5) pour se glisser entre deux frappes, et le rendu
    // AFFICHE l'ordre sur la flèche — sans cette passe l'élève lisait des
    // pastilles « 1.5 » (constaté au rendu).
    if (dd.scene) renumberOrders(dd.scene);
    if (dd.scene) enforceRegulation(dd.scene, sport, text);
    if (dd.scene) enforceShotDepth(dd.scene, sport, text);
    if (dd.scene) enforceBackwardPass(dd.scene, sport, text);
    // ALLER CHERCHER LE VOLANT — `applyStrikeApproach` ci-dessus est conditionné
    // au TEXTE ; la règle `frappe fantôme` ne l'est pas. Ce correcteur-ci porte
    // le prédicat EXACT de la règle et rattrape ce que l'autre laisse passer.
    // EN DERNIER des correcteurs de position, et c'est MESURÉ : `enforceRegulation`
    // et `enforceShotDepth` DÉPLACENT les retombées. Placé avant eux, ce
    // correcteur envoyait le frappeur vers une retombée qu'ils relogeaient
    // ensuite ailleurs — une flèche « Déplacement sur le volant » qui ne va pas
    // chercher le volant (constaté sur B11). Après eux, il lit les retombées
    // DÉFINITIVES : frappe fantôme 5 -> 1 au lieu de 5 -> 2, et +5 cas au lieu
    // de +4 sur les 214 cas capturés.
    if (dd.scene) enforceStrikeOrigin(dd.scene, sport, text);
    // CONDUIRE LE TIR JUSQU'À LA CIBLE — le TIR est la 1re cause de rejet du
    // dépôt (8, une fois regroupées les quatre classes que classOf éclate) et
    // n'avait aucun correcteur. Celui-ci ne traite que les ARRIVÉES (cadre,
    // tir interrompu, panier) : mesuré sur 183 scènes servies et 1155 scènes
    // routées, ces classes tombent à ZÉRO, la lisibilité est inchangée au code
    // près et aucune scène n'est dégradée.
    // …et l'ORIGINE avant l'arrivée : amener le tireur à portée déplace le
    // départ de la flèche, dont l'arrivée est ensuite conduite à la cible.
    if (dd.scene) enforceShotOrigin(dd.scene, sport, text);
    if (dd.scene) enforceShotTarget(dd.scene, sport, text);
    // TOUT À LA FIN : marqueur d'AFFICHAGE demi-terrain (meta seulement — les
    // coordonnées restent sur 0-100 du terrain complet, juges et garde-fou de
    // fidélité ci-dessous n'y voient aucune différence).
    if (dd.scene) fitAgresToAction(dd.scene, sport, text);
    if (dd.scene) applyStadeTerrain(dd.scene, sport, text);
    if (dd.scene) applyHalfCourt(dd.scene, text, sport);
    return dd;
  };
  let d = finish(decide());
  // GARDE-FOU DE FIDÉLITÉ (architecture v2) — la scène choisie est confrontée
  // au texte (src/fidelity.js). SANS contradiction : rien ne change (zéro
  // régression). AVEC contradiction(s) : on évalue l'AUTRE interprète (analyse
  // IA <-> moteur local) et on bascule seulement s'il fait STRICTEMENT mieux.
  // Le garde-fou ne casse jamais la génération (try/catch intégral).
  try {
    const issues = fidelityIssues(d.scene, text, sport);
    if (issues.length) {
      let alt = null;
      if (d.reason === "analysis") {
        const local = localParse(text, parseOpts);
        if (local) alt = { reason: recognizedTemplate(text, parseOpts) ? "template" : "hybrid", scene: local };
      } else if (!SPECIALIZED_GEOMETRY_TEMPLATES.has(recognizedTemplate(text, parseOpts))) {
        // Les gabarits à GÉOMÉTRIE SPÉCIALISÉE (set-plays, colonnes, relance…)
        // sont des verdicts épinglés : leur placement local EST la valeur — le
        // garde-fou ne les remplace jamais par l'analyse IA.
        const s = sceneFromAnalysis(result?.analyse, sport, text);
        if (s) alt = { reason: "analysis", scene: s };
      }
      if (alt && alt.scene) {
        alt = finish(alt);
        if (fidelityIssues(alt.scene, text, sport).length < issues.length) d = alt;
      }
    }
  } catch (_) { /* garde-fou best-effort */ }
  /* COMPLÉTER L'EFFECTIF ANNONCÉ — APRÈS l'arbitrage, et c'est délibéré.
     Placé dans `finish()`, il tournait PENDANT le garde-fou : en réparant le
     compte du gabarit, il lui faisait gagner un arbitrage que l'analyse IA
     devait remporter (corpus doré, bad_1c2_distributeurs). Un correcteur ne
     doit pas peser sur le CHOIX de l'interprète, seulement sur la scène
     retenue.
     `enforceRegulation` est rejoué derrière : il sort les renforts de la
     surface de but et les désempile — sans lui, 3 scènes se dégradaient, TOUTES
     en `zone_gardien`. Réutiliser vaut mieux qu'écrire un évitement de plus. */
  try {
    completeRoster(d.scene, sport, text);
    addPostedTeacher(d.scene, sport, text);
    addNamedGear(d.scene, sport, text);
    addDescribedKeeper(d.scene, sport, text);
    addDefenderMove(d.scene, sport, text);
    enforceRegulation(d.scene, sport, text);
  } catch (_) { /* best-effort, comme le garde-fou */ }
  return d;
}

/* TENNIS DE TABLE — PERSONNE SUR LA TABLE. La table occupe x 22-78 · y 20-80
   (miroir du tracé render.js) : un joueur debout dedans, ou une course qui s'y
   termine, est physiquement impossible. Le moteur historique visait la
   RETOMBÉE de balle avec ses courses « déplacement sur la balle » : on
   PROJETTE joueurs et fins de course au bord EXTÉRIEUR le plus proche en x
   (la hauteur est conservée — le déplacement latéral reste lisible, il longe
   la table au lieu de la traverser). Les frappes (renvoi/tir), elles, visent
   évidemment la table : intactes. Tables dupliquées (_g2…) : géométrie par
   bande différente, intactes. */
export function enforceTableExterior(scene) {
  if (!scene || (scene.players || []).some((p) => /_g\d/i.test(String(p.id)))) return scene;
  const inside = (x, y) => Number.isFinite(x) && Number.isFinite(y) && x > 23 && x < 77 && y > 21 && y < 79;
  const projX = (x) => (x <= 50 ? 20 : 80);
  for (const p of scene.players || []) {
    if (p.team === "OBS" || /observ|arbitre|enseign/i.test(p.role || "")) continue;
    if (inside(+p.x, +p.y)) p.x = projX(+p.x);
  }
  for (const a of scene.actions || []) {
    if (a.type !== "course" && a.type !== "dribble") continue;
    if (inside(+a.tox, +a.toy)) a.tox = projX(+a.tox);
    if (inside(+a.fromx, +a.fromy)) a.fromx = projX(+a.fromx);
  }
  /* … ET LA RÉCIPROQUE : la BALLE, elle, doit retomber SUR la table. Le même
     👎 PROD39 disait aussi « pas d'impacts de rebond » : le moteur y visait le
     FOND LIBÉRÉ (88,20), c'est-à-dire à côté de la table — `autoRebondPoints`
     n'avait donc aucune retombée légale à marquer. On RAMÈNE la retombée sur
     la table par le plus court chemin (le sens du coup — long/court, croisé/
     décroisé — est porté par la DIRECTION, qui est conservée) : le rebond
     apparaît, et la frappe redevient réglementaire. */
  const clampTable = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  for (const a of scene.actions || []) {
    if (a.type !== "renvoi" && a.type !== "tir") continue;
    if (!Number.isFinite(+a.tox) || !Number.isFinite(+a.toy)) continue;
    a.tox = clampTable(+a.tox, 24, 76);
    a.toy = clampTable(+a.toy, 22, 78);
  }
  return scene;
}

/* FLÈCHES DE DÉPLACEMENT QUI NE VONT NULLE PART (salve raquettes) — une course
   ou une conduite dont l'arrivée est SON PROPRE POINT DE DÉPART ne dessine
   rien… sauf son numéro d'étape : l'enseignant voit une pastille « 3 » qui
   promet un déplacement inexistant (« Retour au centre » collé au joueur).
   Le moteur en produit 12 sur le lot doré — ateliers de gymnastique et de tir
   à l'arc (élan de longueur nulle), écartements du 3x3 recalculés sur la
   position d'origine. On les SUPPRIME, ce qui ne retire aucune information :
   par construction, elles n'en portaient aucune.
   Seuil très serré (0,5 unité) : un petit ajustement d'appui reste dessiné.
   PURE au sens où elle ne touche qu'aux actions dégénérées ; mute la scène. */
export function dropStillMoves(scene) {
  const actions = Array.isArray(scene?.actions) ? scene.actions : null;
  if (!actions) return scene;
  const pos = {};
  for (const p of scene.players || []) pos[p.id] = { x: +p.x, y: +p.y };
  const keep = [];
  /* ⚠️ LES ACTIONS SANS `order` VONT EN DERNIER, ET C'EST PORTEUR. J'ai essayé
     de leur donner l'ordre de la frappe qu'elles suivent dans le tableau — la
     convention d'`applyReplacement` —, pour que deux correcteurs de la même
     chaîne rangent le temps pareil. MESURÉ : route template 1091 -> 1071
     (−20 scènes) et une classe NEUVE, `action_vide` 37. Le tri à 9999 tient
     donc quelque chose que je n'ai pas identifié, et l'uniformiser naïvement
     coûte plus qu'il ne rapporte. Révoqué. */
  const ordered = actions.slice().sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
  const drop = new Set();
  for (const a of ordered) {
    if (a.type !== "course" && a.type !== "dribble") continue;
    const A = Number.isFinite(+a.fromx) ? { x: +a.fromx, y: +a.fromy } : pos[a.from];
    const B = a.to != null ? pos[a.to] : (Number.isFinite(+a.tox) ? { x: +a.tox, y: +a.toy } : null);
    if (!A || !B || !Number.isFinite(A.x) || !Number.isFinite(B.x)) continue;
    if (Math.hypot(B.x - A.x, B.y - A.y) < 0.5) drop.add(a);
    else if (a.to == null && a.from != null) pos[a.from] = { x: B.x, y: B.y };
  }
  if (!drop.size) return scene;
  for (const a of actions) if (!drop.has(a)) keep.push(a);
  scene.actions = keep;
  return scene;
}

/* MARQUEUR D'AFFICHAGE « DEMI-TERRAIN » (👎 prod PROD1 : « le prompt demandait
   sur demi-terrain ! »). Quand le texte demande un demi-terrain ET que toute
   l'action tient dans la moitié droite (convention « but attaqué à droite »),
   on pose `meta.halfCourt = "right"` : le RENDU étire alors cette moitié sur
   toute la boîte et dessine le demi-terrain (ligne médiane au bord gauche).
   Les COORDONNÉES ne sont PAS modifiées — juges, éditeur, projection et
   schémas sauvegardés restent sur 0-100 du terrain complet ; sans le marqueur
   le rendu est strictement celui d'avant (aucune régression possible sur
   l'existant, qui n'a jamais ce meta).
   CONSERVATEUR : au moindre doute (élément dans la moitié gauche, plusieurs
   demi-terrains = ateliers parallèles, sport sans but, basket 3x3 déjà en
   demi-terrain), on ne pose rien et l'affichage reste le terrain complet. */
/* STADE (piste en anneau) — pose `terrainOpts.stade` quand le texte l'exige
   (tour de piste, virage, 200 m et plus, relais 4×100 : cf. `wantsStade`).
   Appelée sur TOUTES les routes — ici pour le repli et l'analyse, et depuis
   `applyMeta` (main.js) pour la scène directe : le tracé du fond ne dépend pas
   de l'interprète qui a construit la scène. Mesuré : 0 bascule sur les 1106
   scènes du corpus — le tracé en ligne droite reste celui de tous les sprints
   et de tous les ateliers. */
/* Le PERSONNEL (observateur, juge, chronométreur, enseignant) a sa place sur la
   pelouse — « au centre du stade », d'où il voit tous les couloirs. Il ne doit
   surtout pas être projeté sur la piste, où il gênerait la course : c'est un
   test de non-régression du gabarit relais. Même définition que `isStaff` de
   src/regulation.js, tenue ici en local (le module ne l'exporte pas). */
const isStaffPlayer = (p) => p.team === "OBS"
  || /observ|arbitre|coach|enseign|professeur|juge|chronom|starter|distributeur|ramasseur/i.test(p.role || "");

/* La piste ne va pas d'un bord à l'autre de l'image : à la hauteur d'un
   couloir, l'anneau se referme sur les virages. À y=86 elle court de x=8,8 à
   x=91,2 ; à y=94, de 15,1 à 84,9. La mise en page « ligne droite » étalait
   pourtant les acteurs de x=0 à x=100 — les extrêmes se retrouvaient HORS de
   l'anneau, dans le coin de l'image (mesuré sur la salve athlé nº2 : 6 acteurs
   sur 3 scènes). Bord extérieur = demi-couloir 4,5, soit RY=50 et RX=50/1,737. */
/* LA PELOUSE, en coordonnées de scène : l'intérieur du stade de demi-couloir
   0,5 — droites de x=28,8 à x=71,2 entre y=16 et y=84, virages en demi-ellipses
   de demi-axes 34 (en y) et 34/1,737 (en x). Miroir du tracé de render.js. */
function surPelouseAthle(x, y) {
  if (x >= 28.8 && x <= 71.2) return y > 16 && y < 84;
  const cx = x < 28.8 ? 28.8 : 71.2;
  return ((x - cx) / (34 / 1.737)) ** 2 + ((y - 50) / 34) ** 2 < 1;
}
/* L'INVERSE de laneAt : l'abscisse curviligne (en mètres) du point du couloir
   `voie` le plus proche de (x,y). Balayage grossier puis affinage — la piste
   fait 400 m et les scènes sont petites, le coût est négligeable. */
function abscisseCouloir(x, y, voie) {
  let bu = 0, bd = Infinity;
  for (let u = 0; u < LANE_LAP; u += 2) {
    const q = laneAt(u, voie), d = (q.x - x) ** 2 + (q.y - y) ** 2;
    if (d < bd) { bd = d; bu = u; }
  }
  for (let u = bu - 2; u <= bu + 2; u += 0.25) {
    const q = laneAt(u, voie), d = (q.x - x) ** 2 + (q.y - y) ** 2;
    if (d < bd) { bd = d; bu = u; }
  }
  return bu;
}
/* Marqueurs « doux » : des libellés, pas du matériel posé au sol — ils n'ont
   rien à faire sur la piste. Même liste que src/legibility.js. */
const MARQUEURS_DOUX = new Set(["consigne", "cote", "essai", "contact", "zone"]);

function clampSurPiste(x, y) {
  const k = 1 - ((y - 50) / 50) ** 2;
  if (!(k > 0)) return x;
  const dx = (50 / 1.737) * Math.sqrt(k);
  return Math.max(28.8 - dx + 3, Math.min(71.2 + dx - 3, x));
}

/* ============================================================================
   L'AGRÈS À L'ÉCHELLE DE CE QUI S'Y PASSE
   ----------------------------------------------------------------------------
   Salve gymnastique nº1 : sur 25 agrès LINÉAIRES mesurés (20 scènes IA + les
   42 scènes gym du corpus rejouées), **17 n'ont personne dessus** et **17 sont
   plus courts que la moitié de l'action qu'ils hébergent** — largeur médiane
   12 unités pour une action étalée sur 50 à 90. Vu au rendu (G14) : la poutre
   est un moignon à l'extrême gauche et les cinq temps de l'enchaînement
   défilent à côté. Une poutre fait 5 m : si le gymnaste y marche, y tourne et
   en sort, elle doit s'étendre sous toute la séquence.

   POURQUOI CETTE PASSE EXISTE, et pas un correctif de plus dans le gabarit :
   `dressGymAgres` (parser.js) sait déjà poser une poutre pleine largeur — mais
   il vit derrière `routeDecision`, donc au REPLI seulement. La scène directe,
   qui sert l'essentiel du trafic, n'en voit rien : c'est le même clivage que
   pour le relais et pour la piste, constaté trois fois. Cette fonction est
   donc un TRANSFORMATEUR, pas un constructeur : elle s'applique à n'importe
   quelle scène, d'où qu'elle vienne, et elle est branchée sur LES DEUX ROUTES
   comme `applyStadeTerrain`.

   Prudence assumée : on déplace l'AGRÈS vers l'action, jamais les acteurs vers
   l'agrès. Le placement des élèves porte l'intention pédagogique ; la longueur
   d'une poutre, non. Et on ne touche pas aux scènes à PLUSIEURS agrès (une
   rotation d'ateliers), où l'on ne sait pas quel élève appartient à quel poste.
   ========================================================================== */
const AGRES_LINEAIRES = new Set(["poutre", "barre_fixe", "barres_asym", "barres_par", "banc"]);

export function fitAgresToAction(scene, sport, text) {
  if (!scene || (sport !== "gymnastique" && sport !== "gym")) return scene;
  if (scene.terrainOpts && scene.terrainOpts.agresAjuste) return scene;   // idempotence (deux routes, deux appels)
  const markers = Array.isArray(scene.markers) ? scene.markers : [];
  const lin = markers.filter((m) => String(m.type || "").toLowerCase() === "agres"
    && AGRES_LINEAIRES.has(String(m.kind || "")) && Number.isFinite(+m.x) && Number.isFinite(+m.y));
  if (lin.length !== 1) return scene;          // rotation d'ateliers : hors périmètre
  const agres = lin[0];
  // L'EMPRISE DE L'ACTION : le gymnaste et les points où il passe. Le personnel
  // (pareur, juge, observateur) est exclu — il se tient À CÔTÉ de l'agrès, et
  // l'inclure étirerait la poutre jusqu'au juge.
  const gymnastes = (scene.players || []).filter((p) => !isStaffPlayer(p) && Number.isFinite(+p.x));
  const arrivees = (scene.actions || []).filter((a) => Number.isFinite(+a.tox));
  const xs = [...gymnastes.map((p) => +p.x), ...arrivees.map((a) => +a.tox)];
  const ys = [...gymnastes.map((p) => +p.y), ...arrivees.map((a) => +a.toy).filter(Number.isFinite)];
  if (xs.length < 2) return scene;
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = ys.length ? Math.min(...ys) : 50, y1 = ys.length ? Math.max(...ys) : 50;
  /* ⚠️ L'AGRÈS RESTE HORIZONTAL, et c'est mesuré. Le rendu sait faire du
     vertical (`m.vertical`), mais aucune scène du corpus ne le demande — voir
     `addNamedGear`. On étire donc dans l'axe où l'agrès est dessiné. */
  const vertical = agres.vertical === true;
  const etendue = vertical ? y1 - y0 : x1 - x0;
  if (etendue < 12) return scene;                      // séquence ramassée : rien à étirer
  if ((+agres.w || 12) >= etendue * 0.9 && !!agres.vertical === vertical) return scene;   // déjà à l'échelle ET dans le bon axe
  const median = (v) => { const t2 = v.slice().sort((a, b) => a - b); return t2[Math.floor(t2.length / 2)]; };
  agres.w = Math.round(Math.min(88, etendue + 8));
  if (vertical) {
    agres.vertical = true;
    agres.y = Math.round(((y0 + y1) / 2) * 10) / 10;
    agres.x = Math.round(median(xs) * 10) / 10;
  } else {
    if (agres.vertical) delete agres.vertical;
    agres.x = Math.round(((x0 + x1) / 2) * 10) / 10;
    // …et l'agrès se pose SOUS la séquence : la médiane des hauteurs, pour ne
    // pas se laisser tirer par une sortie qui retombe à l'écart.
    if (ys.length) agres.y = Math.round(median(ys) * 10) / 10;
  }
  scene.terrainOpts = { ...(scene.terrainOpts || {}), agresAjuste: true };
  return scene;
}

/* ============================================================================
   COMPLÉTER L'EFFECTIF ANNONCÉ (correcteur d'`effectif`)
   ----------------------------------------------------------------------------
   `effectif` est la PREMIÈRE cause de défaut de la route template/local du
   repli — 98 signalements sur 1155 scènes — et il ne vient pas d'un gabarit
   mais de DIX : chacun dessine un squelette figé et ignore le nombre annoncé.
   « Trois attaquants face à deux défenseurs » sortait à deux pions.

   Plutôt que dix correctifs, un seul : les juges savent déjà compter
   (`expectedRoster`, fidelity.js — leur propre lecture, exposée), il ne
   manquait personne pour APPLIQUER le compte. Même principe que les
   correcteurs de la session : la règle qui rejette écrit déjà la réponse.

   CE QU'IL NE FAIT PAS, délibérément :
   • il n'enlève JAMAIS personne — un surplus peut être un dispositif voulu
     (files d'attente, jokers, vagues) et le retirer casserait des scènes
     justes ; seul le MANQUE est réparé, comme dans les règles ;
   • il ne touche pas les ATELIERS (rotation, postes, couloirs) : chaque
     cellule y est une mini-scène, l'effectif global n'y veut rien dire ;
   • il ne déplace personne : les pions ajoutés prennent des places LIBRES
     (≥ 10 unités de tout le monde, la contrainte de `sanitizeScene`), dans le
     camp de leur équipe. */
export function completeRoster(scene, sport, text) {
  let attendu; try { attendu = expectedRoster(text, sport); } catch (_) { return scene; }
  if (!attendu || (!attendu.a && !attendu.b)) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : [];
  if (!players.length) return scene;
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const zones = Array.isArray(scene?.zones) ? scene.zones : [];
  // Même signal d'ATELIER que les règles : on ne compte pas l'effectif d'un
  // dispositif à cellules.
  if (actions.some((a) => a.type === "rotation")
    || actions.filter((a) => a.type === "tir").length >= 2
    || zones.some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""))) return scene;
  // Les terrains DUPLIQUÉS (_g2…) répètent l'exercice : le compte global n'a
  // plus de sens, exactement comme pour la règle de surplus au filet.
  if (players.some((p) => /_g\d/i.test(String(p.id)))) return scene;
  /* COMPTER COMME LES RÈGLES, et pas autrement. Les contrôles de MANQUE
     comptent `players.filter(p => p.team === "A")` — « En attente » COMPRIS :
     une colonne d'attente est bel et bien dessinée. En l'excluant, je voyais
     « 2 attaquants » là où la règle en voyait 6, et j'ajoutais un pion à une
     scène que personne ne reprochait (corpus doré,
     hand_2c1_colonnes_partir_milieu). Le prédicat du correcteur DOIT être
     celui de sa règle — la leçon de toute la session. */
  const champ = (team) => players.filter((p) => p.team === team);
  const FILET = new Set(["volleyball", "badminton", "tennis", "tennis_de_table"]);
  const filet = FILET.has(sport);

  const libre = (x, y) => players.every((p) => Math.hypot(+p.x - x, +p.y - y) >= 10)
    && x >= 4 && x <= 96 && y >= 6 && y <= 94;
  for (const team of ["A", "B"]) {
    const veut = team === "A" ? attendu.a : attendu.b;
    const ont = champ(team);
    const autre = champ(team === "A" ? "B" : "A");
    if (!veut || ont.length >= veut) continue;
    if (!ont.length && !autre.length) continue;      // scène vide : on ne devine pas
    /* UN CAMP ENTIÈREMENT VIDE — « 4 contre 1 : 0 défenseur dessiné », « 5
       contre 5 : 0 défenseur ». Première version : le correcteur passait son
       tour, faute de savoir où poser le premier pion (6 des 37 signalements
       restants, plus une part des comptes nommés). On déduit alors la bande de
       l'AUTRE camp — mais pas par simple miroir : au FILET les camps sont
       symétriques autour du filet, tandis qu'en INVASION l'équipe A attaque
       vers x=100 et les défenseurs se placent ENTRE elle et le but attaqué.
       Miroiter y aurait posé la défense dans le dos de l'attaque. */
    const moy = (ps) => ps.reduce((s, p) => s + +p.x, 0) / ps.length;
    const mx = ont.length ? moy(ont)
      : filet ? 100 - moy(autre)
        : Math.max(8, Math.min(88, moy(autre) + (team === "B" ? 15 : -15)));
    const gauche = mx < 50;
    const bande = filet ? (gauche ? [8, 44] : [56, 92]) : [Math.max(6, mx - 12), Math.min(94, mx + 12)];
    // ⚠️ `ont` PEUT ÊTRE VIDE (camp entièrement absent) : `ont[0].role` y jetait
    // une TypeError, avalée par le try/catch de l'appelant — l'équipe A était
    // complétée puis la fonction mourait avant B, en silence. C'est le bug
    // `forceSport` du polissage, à l'identique : un catch qui masque un crash.
    // Ce qui l'a trahi : la mesure montrait un effet PARTIEL (A complétée, B
    // non) là où le code prétendait traiter les deux.
    const role = /d[ée]fens/i.test(ont[0]?.role || "") || team === "B" ? "Défenseur" : "Attaquant";
    let ajoutes = 0;
    // On balaie des places candidates en s'écartant progressivement de l'axe :
    // la scène reste centrée et lisible, et la première place LIBRE gagne.
    const dys = [16, -16, 28, -28, 40, -40, 8, -8];
    for (const dy of dys) {
      if (ont.length + ajoutes >= veut) break;
      for (const px of [mx, bande[0], bande[1], (bande[0] + bande[1]) / 2]) {
        if (ont.length + ajoutes >= veut) break;
        const x = Math.round(Math.max(bande[0], Math.min(bande[1], px)));
        const y = Math.round(Math.max(6, Math.min(94, 50 + dy)));
        if (!libre(x, y)) continue;
        ajoutes++;
        players.push({ id: `${team}x${ajoutes}`, role: `${role} ${ont.length + ajoutes}`,
          x, y, team, color: team === "A" ? "#0072B2" : "#D55E00" });
      }
    }
  }
  scene.players = players;
  return scene;
}

/* ============================================================================
   L'ENSEIGNANT QUI TIENT UN POSTE (correcteur de `role_absent`)
   ----------------------------------------------------------------------------
   Deuxième cause de défaut du repli, et la seule grosse classe présente sur
   SES DEUX ROUTES (22 sur template/local, 7 sur analysis) : le texte donne un
   POSTE à l'enseignant — il distribue, il sert, il se place — et aucun pion
   « Enseignant » n'est dessiné. 22 des 24 signalements sont ce seul message.

   Prédicat recopié de la règle, dérogation comprise : un enseignant qui
   CIRCULE, questionne ou corrige est une consigne PÉDAGOGIQUE, pas un élément
   de la scène — la règle ne le réclame pas, le correcteur ne l'ajoute pas.

   Il rejoint l'équipe `OBS`, comme l'observateur : c'est du PERSONNEL, il ne
   doit pas gonfler l'effectif de jeu (`completeRoster` compte A et B). */
export function addPostedTeacher(scene, sport, text) {
  const t = String(text || "");
  const AP = "['’]";
  const TEACHER = `(?:l${AP}\\s*)?(?:enseignant|professeur|prof|[ée]ducateur|entra[iî]neur|coach)e?s?`;
  const POSTED = "lance|envoie|distribue|sert\\b|alimente|propulse|jette|met en jeu|renvoie|frappe|se tient|se place|se positionne|se situe|est plac|se met|tient|occupe|depuis";
  const posted = new RegExp(`\\b${TEACHER}\\b[^.;:]{0,60}?\\b(?:${POSTED})`, "i").test(t)
    || new RegExp(`\\b(?:${POSTED})[^.;:]{0,40}?\\bpar\\s+${TEACHER}\\b`, "i").test(t);
  if (!posted) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : (scene.players = []);
  if (players.some((p) => /enseign|professeur|^prof|[ée]ducateur|entra[iî]neur|coach|distributeur/i.test(String(p.role || ""))
    || String(p.id || "").toUpperCase() === "E")) return scene;   // déjà là
  const FILET = new Set(["volleyball", "badminton", "tennis", "tennis_de_table"]);
  const champ = (team) => players.filter((p) => p.team === team).length;
  /* OÙ ? Là où l'exercice le met vraiment. Aux sports de FILET, un enseignant
     qui alimente se tient EN FACE, dans le camp le plus vide — c'est la
     géométrie de `buildFeederBasket`, qui a déjà servi. Ailleurs, il est au
     bord, à hauteur du milieu : la place du distributeur. On prend la première
     position LIBRE (≥ 10 unités de tout le monde, la contrainte de
     `sanitizeScene`) pour ne créer aucune superposition. */
  const loin = sport === "tennis_de_table" ? 90 : 76;
  const places = FILET.has(sport)
    ? (champ("B") <= champ("A") ? [{ x: loin, y: 50 }, { x: 100 - loin, y: 50 }] : [{ x: 100 - loin, y: 50 }, { x: loin, y: 50 }])
      .concat([{ x: 50, y: 6 }, { x: 50, y: 94 }])
    : [{ x: 50, y: 6 }, { x: 50, y: 94 }, { x: 6, y: 50 }, { x: 94, y: 50 }];
  /* LIBRE veut dire libre de TOUT, pas seulement des joueurs : mesuré, un
     enseignant s'était posé sur un plot (+1 `sur_marqueur` au banc). On écarte
     donc aussi le matériel DUR — mêmes exclusions que la règle `sur_marqueur`,
     qui ignore les ballons et les éléments « doux » (consigne, cote, zone). */
  const DOUX = /^(consigne|cote|essai|contact|zone|ballon|ball|balle|disque|frisbee|volant)$/i;
  const durs = (Array.isArray(scene?.markers) ? scene.markers : [])
    .filter((m) => !DOUX.test(String(m.type || "")) && Number.isFinite(+m.x));
  const libre = (p) => players.every((q) => Math.hypot(+q.x - p.x, +q.y - p.y) >= 10)
    && durs.every((m) => Math.hypot(+m.x - p.x, +m.y - p.y) >= 5);
  const spot = places.find(libre);
  if (!spot) return scene;   // scène pleine : mieux vaut ne rien dessiner
  players.push({ id: "ENS", role: "Enseignant", team: "OBS", color: "#D55E00", x: spot.x, y: spot.y });
  return scene;
}

/* ============================================================================
   POSER LE MATÉRIEL NOMMÉ (correcteur d'`agres` et de `materiel`)
   ----------------------------------------------------------------------------
   Deux classes, une seule nature : « nommé dans le texte mais absent de la
   scène ». Le message de la règle `agres` donne jusqu'au JSON à écrire —
   personne ne l'écrivait. 27 signalements sur la route template du repli.

   ⚠️ ET UNE REMARQUE DE L'ENSEIGNANT, qui change la façon de le faire :
   « le matériel et les agrès ne sont pas forcément à l'échelle ni bien
   orientés par rapport au mouvement ». Vérifiée au rendu (M9 : un « Mur »
   réduit à un moignon, une course d'élan qui s'arrête loin du trampoline) et
   mesurée : 22 des 41 agrès linéaires du corpus sont hors échelle ou en
   travers de leur station. On ne se contente donc pas de POSER le matériel :
   il est dimensionné sur l'ÉTENDUE DE L'ACTION et centré sur elle — même
   géométrie que `fitAgresToAction`, qui ne savait le faire que pour un agrès
   déjà présent, unique et en gymnastique.

   Le personnel est exclu de l'emprise (leçon de la salve gym : compter le juge
   posté à l'écart étire l'agrès jusqu'à lui). Rien n'est posé sur un joueur. */
export function addNamedGear(scene, sport, text) {
  let manque; try { manque = missingGear(scene, sport, text); } catch (_) { return scene; }
  if (!manque || (!manque.agres.length && !manque.plots && !manque.sol)) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const markers = Array.isArray(scene?.markers) ? scene.markers : (scene.markers = []);
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const acteurs = players.filter((p) => !isStaffPlayer(p) && Number.isFinite(+p.x));
  const arrivees = actions.filter((a) => Number.isFinite(+a.tox));
  const xs = [...acteurs.map((p) => +p.x), ...arrivees.map((a) => +a.tox)];
  const ys = [...acteurs.map((p) => +p.y), ...arrivees.map((a) => +a.toy).filter(Number.isFinite)];
  if (!xs.length) return scene;
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const etendue = Math.max(12, x1 - x0);
  const cx = (x0 + x1) / 2;
  const median = (v) => { const s = v.slice().sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; };
  const cy = ys.length ? median(ys) : 50;
  // Rien ne se pose à moins de 5 unités d'un joueur (seuil de `sur_marqueur`)
  // ni de 6 d'un repère déjà là.
  const libre = (x, y) => x >= 4 && x <= 96 && y >= 6 && y <= 94
    && players.every((p) => Math.hypot(+p.x - x, +p.y - y) >= 5)
    && markers.every((m) => !Number.isFinite(+m.x) || Math.hypot(+m.x - x, +m.y - y) >= 6);
  const poser = (obj, cands) => {
    const spot = cands.find(([x, y]) => libre(x, y));
    if (!spot) return false;
    markers.push({ ...obj, x: Math.round(spot[0]), y: Math.round(spot[1]) });
    return true;
  };

  /* LES AGRÈS — et deux distinctions que le RENDU a imposées, pas le banc.
     1) TOUS LES AGRÈS NE S'ÉTIRENT PAS. Une poutre, une barre, un banc
        couvrent la séquence : ils sont LINÉAIRES, et leur longueur suit
        l'action. Un plan incliné, un tremplin, un cheval, un trampoline ont
        une taille PHYSIQUE fixe — les étirer sur 85 unités donne une rampe
        absurde qui recouvre la scène (vu au rendu sur PROD13).
     2) PLUSIEURS AGRÈS NOMMÉS = PLUSIEURS ATELIERS, donc CÔTE À CÔTE. Les
        empiler au même centre les superposait. On partage l'étendue en autant
        de tranches, chacun dans la sienne. */
  const ETIRABLES = new Set(["poutre", "barre_fixe", "barres_asym", "barres_par", "banc", "echelle"]);
  /* LES STATIONS, QUAND LA SCÈNE EN A. Un parcours gym porte des marqueurs
     `station` : ils DISENT où sont les postes. Les ignorer, c'était centrer
     chaque agrès sur toute la scène et les superposer (vu au rendu sur PROD13,
     où trois stations existaient à x=14, 50 et 86). On préfère les stations
     LIBRES — celles qui n'ont pas déjà un agrès sur elles. */
  const stations = markers.filter((m) => String(m.type || "") === "station" && Number.isFinite(+m.x));
  const dejaEquipee = (st) => markers.some((m) => String(m.type || "") === "agres"
    && Number.isFinite(+m.x) && Math.abs(+m.x - +st.x) < 12);
  const libres = stations.filter((st) => !dejaEquipee(st));
  const n = manque.agres.length;
  manque.agres.forEach((kind, i) => {
    /* Une station LIBRE par agrès — et pas deux fois la même : à court de
       stations, on retombe sur le découpage en tranches, qui les sépare au
       moins. Empiler deux ateliers au même point est précisément ce qu'on
       cherche à éviter. */
    const st = libres[i] || null;
    const part = (x1 - x0) / n;
    const centre = st ? +st.x : (n === 1 ? cx : x0 + part * (i + 0.5));
    const cyi = st ? +st.y : cy;
    // Largeur : l'étendue de l'ACTION pour un agrès linéaire seul, la part de
    // sa station quand ils sont plusieurs, une taille PHYSIQUE fixe sinon.
    const ecart = stations.length >= 2
      ? Math.abs(+stations[1].x - +stations[0].x) : (n === 1 ? etendue + 8 : part);
    const large = ETIRABLES.has(kind)
      ? Math.round(Math.min(88, st ? ecart - 4 : (n === 1 ? etendue + 8 : part - 2)))
      : 18;
    /* DEUX AGRÈS NE SE FRÔLENT PAS. Le garde de place générique suffit pour un
       plot (6 unités) ; un agrès est LARGE de 16 à 88, et deux posés à 8
       l'un de l'autre se recouvrent au rendu. On exige donc un écart digne de
       leur taille, et on cherche plus loin s'il le faut. */
    const loinDesAgres = (x, y) => markers.every((m) => String(m.type || "") !== "agres"
      || !Number.isFinite(+m.x) || Math.hypot(+m.x - x, +m.y - y) >= 16);
    const cands = [[centre, cyi], [centre, cyi + 14], [centre, cyi - 14],
      [Math.max(10, centre - 22), cyi], [Math.min(90, centre + 22), cyi],
      [Math.max(10, centre - 22), cyi + 14], [Math.min(90, centre + 22), cyi - 14]]
      .filter(([x, y]) => loinDesAgres(x, y));
    /* ⚠️ PAS DE DÉCISION AUTOMATIQUE D'ORIENTATION — et c'est une mesure qui
       l'a tranché, pas une intuition. Le rendu SAIT désormais dessiner un
       agrès vertical, mais aucune scène du corpus n'en réclame : les 9 cas que
       j'avais comptés « en travers » venaient tous d'une métrique fautive, qui
       attribuait à un seul agrès la géométrie de PLUSIEURS ateliers empilés
       ou des flèches de ROTATION entre eux. Orienter là-dessus ferait suivre à
       la poutre les flèches de rotation : une régression, pas un correctif. */
    poser({ type: "agres", kind, w: Math.max(14, large) }, cands.length ? cands : [[centre, cyi]]);
  });

  /* LES PLOTS CHIFFRÉS — étagés LE LONG de l'action, pas alignés au hasard :
     c'est ce que « quatre plots délimitent le couloir » veut dire. */
  if (manque.plots) {
    const n = manque.plots;
    for (let i = 0; i < n; i++) {
      const x = x0 + ((i + 0.5) / n) * (x1 - x0);
      poser({ type: "plot" }, [[x, cy - 12], [x, cy + 12], [x, cy - 20], [x, cy + 20]]);
    }
  }
  /* LE MATÉRIEL À POSER non chiffré : trois repères suffisent à montrer le
     dispositif — le contrôle juge la PRÉSENCE, pas le placement. */
  if (manque.sol) {
    for (let i = 0; i < 3; i++) {
      const x = x0 + ((i + 0.5) / 3) * (x1 - x0);
      poser({ type: manque.sol }, [[x, cy - 12], [x, cy + 12], [x, cy - 20], [x, cy + 20]]);
    }
  }
  return scene;
}

/* ============================================================================
   LE GARDIEN DÉCRIT, ET LA DÉFENSE QUI DOIT BOUGER
   ----------------------------------------------------------------------------
   Deux classes de plus du même genre que les précédentes : le texte le DIT, la
   scène ne le montre pas. `gardien` 8 et `defense_immobile` 9 sur la route
   template du repli.

   Le dépôt possédait déjà un `ensureGoalkeeper` (parser.js) — mais conditionné
   à un TIR vers le but, alors que la règle se déclenche dès que le TEXTE parle
   d'un gardien. Encore le même écart de prédicat, et la même réponse : le
   correcteur porte celui de sa règle. */
export function addDescribedKeeper(scene, sport, text) {
  const GOAL = new Set(["football", "futsal", "handball", "basketball", "basket3x3"]);
  if (!GOAL.has(sport)) return scene;
  let t; try { t = coreSituationText(text); } catch (_) { t = String(text || ""); }
  // Les dérogations de la règle, recopiées : « sans gardien » l'INTERDIT, et un
  // atelier ne se juge pas sur son effectif de jeu.
  if (/sans gardien|pas de gardien|but vide|petits? buts?|mini.?buts?/i.test(t)) return scene;
  if (!/\bgardien(?:ne)?s?\b|portier/i.test(t)) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : (scene.players = []);
  const actions = Array.isArray(scene?.actions) ? scene.actions : [];
  const zones = Array.isArray(scene?.zones) ? scene.zones : [];
  if (actions.some((a) => a.type === "rotation")
    || actions.filter((a) => a.type === "tir").length >= 2
    || zones.some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""))) return scene;
  if (players.some((p) => p.team === "k" || /gardien/i.test(p.role || ""))) return scene;
  /* DEVANT LE BUT ATTAQUÉ — celui vers lequel le jeu va. On le lit sur la
     conclusion quand il y en a une, sinon sur le camp de l'attaque : l'équipe A
     attaque vers x=100 par convention du dépôt. Même poste que
     `ensureGoalkeeper` (x=94), pour que les deux chemins posent le gardien au
     même endroit. */
  const tir = actions.find((a) => a.type === "tir" && Number.isFinite(+a.tox));
  const droite = tir ? +tir.tox >= 50
    : (players.filter((p) => p.team === "A").reduce((s, p) => s + +p.x, 0) / Math.max(1, players.filter((p) => p.team === "A").length)) < 50;
  players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: droite ? 94 : 6, y: 50 });
  return scene;
}

/* LA DÉFENSE QUI DOIT BOUGER — le texte lui donne une action (elle cherche à
   intercepter, coulisse, sort sur le porteur) et aucun défenseur n'a la moindre
   flèche. Le message prescrit le geste : « dessiner au moins leur déplacement
   principal (coulissage, repli, sortie sur le porteur…) ». On dessine le plus
   lisible des trois — la SORTIE SUR LE PORTEUR, par le défenseur le plus proche
   de lui : c'est le geste que le texte décrit le plus souvent, et le seul qui
   se déduise de la scène sans rien inventer. */
export function addDefenderMove(scene, sport, text) {
  const INVASION = new Set(["football", "futsal", "handball", "basketball", "basket3x3", "rugby", "ultimate"]);
  if (!INVASION.has(sport)) return scene;
  const t = String(text || "");
  const actif = /d[ée]fenseurs?\b[^.;]{0,90}?(?:cherch|intercept|coulis|press|harc[èe]l|suiv|d[ée]marr|recul|mont|se replac|d[ée]fend|quitten?t?|couper (?:une |la )?ligne|sorten?t? sur|flotte)/i.test(t)
    || /d[ée]fense (?:active|individuelle|homme [àa] homme|tout terrain)/i.test(t);
  if (!actif) return scene;
  if (/passi[fv]|statique|immobile|sans (?:d[ée]fense|opposition|d[ée]fenseur)|ne bougent? pas|figurant/i.test(t)) return scene;
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const actions = Array.isArray(scene?.actions) ? scene.actions : (scene.actions = []);
  const defs = players.filter((p) => p.team === "B" && !/gardien|observ|arbitre|enseign/i.test(p.role || ""));
  if (!defs.length) return scene;
  const defIds = new Set(defs.map((p) => p.id));
  if (actions.some((a) => defIds.has(a.from))) return scene;          // la défense bouge déjà
  const atkMoves = actions.filter((a) => a.order != null && !defIds.has(a.from)).length;
  if (atkMoves < 2) return scene;                                     // même seuil que la règle
  /* LE PORTEUR : celui d'où part la première action de balle. Sa position
     ÉVOLUE — une conduite le déplace —, on suit donc la chronologie comme les
     juges le font. */
  const pos = {}; players.forEach((p) => { pos[p.id] = { x: +p.x, y: +p.y }; });
  const chrono = actions.filter((a) => a.order != null).slice().sort((a, b) => a.order - b.order);
  let porteur = null;
  for (const a of chrono) {
    if (!porteur && ["passe", "centre", "dribble", "tir"].includes(a.type)) porteur = a.from;
    if ((a.type === "course" || a.type === "dribble") && a.from && Number.isFinite(+a.tox)) pos[a.from] = { x: +a.tox, y: +a.toy };
  }
  const cible = porteur && pos[porteur] ? pos[porteur] : null;
  if (!cible) return scene;
  const d = defs.reduce((best, p) => (Math.hypot(pos[p.id].x - cible.x, pos[p.id].y - cible.y)
    < Math.hypot(pos[best.id].x - cible.x, pos[best.id].y - cible.y) ? p : best), defs[0]);
  const ici = pos[d.id];
  const dist = Math.hypot(cible.x - ici.x, cible.y - ici.y);
  // Il SORT sur le porteur, il ne lui rentre pas dedans : on s'arrête à 6
  // unités — au-delà du seuil de superposition, en deçà d'une flèche nulle.
  if (dist < 10) return scene;
  const k = (dist - 6) / dist;
  const ordres = chrono.map((a) => a.order).filter((o) => o != null);
  actions.push({ from: d.id, tox: Math.round(ici.x + (cible.x - ici.x) * k), toy: Math.round(ici.y + (cible.y - ici.y) * k),
    type: "course", style: "dashed", order: (ordres.length ? Math.max(...ordres) : 0) + 1, label: "Sortie sur le porteur" });
  return scene;
}

export function applyStadeTerrain(scene, sport, text) {
  if (!scene || sport !== "athletisme" || !wantsStade(text)) return scene;
  // Scène déjà POSÉE sur la piste par un gabarit dédié (relais) : on ne
  // retouche rien, sinon on ré-aplatirait le tour sur la ligne droite.
  if (scene.terrainOpts && scene.terrainOpts.stadePlace) return scene;
  // IDEMPOTENCE. Sur les routes classiques cette fonction est appelée DEUX fois
  // (finish() ici, puis applyMeta côté client) — et elle ne l'était pas : la
  // règle de marqueur `|y-50| < 25 ? 86 : 74` renvoie à 74 ce qu'elle vient de
  // poser à 86, et inversement au passage suivant. Mesuré : 6 des 13 scènes de
  // la salve athlé nº2 changeaient au second appel (56 entités déplacées), et
  // 4 scènes routées du corpus. Le drapeau ferme la porte une fois pour toutes.
  if (scene.terrainOpts && scene.terrainOpts.stadePose) return scene;
  scene.terrainOpts = { ...(scene.terrainOpts || {}), stade: true, stadePose: true };
  // ON NE COURT PAS SUR LA PELOUSE. Le tracé stade met la piste en ANNEAU et
  // la pelouse au centre : une scène construite sur l'axe (y≈50) poserait ses
  // coureurs au milieu du terrain. On les ramène SUR les couloirs.
  //   · relais court et sprints (4×40, 4×60 scolaires — « tracés en ligne
  //     droite ou sur une portion réduite de la piste », référentiel EPS) :
  //     tout sur la LIGNE DROITE avant ;
  //   · tour de piste, 400 m et plus, 4×100 (« les trois zones sont réparties
  //     sur tout l'anneau ») : les relayeurs sont répartis sur l'ANNEAU, un
  //     par tranche, et les zones de transmission avec eux.
  const t = String(text || "");
  const anneau = /4\s*[x×]\s*(?:100|200|400)|\b(?:400|800|1000|1500|3000)\s*m|tours? de piste|demi.?fond|\bsteeple\b/i.test(t);
  if (!anneau) {
    // LIGNE DROITE : on POSE les acteurs sur les couloirs avant (géométrie en x
    // intacte) plutôt que de tout translater — une translation uniforme
    // poussait l'observateur et les libellés au ras du bord de l'image.
    // 86 : dans la bande de couloirs (qui commence à 84) avec la marge qu'il
    // faut pour que les ÉTIQUETTES, dessinées sous la pastille, ne soient pas
    // rognées par le bas de l'image.
    const Y_PISTE = 86, Y_BORD = 74;      // couloirs ; bord de pelouse (staff)
    // Le rectangle « Couloir » n'a plus de sens : la piste EST le couloir.
    scene.zones = (scene.zones || []).filter((z) => !/^couloir$/i.test(String(z.label || "")));
    // UNE ÉQUIPE PAR COULOIR. L'ancien test `team === "A" || team === "B"`
    // envoyait TOUTE équipe au-delà de B sur Y_BORD, c'est-à-dire sur la
    // PELOUSE (y=74 est à l'intérieur du gazon dès que x ∈ [15, 85]) : un
    // relais à trois équipes voyait la troisième courir sur le gazon — le
    // défaut exact que cette fonction existe pour empêcher, et celui que
    // l'enseignant avait déjà rejeté. Vu au rendu (salve athlé nº2, T02 et
    // T17). Les équipes prennent désormais les couloirs 1, 2, 3… ; seul le
    // PERSONNEL reste au bord de la pelouse, qui est bien sa place.
    const equipes = [...new Set((scene.players || []).filter((p) => !isStaffPlayer(p) && p.team).map((p) => p.team))].sort();
    for (const p of scene.players || []) {
      if (isStaffPlayer(p)) { p.y = Y_BORD; continue; }
      const voie = Math.min(4, Math.max(1, equipes.indexOf(p.team) + 1)) || 1;
      p.y = laneAt(0, voie).y;            // 86, 90, 94, 98 — la piste, par construction
      p.x = Math.round(clampSurPiste(+p.x, p.y) * 10) / 10;   // …et dans l'anneau, pas dans le coin
    }
    // Le matériel posé au sol (plots, haies, témoin, cibles) jalonne la PISTE ;
    // les libellés (consigne, cote) se rangent au bord. L'ancienne règle
    // décidait sur la POSITION de départ (`|y−50| < 25`), ce qui la rendait
    // non idempotente — elle renvoyait à 74 ce qu'elle venait de poser à 86,
    // et inversement au passage suivant. La nature du marqueur, elle, ne
    // change pas d'un appel à l'autre.
    // Un jalon se pose au BORD de la piste, pas dans le couloir où l'on court :
    // posés sur la ligne des coureurs, les plots passaient sous les pastilles
    // (mesuré : +4 `sur_marqueur` au banc). Ils prennent le couloir juste au-delà
    // du dernier occupé — quatre unités suffisent à dégager une pastille.
    const Y_JALON = laneAt(0, Math.min(4, (equipes.length || 1) + 2)).y;
    for (const m of scene.markers || []) {
      if (!Number.isFinite(+m.y)) continue;
      m.y = MARQUEURS_DOUX.has(String(m.type || "").toLowerCase()) ? Y_BORD : Y_JALON;
      m.x = Math.round(clampSurPiste(+m.x, m.y) * 10) / 10;
    }
    for (const z of scene.zones || []) {
      if (!Number.isFinite(+z.y)) continue;
      if (+z.h === 0) { z.y = Y_BORD - 6; continue; }   // cote (trait + libellé)
      z.y = Y_PISTE - 5; z.h = 10;                      // bandes : sur la piste
    }
    for (const a of scene.actions || []) {
      if (Number.isFinite(+a.toy)) a.toy = Y_PISTE;
      if (Number.isFinite(+a.fromy)) a.fromy = Y_PISTE;
    }
    return scene;
  }
  // ANNEAU : les coureurs prennent les quatre tranches de la piste, dans le
  // sens de la course (ligne droite avant -> virage -> ligne opposée -> virage).
  // LES POINTS VIENNENT DE laneAt() — la géométrie exacte de la piste — et non
  // plus d'ancres estimées à l'œil. Les deux ancres de virage précédentes,
  // (88,66) et (12,34), tombaient À L'INTÉRIEUR DE LA PELOUSE : sur l'ellipse
  // du virage (demi-axes 19,57 et 34) elles valent 0,958, où tout ce qui est
  // sous 1 est du gazon. La fonction chargée de sortir les coureurs de la
  // pelouse en posait donc elle-même la moitié dessus — mesuré sur la salve
  // athlé nº2 : 12 des 21 coureurs sur le gazon venaient de ces deux ancres.
  // C'est la leçon déjà écrite dans CLAUDE.md : la géométrie de référence se
  // LIT, elle ne s'estime pas.
  // Les relayeurs se répartissent RÉGULIÈREMENT sur le tour — quatre coureurs
  // donnent les quatre quarts (le comportement d'avant), trois donnent les
  // tiers, cinq les cinquièmes. L'ancien `i % 4` faisait retomber le 5e
  // exactement sur le 1er.
  const quart = (i, n, voie) => {
    const q = laneAt((i * LANE_LAP) / Math.max(1, n), voie);
    return { x: Math.round(q.x * 10) / 10, y: Math.round(q.y * 10) / 10 };
  };
  // Une équipe par couloir, ici aussi : le relais à trois équipes est le cas
  // qui a révélé le défaut, et l'anneau ne repositionnait que l'équipe A.
  const equipesA = [...new Set((scene.players || []).filter((p) => !isStaffPlayer(p) && p.team).map((p) => p.team))].sort();
  for (const [ti, eq] of equipesA.entries()) {
    if (eq === "A") continue;            // l'équipe A est traitée juste après
    const gens = (scene.players || []).filter((p) => p.team === eq && !isStaffPlayer(p));
    if (gens.length >= 2) gens.forEach((p, i) => { const q = quart(i, gens.length, Math.min(4, ti + 1)); p.x = q.x; p.y = q.y; });
  }
  const coureurs = (scene.players || []).filter((p) => p.team === "A" && !isStaffPlayer(p));
  if (coureurs.length >= 2) {
    const voieA = Math.min(4, Math.max(1, equipesA.indexOf("A") + 1)) || 1;
    coureurs.forEach((p, i) => { const q = quart(i, coureurs.length, voieA); p.x = q.x; p.y = q.y; });
    // COURSES : chaque relayeur court SA TRANCHE, en suivant le couloir.
    // L'élan court posé ici auparavant était un pis-aller de l'époque où le
    // rendu ne savait pas courber : « les trajectoires restent des segments,
    // le renderer ne courbe pas » (journal, session « le relais se court sur
    // la piste »). Depuis, `a.path` existe — c'est la demande explicite de
    // l'enseignant : « des flèches de course qui suivent le couloir (donc qui
    // acceptent les courbes) ». Et l'élan ne s'affichait même pas : il posait
    // `tox/toy` sans retirer `a.to`, que le rendu lit en priorité — d'où les
    // longues cordes en travers de la pelouse encore visibles sur la salve.
    const byId = Object.fromEntries(coureurs.map((p) => [p.id, p]));
    const pas = LANE_LAP / coureurs.length;
    for (const a of scene.actions || []) {
      if (a.type !== "course" || !byId[a.from]) continue;
      const i = coureurs.indexOf(byId[a.from]);
      a.path = laneSpan(i * pas, (i + 1) * pas, voieA);
      delete a.to;                       // sinon le rendu retomberait sur la corde
      const fin = a.path[a.path.length - 1];
      a.tox = fin.x; a.toy = fin.y;
    }
    // TRANSMISSIONS : une passe relie deux acteurs par un SEGMENT — entre deux
    // tranches opposées de l'anneau, elle traverserait la pelouse de part en
    // part. Sur l'anneau, la transmission est portée par la ZONE étiquetée et
    // la proximité des deux relayeurs ; on retire les passes longues (les
    // courtes, quand deux relayeurs sont voisins, restent lisibles).
    scene.actions = (scene.actions || []).filter((a) => {
      if (a.type !== "passe" || !byId[a.from] || !byId[a.to]) return true;
      const A = byId[a.from], B = byId[a.to];
      return Math.hypot(B.x - A.x, B.y - A.y) <= 22;
    });
    // LES ZONES DE TRANSMISSION, EN RUBAN LE LONG DU COULOIR. Elles étaient
    // posées en RECTANGLE tendu entre deux relayeurs de tranches opposées :
    // un tel rectangle couvre tout l'intérieur de l'anneau, c'est-à-dire la
    // pelouse — vu au rendu sur toute la salve athlé nº2 (T04, T17, T19).
    // Le ruban d'épaisseur constante le long d'une polyligne existe depuis le
    // gabarit relais (`z.band` + `z.thick`) : c'est lui qu'il fallait, et
    // `laneSpan` trace déjà la portion de couloir. Une zone se termine à la
    // marque de relais (au-delà, c'est la disqualification) et remonte de sa
    // longueur en amont.
    const zoneLen = Math.max(5, Math.min(30, Number((String(text || "")
      .match(/zone[^.]{0,30}?transmission[^.]{0,20}?(\d{1,2})\s*m/i) || [])[1]) || 20));
    const elanLen = Math.max(5, Math.min(20, Number((String(text || "")
      .match(/zone\s+d['’]?\s*[ée]lan[^.]{0,20}?(\d{1,2})\s*m/i) || [])[1]) || 10));
    const enRuban = (z, u0, u1, thick, style) => {
      const band = laneSpan(u0, u1, voieA);
      const xs = band.map((p) => p.x), ys = band.map((p) => p.y);
      z.band = band; z.thick = thick; z.style = style;
      z.x = Math.round(Math.min(...xs) - 3); z.y = Math.round(Math.min(...ys) - 3);
      z.w = Math.round(Math.max(...xs) - Math.min(...xs) + 6);
      z.h = Math.round(Math.max(...ys) - Math.min(...ys) + 6);
    };
    const estElan = (z) => /[ée]lan/i.test(String(z.label || ""));
    const zt = (scene.zones || []).filter((z) => /transmission|relais|t[ée]moin|passage/i.test(String(z.label || "")) && !estElan(z));
    zt.forEach((z, i) => {
      const marque = Math.min(i + 1, coureurs.length - 1) * pas;   // la fin de zone
      enRuban(z, marque - zoneLen, marque, 4.4, "dashed");
    });
    // La ZONE D'ÉLAN précède immédiatement la zone de transmission : c'est là
    // que le receveur se lance avant d'entrer dans la zone (reste ouvert de la
    // session relais, et demande explicite du prompt T19 de la salve).
    (scene.zones || []).filter(estElan).forEach((z, i) => {
      const marque = Math.min(i + 1, coureurs.length - 1) * pas;
      enRuban(z, marque - zoneLen - elanLen, marque - zoneLen, 4.4, "solid");
    });
    // Le couloir rectangulaire n'a plus de sens sur un anneau.
    scene.zones = (scene.zones || []).filter((z) => !/^couloir$/i.test(String(z.label || "")));
    // Toute zone RESTÉE rectangulaire et posée sur la pelouse serait lue comme
    // un tracé au sol au milieu du gazon : on la ramène en ruban sur le couloir,
    // à l'endroit le plus proche, en gardant son étendue.
    for (const z of scene.zones || []) {
      if (Array.isArray(z.band) || !Number.isFinite(+z.x) || !Number.isFinite(+z.y)) continue;
      const cx = +z.x + (+z.w || 0) / 2, cy = +z.y + (+z.h || 0) / 2;
      if (!surPelouseAthle(cx, cy)) continue;
      const u = abscisseCouloir(cx, cy, voieA);
      const demi = Math.max(4, Math.min(40, ((+z.w || 10) + (+z.h || 10)) / 4));
      enRuban(z, u - demi, u + demi, +z.h === 0 ? 1.8 : 4.4, +z.h === 0 ? "solid" : "dashed");
    }
  }
  // MARQUEURS DURS : les plots des 200 m d'une course en durée, les repères
  // d'une zone. Ils jalonnent la PISTE — ni le gazon, ni le couloir où l'on
  // court. Tous sont ramenés sur le couloir JUSTE AU-DELÀ du dernier occupé :
  // les projeter seulement quand ils étaient sur la pelouse ne suffisait pas,
  // car l'anneau de plots des scènes de tour de piste tombe déjà sur le
  // couloir 1 — et le placement corrigé des coureurs y amenait justement les
  // pastilles (mesuré : 4 `sur_marqueur` de plus au banc).
  // HORS du garde `coureurs.length >= 2` : une course en durée n'a qu'UN
  // coureur, et ses plots des 200 m restaient au milieu du gazon (T09).
  // +2 couloirs : aux extrémités des virages, deux couloirs voisins ne sont
  // écartés que de ~2,3 unités en x (l'ellipse comprime l'axe), et un seul
  // couloir d'écart laisse encore la pastille sur le plot.
  const voieJalon = Math.min(4, [...new Set((scene.players || [])
    .filter((p) => !isStaffPlayer(p) && p.team).map((p) => p.team))].length + 2);
  for (const m of scene.markers || []) {
    if (!Number.isFinite(+m.x) || !Number.isFinite(+m.y)) continue;
    if (MARQUEURS_DOUX.has(String(m.type || "").toLowerCase())) continue;
    const q = laneAt(abscisseCouloir(+m.x, +m.y, voieJalon), voieJalon);
    m.x = Math.round(q.x * 10) / 10; m.y = Math.round(q.y * 10) / 10;
  }
  return scene;
}

export function applyHalfCourt(scene, text, sport) {
  if (!scene || scene.meta?.halfCourt) return scene;
  // Invasion à but/panier seulement : au filet le « demi-terrain » n'existe
  // pas, et le basket 3x3 est DÉJÀ dessiné en demi-terrain.
  if (!["basketball", "handball", "football", "futsal"].includes(sport)) return scene;
  const t = String(text || "");
  if (!/demi[- ]terrain|demi[- ]court|(?:sur|dans) (?:une? |la |sa )?moiti[ée] d[eu] terrain/i.test(t)) return scene;
  // « deux demi-terrains », « chaque demi-terrain », « par demi-terrain » :
  // ce sont des ateliers PARALLÈLES sur le terrain complet, pas une demande.
  if (/(?:deux|2|chaque|par|les)\s+demi[- ]terrains?/i.test(t)) return scene;
  const ps = (scene.players || []).filter((p) => Number.isFinite(+p.x));
  if (!ps.length) return scene;
  // TOUT doit tenir dans la moitié droite : joueurs, extrémités d'actions,
  // matériel, zones. 45 (et pas 50) : un défenseur « à la ligne médiane » est
  // souvent posé à 45-50 — il finira au bord gauche, ce qui est sa place.
  const xs = [
    ...ps.map((p) => +p.x),
    ...(scene.actions || []).flatMap((a) => [a.tox, a.fromx].map(Number).filter(Number.isFinite)),
    ...(scene.markers || []).map((m) => +m.x).filter(Number.isFinite),
    ...(scene.zones || []).flatMap((z) => [+z.x, +z.x + (+z.w || 0)]).filter(Number.isFinite),
  ];
  if (!xs.length || Math.min(...xs) < 45) return scene;
  (scene.meta = scene.meta || {}).halfCourt = "right";
  return scene;
}

/* OVERLAY « PORTES SUR LES PASSES » — le texte décrit des passes À TRAVERS DES
   PORTES mais la scène routée n'a aucun matériel (ex. l'IA classe « passes dans
   les portes puis tir » en invasion) : on matérialise une porte (2 plots) au
   MILIEU de chaque trajectoire de passe, perpendiculaire à la passe. Sans effet
   si du matériel est déjà posé (gabarit portes_passes, découpage…). */
export function addPassGates(scene, text) {
  const t = String(text || "");
  if (!/\bportes?\b/i.test(t) || !/passe/i.test(t)) return scene;
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  // GARDE « PORTES DÉJÀ POSÉES » : une porte = DEUX plots proches (≤10). Si la
  // scène contient déjà ≥2 paires (gabarits portes_passes, défenseurs
  // entrants…), on ne rajoute rien. Les plots d'une DÉLIMITATION (coins d'un
  // carré, espacés), eux, ne comptent pas — un texte peut décrire les deux
  // (« rectangle délimité par des plots + 3 portes de coupelles »).
  {
    const plots = markers.filter((m) => m.type === "plot");
    let pairs = 0;
    for (let i = 0; i < plots.length; i++)
      for (let j = i + 1; j < plots.length; j++)
        if (Math.hypot(plots[i].x - plots[j].x, plots[i].y - plots[j].y) <= 10) { pairs++; break; }
    if (pairs >= 2) return scene;
  }
  const pos = {};
  for (const p of (scene.players || [])) pos[p.id] = { x: +p.x, y: +p.y };
  const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const W = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  const nm = t.match(/(\d+|deux|trois|quatre|cinq|six)\s*portes?/i);
  const nMax = Math.min(nm ? (W[nm[1].toLowerCase()] || parseInt(nm[1], 10) || 3) : 3, 4);
  const cl = (v) => Math.round(Math.max(2, Math.min(98, v)));
  let made = 0;
  for (const a of acts) {
    if (made >= nMax) break;
    // Le suivi des courses/conduites garde les positions à jour (les passes
    // suivantes partent de la position ATTEINTE).
    if ((a.type === "course" || a.type === "dribble") && a.from && pos[a.from] && a.tox != null) {
      pos[a.from] = { x: +a.tox, y: +a.toy }; continue;
    }
    if (a.type !== "passe" || !a.from || !pos[a.from]) continue;
    const from = pos[a.from];
    const to = a.to && pos[a.to] ? pos[a.to] : (a.tox != null ? { x: +a.tox, y: +a.toy } : null);
    if (!to) continue;
    const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2;
    const dx = to.x - from.x, dy = to.y - from.y, L = Math.hypot(dx, dy) || 1;
    const nx = -dy / L, ny = dx / L;                                     // normale à la passe
    // Passes ALLER-RETOUR entre joueurs immobiles : même segment -> même porte.
    // On ne matérialise pas deux fois la même (dédoublonnage par proximité).
    if (markers.some((m) => m.type === "plot" && Math.hypot(m.x - mx, m.y - my) < 8)) continue;
    markers.push({ type: "plot", x: cl(mx + nx * 4), y: cl(my + ny * 4) });
    markers.push({ type: "plot", x: cl(mx - nx * 4), y: cl(my - ny * 4) });
    made++;
  }
  return scene;
}

/* OVERLAY « PLOTS DE DÉPLACEMENT / REPÈRES » — « 2 plots au sol pour délimiter
   la zone de déplacement », « toucher un plot entre chaque frappe » : N plots
   de REPÈRE posés quand le texte les décrit SANS dimensions cotées (sinon
   addCourtDelimitation s'en charge) et qu'aucun plot n'est déjà dessiné.
   Placés autour de l'acteur qui TRAVAILLE (joueur A le plus proche), étalés en
   hauteur = zone latérale de déplacement. Générique tous sports. */
export function addMovementPlots(scene, text, sport) {
  const t = coreSituationText(String(text || "").toLowerCase());
  const m = t.match(/(\d+|un|deux|trois|quatre|cinq|six)\s*(?:plots?|c[ôo]nes?|coupelles?|rep[èe]res?)/);
  if (!m) return scene;
  // Contexte DÉPLACEMENT / REPÈRE uniquement (pas une porte, pas une aire cotée).
  if (!/d[ée]placement|rep[èe]re|toucher?\b|entre chaque|zone de d[ée]plac|appuis?\b/.test(t)) return scene;
  const dims = parseCourtDims(t);
  if (dims.len || dims.wid) return scene;                  // aire cotée -> addCourtDelimitation
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  if (markers.some((mk) => ["plot", "cone", "cône", "coupelle"].includes(String(mk.type || "").toLowerCase()))) return scene;
  const W = { un: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
  // « UN plot à toucher » (filet : « vient toucher un plot posé au centre de
  // son camp, puis réceptionne ») : un seul plot-repère, posé au centre du camp
  // du joueur qui SE DÉPLACE (celui qui a une course), pas une rangée de deux.
  if ((W[m[1]] || parseInt(m[1], 10)) === 1) {
    if (!SPORTS_FILET.has(sport) || !/touche/.test(t)) return scene;
    // Le toucheur : l'auteur de la première course, sinon l'auteur du DERNIER
    // renvoi (le geste travaillé) — le plot va au centre de SON camp.
    const byOrder = (a, b) => (a.order ?? 99) - (b.order ?? 99);
    const course = (scene.actions || []).filter((a) => a.type === "course" && a.from).sort(byOrder)[0];
    const renvoi = (scene.actions || []).filter((a) => a.type === "renvoi" && a.from).sort(byOrder).pop();
    const mover = (scene.players || []).find((p) => p.id === (course ? course.from : renvoi && renvoi.from));
    // « posé sur la ligne de côté / la ligne latérale » : au bord du terrain,
    // pas au centre du camp.
    const py1 = /ligne de c[ôo]t[ée]|ligne lat[ée]rale/.test(t) ? 86 : 50;
    markers.push({ type: "plot", x: mover && +mover.x > 50 ? 74 : 26, y: py1 });
    return scene;
  }
  const n = Math.max(2, Math.min(5, W[m[1]] || parseInt(m[1], 10) || 2));
  const worker = (scene.players || []).filter((p) => p.team === "A").sort((a, b) => a.x - b.x)[0];
  const wx = worker ? +worker.x : 16;
  const px = Math.round(Math.min(wx + 8, 44));
  for (let i = 0; i < n; i++) {
    const py = Math.round(Math.max(14, Math.min(86, 50 + (i - (n - 1) / 2) * (60 / Math.max(1, n - 1)))));
    markers.push({ type: "plot", x: px, y: py });
  }
  return scene;
}

/* OVERLAY « CIBLE AU SOL » (filet) — « renvoie en manchette vers une cible au
   sol » : la cible visée par le renvoi du travailleur est MATÉRIALISÉE (un
   cerceau au point de chute du premier renvoi qui répond à l'envoi). Sans
   effet si une cible/un cerceau existe déjà (gabarits zones/cibles). */
export function addGroundTarget(scene, text) {
  const t = String(text || "");
  if (!/cible\s+(?:au sol|pos[ée]e|[àa] terre)|vers\s+(?:une|la|sa)\s+cible|dans\s+(?:un|le)\s+cerceau/i.test(t)) return scene;
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  if (markers.some((m) => ["cible", "cerceau"].includes(String(m.type || "").toLowerCase()))) return scene;
  const renvois = (scene.actions || []).filter((a) => a.type === "renvoi" && a.from && a.tox != null)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  if (!renvois.length) return scene;
  // Le renvoi VISEUR = la première réponse d'un AUTRE joueur que l'engageur
  // (c'est le geste travaillé) ; à défaut, le premier renvoi.
  const aim = renvois.find((a) => a.from !== renvois[0].from) || renvois[0];
  markers.push({ type: "cerceau", x: Math.round(aim.tox), y: Math.round(aim.toy) });
  return scene;
}

/* OVERLAY « JOKERS » (invasion) — « deux jokers offensifs qui jouent avec
   l'équipe en possession » : les N derniers attaquants prennent le rôle
   « Joker » (le 3x3 a déjà le sien via normalize3x3 — respecté). */
export function addJokers(scene, text, sport) {
  if (!INVASION_OVERLAY_SPORTS.has(sport)) return scene;
  const t = coreSituationText(String(text || ""));
  const m = t.match(/\b(un|une|deux|trois|[123])\s+jokers?\b/i) || (/\bjokers?\b/i.test(t) ? [null, "un"] : null);
  if (!m) return scene;
  if ((scene.players || []).some((p) => /joker/i.test(p.role || ""))) return scene;
  const n = { un: 1, une: 1, deux: 2, trois: 3 }[String(m[1]).toLowerCase()] || parseInt(m[1], 10) || 1;
  const A = (scene.players || []).filter((p) => p.team === "A" && !/attente|gardien/i.test(p.role || ""));
  if (A.length <= n) return scene;
  A.slice(-n).forEach((p) => { p.role = "Joker"; });
  return scene;
}

/* OVERLAY « CERCEAUX DANS LES INTERVALLES » (basket) — « trois cerceaux posés
   au sol matérialisent les intervalles de la zone » : cerceaux posés dans les
   trous de la zone (tête de raquette + ailes), calés sur la rangée avant des
   défenseurs dessinés. */
export function addZoneGapHoops(scene, text, sport) {
  if (!["basketball", "basket3x3"].includes(sport)) return scene;
  const t = coreSituationText(String(text || ""));
  if (!/cerceaux?[^.;]{0,40}intervalles?|intervalles?[^.;]{0,40}cerceaux?/i.test(t)) return scene;
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  if (markers.some((m) => m.type === "cerceau")) return scene;
  const defs = (scene.players || []).filter((p) => p.team === "B");
  if (defs.length < 3) return scene;
  const frontX = Math.min(...defs.map((p) => +p.x));
  const n = /trois|3\s*cerceaux?/i.test(t) ? 3 : 3;
  const spots = [{ x: frontX + 6, y: 50 }, { x: frontX + 9, y: 30 }, { x: frontX + 9, y: 70 }];
  for (const s of spots.slice(0, n)) markers.push({ type: "cerceau", x: Math.round(clampInt(s.x, 6, 94) ?? s.x), y: s.y });
  return scene;
}

/* OVERLAY « DÉFENSEUR RETARDATAIRE » (invasion) — « au signal, un SECOND
   défenseur part de la ligne de fond et sprinte pour revenir défendre » : le
   « +1 » du surnombre (2c1+1, 3c2+1) est dessiné à la ligne de fond du camp de
   départ avec sa course de poursuite — quelle que soit la route. Gate étroite :
   un défenseur SUPPLÉMENTAIRE explicitement décrit, petite scène de surnombre. */
const INVASION_OVERLAY_SPORTS = new Set(["football", "futsal", "handball", "basketball", "basket3x3", "rugby", "ultimate"]);
export function addLateDefender(scene, text, sport) {
  if (!INVASION_OVERLAY_SPORTS.has(sport)) return scene;
  const t = coreSituationText(String(text || ""));
  if (!/(?:seconde?|deuxi[èe]me|2e|autre)\s+d[ée]fenseur[^.;]{0,80}?(?:part|s'[ée]lance|sprinte?)/i.test(t)) return scene;
  const players = Array.isArray(scene.players) ? scene.players : [];
  const defs = players.filter((p) => p.team === "B" && !/gardien/i.test(p.role || ""));
  if (!defs.length || defs.length >= 3) return scene;          // le « +1 » d'un petit surnombre uniquement
  if (players.some((p) => /retardataire/i.test(p.role || ""))) return scene;
  const atts = players.filter((p) => p.team === "A" && !/attente/i.test(p.role || ""));
  if (!atts.length) return scene;
  const minX = Math.min(...atts.map((p) => +p.x));
  let num = 0; for (const p of players) num = Math.max(num, +p.num || 0);
  const dy = atts.some((p) => Math.abs(p.y - 22) < 10) ? 78 : 22;   // à l'écart des attaquants
  players.push({ id: "DR", num: ++num, role: "Retardataire", team: "B", color: "#D55E00", x: Math.max(3, Math.round(minX - 5)), y: dy });
  (scene.actions || (scene.actions = [])).push({ from: "DR", tox: 80, toy: 46, type: "course", style: "dashed", order: 98, label: "Sprint de repli au signal" });
  return scene;
}

/* OVERLAY « DÉPART MATÉRIALISÉ » — « l'attaquant part d'un plot placé… »,
   « départ au cône » : on pose un plot à la position INITIALE du premier acteur
   de la phase (s'il n'y en a pas déjà un à proximité). Générique tous sports. */
const START_PLOT_RE = /part\s+(?:d['’]un|du)\s+(?:plot|c[ôo]ne)|d[ée]part\s+(?:d['’]un|du|au)\s+(?:plot|c[ôo]ne)|(?:plot|c[ôo]ne)\s+de\s+d[ée]part/i;
export function addStartPlot(scene, text) {
  if (!START_PLOT_RE.test(String(text || ""))) return scene;
  const acts = (Array.isArray(scene.actions) ? scene.actions : []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const first = acts.find((a) => a.from);
  const p = first && (scene.players || []).find((pl) => pl.id === first.from);
  if (!p) return scene;
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  if (markers.some((m) => m.type === "plot" && Math.hypot(m.x - p.x, m.y - p.y) < 8)) return scene;
  markers.push({ type: "plot", x: Math.max(2, Math.round(p.x - 5)), y: Math.max(2, Math.round(p.y - 4)) });
  return scene;
}

/* OVERLAY générique « terrain délimité/balisé par des plots, de N m sur M m » : ajoute des
   PLOTS aux 4 coins + milieux des côtés d'un rectangle ENGLOBANT les joueurs, et COTE les
   dimensions lues dans le texte. Applicable à tout sport (volley 4x4 sur 14x6, hand sur
   demi-terrain balisé, foot réduit…) — c'est une annotation, le fond de terrain reste dessiné. */
const DELIM_RE = /d[ée]limit|balis|jalonn|mat[ée]rialis|\bplots?\b|\bc[ôo]nes?\b|coupelles?/i;
/* (parseCourtDims / plotColorFromText : partagés avec les gabarits locaux —
   définis dans parser.js.) */
export function addCourtDelimitation(scene, text) {
  const t = String(text || "");
  if (!DELIM_RE.test(t)) return scene;
  const dims = parseCourtDims(t);
  if (!dims.len && !dims.wid) return scene;                 // pas de cote -> on ne fabrique rien
  const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
  // MATÉRIEL DÉJÀ POSÉ (gabarit qui délimite sa propre aire : toro, portes…) :
  // on ne superpose pas un second rectangle de plots.
  if (markers.filter((m) => m.type === "plot").length >= 4) return scene;
  // L'emprise du terrain délimité englobe les JOUEURS (hors observateur, posté
  // en dehors) ET le MATÉRIEL déjà posé (zones pleines : carrés de couleur,
  // portes…) — un porteur seul au centre ne doit pas réduire la boîte à un point.
  const players = (Array.isArray(scene.players) ? scene.players : []).filter((p) => p.team !== "OBS");
  if (players.length < 1) return scene;
  const clamp = (v, lo, hi) => Math.round(Math.max(lo, Math.min(hi, v)));
  const xs = players.map((p) => +p.x || 0), ys = players.map((p) => +p.y || 0);
  for (const z of (Array.isArray(scene.zones) ? scene.zones : [])) {
    if (!(+z.w > 0 && +z.h > 0)) continue;
    xs.push(+z.x, +z.x + +z.w); ys.push(+z.y, +z.y + +z.h);
  }
  const x0 = clamp(Math.min(...xs) - 6, 3, 42), x1 = clamp(Math.max(...xs) + 6, 58, 97);
  const y0 = clamp(Math.min(...ys) - 6, 3, 32), y1 = clamp(Math.max(...ys) + 6, 68, 97);
  const taken = new Set(markers.filter((m) => m.type === "plot").map((m) => `${Math.round(m.x)},${Math.round(m.y)}`));
  const mx = Math.round((x0 + x1) / 2), my = Math.round((y0 + y1) / 2);
  // NOMBRE DE PLOTS annoncé (« délimité par 4 plots ») : ≤4 -> les coins
  // seulement ; sinon coins + milieux des côtés (8). Couleur lue du texte.
  const pm = t.match(/(\d+|quatre|six|huit)\s*(?:plots?|c[ôo]nes?|coupelles?)/i);
  const NW = { quatre: 4, six: 6, huit: 8 };
  const nPlots = pm ? (NW[pm[1].toLowerCase()] || parseInt(pm[1], 10)) : 8;
  const color = plotColorFromText(t);
  const spots = nPlots <= 4
    ? [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]
    : [[x0, y0], [x1, y0], [x1, y1], [x0, y1], [mx, y0], [mx, y1], [x0, my], [x1, my]];
  for (const [px, py] of spots) {
    const k = `${px},${py}`;
    if (!taken.has(k)) { markers.push({ type: "plot", x: px, y: py, ...(color ? { color } : {}) }); taken.add(k); }
  }
  // COTES : la LONGUEUR avec l'élément « cote » de la bibliothèque (📏 double
  // flèche à butées) au-dessus du bord haut ; la largeur (si différente) reste
  // une ligne verticale étiquetée (la cote ne se dessine qu'à l'horizontale).
  const zones = Array.isArray(scene.zones) ? scene.zones : (scene.zones = []);
  if (dims.len) markers.push({ type: "cote", x: mx, y: Math.max(2, y0 - 4), w: x1 - x0, text: dims.len });
  // Carré (wid == len) : une seule cote suffit — pas de doublon vertical.
  if (dims.wid && dims.wid !== dims.len) zones.push({ x: x0, y: y0, w: 0, h: y1 - y0, style: "dashed", label: dims.wid });
  // ZONES D'EN-BUT matérialisées (rugby / ultimate) : une bande hachurée à
  // chaque extrémité du terrain délimité.
  if (/zones?\s+d['’]en.?but|en.?but\s+(?:de|matérialis)/i.test(t)) {
    const w = Math.min(10, Math.round((x1 - x0) / 6));
    zones.push({ x: x0, y: y0, w, h: y1 - y0, style: "dashed", label: "En-but" });
    zones.push({ x: x1 - w, y: y0, w, h: y1 - y0, style: "dashed", label: "En-but" });
  }
  return scene;
}

/* Détecte un observateur/arbitre décrit dans le texte et l'ajoute avec sa
   BULLE de consigne. Deux familles de rôles (réutilisable filet, invasion…) :
   1) il ANNONCE un signal (« annonce la couleur », « au top ») ;
   2) il OBSERVE/COMPTE (« un observateur compte les paniers marqués », « un
      camarade note les réussites », « chronomètre le passage ») — rôle
      d'observation entre pairs, très courant en EPS. */
const CONSIGNE_VERB = /annonce|signale|crie|appelle|valide|[àa] voix haute|donne le signal|au signal|au top|coup de sifflet/i;
const OBSERVE_VERB = /(compt\w*|not[ée]\w*|note[nrz]?t?\b|chronom[èe]tr\w*|[ée]valu\w*|rel[èe]v\w*|enregistr\w*|observ\w*|comptabilis\w*)\s*((?:les?|la|l['’]|des?|du|chaque)\s+[A-Za-zà-ÿ'’ -]{2,24})?/i;
export function addObserverConsigne(scene, text) {
  const t = String(text || "");
  if (!/observ|arbitre|coach|meneur|enseignant|professeur|entra[iî]neur|animateur|partenaire ext|juge\b|chronom|camarade|secr[ée]taire/i.test(t)) return scene;
  const players = Array.isArray(scene.players) ? scene.players : (scene.players = []);
  if (players.some((p) => p.team === "OBS" || /observ|arbitre/i.test(p.role || ""))) return scene;   // déjà présent
  // POSTE DEMANDÉ (retour prod 👎 : « il était demandé de le placer en ligne de
  // fond ! ») — la demande explicite prime sur le poste par défaut (médiane).
  // « Ligne de fond / derrière le but-panier » = côté ATTAQUÉ (droite).
  const atEndline = /ligne de fond|derri[èe]re (?:le|la|les) (?:but|panier|cage|en-?but)/i
    .test(String(text || ""));
  const spot = atEndline ? { x: 97, y: 88, bx: 88, by: 84 } : { x: 50, y: 95, bx: 50, by: 91 };
  const put = (word) => {
    const markers = Array.isArray(scene.markers) ? scene.markers : (scene.markers = []);
    // Par défaut : à hauteur du filet (x = 50, la médiane / le filet) et DANS
    // le couloir de double (bord du terrain) — la place de l'arbitre.
    players.push({ id: "OBSx", role: "Observateur", team: "OBS", color: "#F2C200", x: spot.x, y: spot.y });
    markers.push({ type: "consigne", x: spot.bx, y: spot.by, text: word });
    return scene;
  };
  // 1) SIGNAL ANNONCÉ — mot entre guillemets en priorité, sinon après le verbe.
  if (CONSIGNE_VERB.test(t)) {
    let word = null;
    // 1) Guillemets FRANÇAIS « … » — le signal annoncé le plus fiable
    //    (« fixé ! », « Top ! ») : prime sur tout.
    const qf = t.match(/«\s*([^»]{1,20})\s*»/);
    if (qf) word = qf[1];
    // 2) SIGNAL DE DÉPART explicite (« au top », « au signal », « coup de
    //    sifflet ») -> « Top ! ». Placé AVANT les guillemets GÉNÉRIQUES pour
    //    qu'une apostrophe d'ÉLISION (« au top de L'enseignant L'attaque »)
    //    ne soit pas prise pour des guillemets et ne capture « enseignant l ».
    if (!word && /au\s+top|au\s+signal|coup de sifflet|signal sonore/i.test(t)) word = "Top !";
    // 3) Guillemets génériques (apostrophes droites/typographiques, guillemets ").
    if (!word) {
      const q = t.match(/["'‘“]\s*([^»"'’”]{1,20})\s*["'’”]/);
      if (q) word = q[1];
    }
    if (!word) { const b = t.match(/(?:annonce|signale|crie|appelle|valide|dit)\s+(?:le |la |l['’]|un |une )?([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ'’-]{1,18})/i); if (b) word = b[1]; }
    // Mot-outil capturé (« annonce SA zone », « annonce SI la frappe… ») :
    // pas un signal — on retombe sur la branche observation/comptage.
    if (word && /^(?:sa|son|ses|si|que|quand|leur|leurs)$/i.test(word.trim())) word = null;
    if (!word && /au signal|au top|coup de sifflet|signal sonore/i.test(t)) word = "Top !";
    if (word) {
      word = word.trim().replace(/\s+/g, " ").slice(0, 18);
      return put(word.charAt(0).toUpperCase() + word.slice(1));
    }
    // Pas de mot extrait (« compte à voix haute les réceptions ») : on retombe
    // sur la branche observation/comptage plutôt que d'abandonner l'observateur.
  }
  // 2) OBSERVATION / COMPTAGE — verbe et objet lus dans la PHRASE de
  //    l'observateur (« compte les paniers marqués » -> bulle homonyme).
  //    « meneur » exclu ici : c'est d'abord un POSTE de jeu (basket).
  // TOUS les segments qui mentionnent l'observateur sont lus : la personne est
  // souvent introduite dans une phrase de dispositif (« … et un observateur »)
  // et son ACTION dans la suivante (« L'observateur compte à voix haute … ») —
  // ne lire que le premier segment perdait l'observateur.
  const segs = t.split(/[.;\n]/).filter((s) => /observ|arbitre|juge\b|chronom|camarade|secr[ée]taire|coach|enseignant|professeur/i.test(s));
  // Le NOM de la personne est retiré avant de chercher le verbe : sinon
  // « observATEUR » matchait /observ\w*/ et volait la place du vrai verbe
  // (« compte les paniers marqués » -> bulle « Observe » au lieu du comptage).
  let o = null;
  for (const seg of segs) {
    o = seg.replace(/observat\w+|arbitres?|juges?|chronom[èe]treu\w+|camarades?|secr[ée]taires?|coach\w*|enseignant\w*|professeur\w*/gi, "").match(OBSERVE_VERB);
    if (o) break;
  }
  // 3) OBSERVATEUR NOMMÉ SANS VERBE RECONNU (retours prod 👎 PROD3/PROD9) — le
  //    texte pose la personne dans le dispositif mais décrit sa tâche avec un
  //    verbe hors liste (« se place derrière le terrain pour VÉRIFIER la
  //    communication », « REPÉRER les fentes trop tardives », « un officiel qui
  //    donne le départ »). L'abandonner, c'est dessiner une autre situation :
  //    on la dessine avec la bulle générique, comme le fait `observerFromText`.
  //    RESTREINT aux noms qui désignent VRAIMENT un observateur : « enseignant »
  //    ou « coach » ouvrent la fonction (bulle de consigne) mais ne sont pas des
  //    observateurs — les dessiner comme tels sur tout texte qui les mentionne
  //    peuplerait la moitié du corpus d'observateurs fantômes.
  if (!o) return /observateur|observatrice|arbitr|juge\b|chronom[èe]tr|secr[ée]taire|marqueur/i.test(t) ? put("Observe") : scene;
  const verb = o[1].toLowerCase();
  const base = /chronom/.test(verb) ? "Chronomètre" : /not[ée]|note/.test(verb) ? "Note"
    : /[ée]valu/.test(verb) ? "Évalue" : /rel[èe]v/.test(verb) ? "Relève"
    : /enregistr/.test(verb) ? "Note" : /compt/.test(verb) ? "Compte" : "Observe";
  const obj = (o[2] || "").trim().replace(/\s+/g, " ");
  // 44 = 2 lignes de 22 : le rendu REPLIE désormais les bulles (wrapLabel) —
  // tronquer à 32 dès la création coupait la consigne en plein mot (« Observe
  // les courses vers le pani ») avant même que le rendu puisse la replier.
  return put((base + (obj ? " " + obj : "")).slice(0, 44));
}


/* CONTRÔLE QUALITÉ (architecture v2, étape 2) — détecteurs OBJECTIFS appliqués
   à une scène AVANT affichage. Une scène (souvent issue de l'analyse IA) qui
   échoue est écartée -> repli sur le moteur local. Renvoie la liste des
   problèmes trouvés (tableau vide = scène saine). On n'utilise JAMAIS la
   `confidence` de l'IA : seulement des faits géométriques vérifiables. */
const GOAL_SPORTS = ["football", "futsal", "handball", "basketball", "basket3x3"];
export function qualityIssues(scene, sport, text) {
  const issues = [];
  const players = Array.isArray(scene?.players) ? scene.players : [];
  const acts = (Array.isArray(scene?.actions) ? scene.actions : []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  // Scène d'ATELIER / DISPOSITIF (rotation/postes, couloirs, cibles) : chaque cellule
  // est une mini-scène locale — les contrôles « de jeu » (tir depuis son camp, porteur
  // unique) n'y ont aucun sens. Signal robuste : une flèche de rotation, des étiquettes
  // d'atelier/couloir, OU PLUSIEURS tirs parallèles (un par poste/couloir).
  const isDrill = acts.some((a) => a.type === "rotation")
    || acts.filter((a) => a.type === "tir").length >= 2
    || (Array.isArray(scene?.zones) ? scene.zones : []).some((z) => /^(Atelier|Poste|Station|Couloir)\b/i.test(z.label || ""));
  // 1) TIR DEPUIS SON PROPRE CAMP — dans un sport à but (attaqué à droite,
  //    x≈100), la conclusion doit partir de la moitié offensive. On calcule
  //    l'origine RÉELLE du tir en REJOUANT le suivi de position du rendu
  //    (render.js : une course/conduite/écran déplace le joueur, donc le tir
  //    part de la position ATTEINTE, pas de la pastille de départ). Un tir
  //    déclenché depuis son propre camp (x < 50) = le porteur n'a pas franchi la
  //    médiane : flèche qui traverse le terrain (cas IA faibles : 2c1, écran...).
  //    Coupe nette : set-plays locaux sains à x≈72-87 ; scènes IA cassées ≤46.
  if (GOAL_SPORTS.includes(sport) && !isDrill) {
    const pos = {}; players.forEach((p) => { pos[p.id] = { x: +p.x, y: +p.y }; });
    let origin = null;
    for (const a of acts) {
      if (a.type === "tir") { origin = a.fromx != null ? a.fromx : pos[a.from]?.x; break; }
      if ((a.type === "course" || a.type === "dribble" || a.type === "ecran") && a.tox != null && a.from && pos[a.from]) pos[a.from] = { x: a.tox, y: a.toy };
    }
    // THÈME EXPLICITE « marquer depuis son camp » (salve futsal F11 : « tout
    // but marqué depuis son propre camp compte double ») : la frappe lointaine
    // EST l'objet de la situation — sans cette dérogation, le thème est
    // indessinable (double-bind : être fidèle = être rejeté). Resserrée sur un
    // mot de TIR devant la locution (« du milieu de terrain » seul décrit une
    // position, pas une frappe). Mesuré : 0 occurrence au corpus, gratuite.
    const longShotTheme = /(?:but|marqu\w*|frapp\w*|tir\w*|lob\w*)[^.;]{0,30}(?:depuis|dès|de) (?:son|sa|leur) (?:propre )?(?:camp|moiti[ée])|(?:frappe|tir)s?[^.;]{0,20}(?:de loin|longue distance|du milieu de terrain)/i.test(String(text || ""));
    if (origin != null && origin < 50 && !longShotTheme) issues.push(`tir depuis son propre camp (origine x=${Math.round(origin)} < 50)`);
  }
  // 2) PASSE / CONDUITE FANTÔME — un joueur émet une passe (ou conduit le
  //    ballon) alors qu'il ne l'a PAS : on suit le porteur le long de la phase
  //    (le ballon change de mains sur passe/centre, part au tir). Une passe/
  //    conduite issue d'un non-porteur est incohérente (cas IA : pick&roll où
  //    « 1 » repasse après avoir déjà donné le ballon).
  // Atelier MULTI-FILES (passe-et-suit en deux files, relais…) : chaque file a SON
  // ballon, donc le suivi à porteur unique ne s'applique pas — on saute le contrôle
  // dès qu'il y a au moins deux ballons en jeu.
  /* ⚠️ CE SUIVI ACCUSAIT PLUS SOUVENT QU'IL N'AVAIT RAISON (mesure du 13/08,
     650 scènes IA capturées sur les 19 lots d'invasion, variance de tirage
     nulle) : sur les 20 scènes qu'il signalait, 11 étaient des FAUX POSITIFS —
     55 %, sur la PREMIÈRE cause de rejet de la route qui sert. Son modèle de
     possession ignorait trois choses que les scènes disent pourtant :
       • la passe qui ne vise PAS un partenaire identifié — passe dans l'espace,
         relance longue sur un joueur lancé (4 cas), check-ball rendu par
         l'adversaire ou feinte de passe (1 cas). Le porteur y devient INCONNU,
         et `null` veut dire « je ne sais pas », jamais « personne » : le suivi
         reprend au premier émetteur suivant, sans accuser ;
       • la RÉCUPÉRATION du ballon (3 cas) — interception, pressing qui aboutit :
         le cœur même des situations de transition, où tout ce qui suit était
         accusé ;
       • les ATELIERS EN PARALLÈLE (2 cas), où chaque groupe a son ballon même
         quand l'IA n'en dessine qu'un — le garde-fou `nBalls` ne voyait rien.
     Mesure : signalements 20 -> 10, dont les NEUF vrais défauts tous conservés ;
     acceptées 297 -> 299/325, rejets 28 -> 26, lisibilité inchangée au bit près.
     Prédicats RÉUTILISÉS, pas réécrits : `parallelGroupsCount` est celui du
     moteur (parser.js). Et le vocabulaire de récupération a été relevé SUR LES
     650 SCÈNES, pas deviné : « Reprise » (23 occurrences) est une frappe
     — reprise de volée, du plat du pied — et « contre » désigne la phase qui
     suit (« Contre-attaque »), pas le gain du ballon ; les inclure aurait
     éteint le contrôle sur des scènes saines. Restent `intercept` et `récup`,
     les deux seuls qui désignent toujours un changement de main.
     RESTE OUVERT, mesuré et non traité : l'ALIMENTATION (1 cas — « un panier de
     balles, sert alternativement les deux ailiers » : le serveur garde le
     ballon). `isOneWayFeed` (parser.js) est le prédicat naturel mais répond
     `false` ici, éteint par le « renvoie » du ramasseur. Un seul cas ne
     justifie pas d'en dériver un jumeau. */
  const nBalls = (Array.isArray(scene?.markers) ? scene.markers : []).filter((m) => m.type === "ballon").length;
  /* TROIS CAMPS NE S'AFFRONTENT PAS, ILS TRAVAILLENT EN PARALLÈLE. Cinquième
     angle mort, trouvé sur la seconde capture : un relais 4×100 « trois équipes,
     une par couloir » dessine des transmissions SIMULTANÉES (même `order`), une
     par équipe — et le suivi à porteur unique accusait la deuxième. Aucun
     marqueur `ballon` là-dedans : le garde-fou `nBalls` ne pouvait rien voir.
     Signal STRUCTUREL, sans lecture de texte : un match oppose exactement deux
     camps ; une troisième équipe de jeu ne peut être qu'un groupe parallèle. */
  const camps = new Set(players.map((p) => String(p.team || "")).filter((t) => t && t !== "k" && t !== "OBS")).size;
  if (nBalls < 2 && camps < 3 && parallelGroupsCount(text) < 2) {
    const TURNOVER = /intercept|r[ée]cup/i;
    /* …sauf quand l'objet de « récupérer » est QUELQU'UN : « récupère l'ailier »,
       « récupère le corner » (= le joueur laissé libre au corner) sont du
       MARQUAGE défensif, pas un gain de balle. Trouvé en vérifiant les
       signalements un par un après le correctif : ce seul mot accusait à tort
       une scène de plus (v33 K13) qu'il n'en sauvait. Le ballon, lui, se
       nomme — « récupère la balle », « récupère et avance ». */
    const MARKING = /r[ée]cup\S*\s+(?:l['’]|(?:les|le|la|son|sa|ses|un|une)\s+)(?!ballon|balle|disque)/i;
    const teamOf = {}; players.forEach((p) => { teamOf[p.id] = String(p.team || ""); });
    // ADVERSAIRE au sens strict : les deux camps de JEU. Un gardien ("k") ou un
    // enseignant ("OBS") n'est l'adversaire de personne — leur passe transmet.
    const rival = (x, y) => (teamOf[x] === "A" && teamOf[y] === "B") || (teamOf[x] === "B" && teamOf[y] === "A");
    let holder = null;
    for (const a of acts) {
      // Le ballon change de main quand la scène le DIT — y compris sur une
      // action que ce suivi ne regarde pas (l'interception est souvent une
      // `course`).
      if (a.from && TURNOVER.test(a.label || "") && !MARKING.test(a.label || "")) holder = a.from;
      const t = a.type;
      if (!["passe", "centre", "dribble", "tir"].includes(t)) continue;
      if (holder == null) holder = a.from;                       // 1er porteur déduit
      else if ((t === "passe" || t === "centre" || t === "dribble") && a.from && a.from !== holder) {
        issues.push(`${t} fantôme : ${a.from} n'a pas le ballon`); break;
      }
      if (t === "passe" || t === "centre") holder = (a.to && !rival(a.from, a.to)) ? a.to : null;
      else if (t === "tir") holder = null;
    }
  }
  // 3) ÉCRAN ANNONCÉ MAIS NON DESSINÉ — une action est libellée « écran/bloc » mais
  //    aucune action de type `ecran`. EXCLUT les sports de filet : « bloc » y est une
  //    FRAPPE (contre le top-spin), pas un écran.
  //    Et « bloc » n'est un écran que POSÉ (« pose un bloc ») : dans les sports
  //    à but c'est presque toujours le BLOC DÉFENSIF (« relance par-dessus le
  //    bloc », « contre un bloc médian ») — pas un écran à dessiner.
  if (!SPORTS_FILET.has(sport)
    && acts.some((a) => {
      const L = a.label || "";
      // « écran POUR A2 » (écran indirect au profit d'un partenaire) : la
      // course de placement du poseur EST le rendu — pas une omission.
      return (/[ée]cran/i.test(L) && !/[ée]cran\s+pour/i.test(L))
        || (/\bbloc\b/i.test(L) && /pos[ée]?\w*\s+(?:un|le|son)\s+bloc|fait\s+(?:un|le)\s+bloc|bloc\s+(?:pour|sur)\b/i.test(L));
    }) && !acts.some((a) => a.type === "ecran"))
    // Message PRESCRIPTIF (salve 3x3 T13 : la scène portait toutes les courses
    // de la DÉFENSE d'écran mais jamais l'écran lui-même, et la réparation
    // tournait en rond sur le constat) : dire le geste à dessiner.
    issues.push("écran annoncé mais non dessiné — AJOUTER une flèche de type « ecran » : elle part du POSEUR et s'arrête sur le défenseur visé, juste avant la course qui exploite l'écran");
  return issues;
}

export function sceneFromAnalysis(analyse, sport, text) {
  try {
    if (!analyse || analyse.version !== 1) return null;
    let s = null;
    if (analyse.famille === "filet") s = filetScene(analyse, sport, text);
    else if (analyse.famille === "invasion") s = invasionScene(analyse, sport, text);
    else if (analyse.famille === "parcours") s = parcoursScene(analyse, sport, text);
    else if (analyse.famille === "atelier") s = atelierScene(analyse, sport, text);
    else if (analyse.famille === "dispositif") s = dispositifScene(analyse, sport, text);
    else if (analyse.famille === "defense") s = defenseFromAnalysis(analyse, sport, text);
    // cible : routage regex fiable, pas d'analyse (encore)
    // CALQUE « decoupage » (hybride) : terrain découpé en zones par des lignes/
    // plots, applicable À TOUTE famille. On l'ajoute par-dessus la scène.
    if (s && analyse.decoupage && /diag/i.test(String(analyse.decoupage.sens || "")) && analyse.famille === "invasion") {
      // DÉCOUPAGE EN DIAGONALE : deux 3v2 simultanés en miroir (un par demi-terrain).
      applyDiagonalMirror(s);
    } else if (s && analyse.decoupage) {
      // En invasion, le jeu se déroule dans la moitié attaquée : on découpe de
      // la médiane (50) à la ligne de fond, pas tout le terrain.
      const dkOpts = analyse.famille === "invasion" ? { base: 50 } : {};
      const ov = decoupageFromAnalysis(analyse.decoupage, sport, dkOpts);
      if (ov) {
        s.zones = [...(s.zones || []), ...ov.zones];
        s.markers = [...(s.markers || []), ...ov.markers];
        // « 1 défenseur par zone » : on répartit les défenseurs dans les bandes.
        // EXCLU : les joueurs d'un MUR de coup franc (formés par normalizeFreeKick) —
        // un découpage halluciné par l'IA les arrachait du mur pour les répartir.
        if (analyse.famille === "invasion" && ov.bands && ov.bands.length) {
          const defs = (s.players || []).filter((p) => p.team === "B" && !/gardien|^mur$/i.test(p.role || ""));
          defs.forEach((d, i) => { const b = ov.bands[Math.min(i, ov.bands.length - 1)]; if (b) d.x = b.x; });
        }
      }
    }
    // GROUPES EN PARALLÈLE (filet) : « terrain coupé en deux dans la largeur pour
    // faire travailler 2 groupes » -> on duplique l'exercice dans N bandes empilées.
    if (s && analyse.famille === "filet") {
      const ng = parallelGroupsCount(text);
      if (ng >= 2) applyParallelGroups(s, ng);
    }
    if (s && s.analyse) delete s.analyse;   // l'analyse vit dans meta, pas dans la scène
    return s;
  } catch (_) {
    return null;   // une analyse malformée ne doit JAMAIS casser la génération
  }
}


/* REPLACEMENT ENTRE LES FRAPPES (chantier « repli », 08/08) -----------------
   Mesure : sur les 45 scènes de filet du lot doré où un replacement est
   DEMANDÉ, le pipeline classique n'en dessinait aucun dans 49 % des cas. Le
   rallye générique a été traité dans parser.js ; il restait 15 cas, tous bâtis
   par `filetScene` — 9 par la route template (buildRacketSequence produit une
   analyse « filet ») et 6 par la route analysis. Les deux passent ICI, d'où un
   seul correctif. Et ce ne sont pas des cas de bord : plusieurs de ces prompts
   s'intitulent « Se replacer au centre » ou « service et replacement » — le
   replacement y est l'objectif même de la séance.

   GATE : uniquement si le texte le demande, avec le MÊME déclencheur que la
   règle `replacement` de src/fidelity.js (juge et moteur doivent dire la même
   chose). Sinon on alourdirait tous les échanges de filet.

   BASE (précision de l'enseignant, 08/08) : le BADMINTON a une base unique, le
   centre de son demi-court ; le TENNIS en a DEUX — derrière sa ligne de fond
   quand on joue du fond, à ~2 m du filet quand on y monte — et l'on choisit
   celle qui correspond à la profondeur où le joueur vient de frapper ; au
   TENNIS DE TABLE le joueur reste À L'EXTÉRIEUR de la table. Toujours sur
   l'axe (y=50), toujours dans son camp. */
function replacementBase(sport, side, playedAtNet) {
  const gauche = side === "left";
  if (sport === "tennis") return { x: playedAtNet ? (gauche ? 43 : 57) : (gauche ? 8 : 92), y: 50 };
  if (sport === "tennis_de_table") return { x: gauche ? 16 : 84, y: 50 };
  if (sport === "volleyball") return { x: gauche ? 30 : 70, y: 50 };
  return { x: gauche ? 27 : 73, y: 50 };            // badminton et défaut
}
/* CHANTIER REPLI (👎 PROD103, « il devrait y avoir un petit déplacement entre
   chaque frappe ») — le moteur local faisait la FRAPPE FANTÔME que la garde
   reproche à l'IA : le frappeur restait planté pendant que les retombées
   adverses tombaient à 25-30 unités de lui. Quand le texte demande le
   déplacement/replacement, une COURSE vers la retombée s'intercale avant
   chaque frappe lointaine (>12 unités). Au tennis de table, la course reste
   AUTOUR de la table (x borné hors du plateau — même contrainte que
   enforceTableExterior) : seul l'alignement LATÉRAL est joué, c'est le
   « petit déplacement » du prof. */
export function applyStrikeApproach(scene, sport, text) {
  if (!SPORTS_FILET.has(sport)) return;
  const t = String(text || "");
  if (!/replac|d[ée]placement|se d[ée]place|revenir au centre/i.test(t)) return;
  if (/sur place|sans (?:se )?d[ée]plac|sans bouger|statique|immobile|pieds fixes/i.test(t)) return;
  const actions = Array.isArray(scene.actions) ? scene.actions : [];
  const byId = {}; (scene.players || []).forEach((p) => { byId[p.id] = p; });
  const pos = {}; (scene.players || []).forEach((p) => { pos[p.id] = { x: +p.x, y: +p.y }; });
  const chrono = actions.filter((a) => a.order != null).slice().sort((x, y) => x.order - y.order);
  const strikes = chrono.filter((a) => a.type === "renvoi" || a.type === "tir");
  if (strikes.length < 3) return;                      // pas un rallye
  // Limites du plateau de TT (miroir des tracés) : la course d'approche reste
  // à l'EXTÉRIEUR — un déplacement qui finit sur la table est un défaut grave.
  const TT = sport === "tennis_de_table";
  const ajouts = [];
  let landing = null;                                  // dernière retombée en jeu
  for (const a of chrono) {
    if ((a.type === "course" || a.type === "dribble") && a.from && Number.isFinite(+a.tox))
      pos[a.from] = { x: +a.tox, y: +a.toy };
    if (a.type !== "renvoi" && a.type !== "tir") continue;
    const p = byId[a.from];
    if (p && !/observ|arbitre|enseign/i.test(p.role || "") && landing && pos[a.from]) {
      const ici = pos[a.from];
      // La retombée est-elle dans SON camp, et loin de lui ?
      const sonCamp = (+p.x < 50) === (landing.x < 50);
      const dejaBouge = chrono.some((c) => (c.type === "course" || c.type === "dribble")
        && c.from === a.from && c.order > landing.order && c.order < a.order);
      if (sonCamp && !dejaBouge && Math.hypot(landing.x - ici.x, landing.y - ici.y) > 12) {
        const cible = TT
          ? { x: +p.x < 50 ? Math.min(ici.x + 6, 18) : Math.max(ici.x - 6, 82), y: landing.y }
          : { x: landing.x, y: landing.y };
        if (Math.hypot(cible.x - ici.x, cible.y - ici.y) >= 3) {
          ajouts.push({ from: a.from, tox: cible.x, toy: cible.y, type: "course", style: "dashed",
            order: a.order - 0.5, label: TT ? "Petit déplacement" : "Déplacement sur la balle" });
          pos[a.from] = cible;
        }
      }
    }
    if (Number.isFinite(+a.tox)) landing = { x: +a.tox, y: +a.toy, order: a.order };
  }
  if (ajouts.length) scene.actions = actions.concat(ajouts);
}

export function applyReplacement(scene, sport, text) {
  if (!SPORTS_FILET.has(sport)) return;               // appelé depuis finish(), tous sports confondus
  const asks = /replac|revenir (?:vers|au|à) (?:le |la )?(?:centre|milieu)|retour au (?:centre|milieu)|revien(?:s|t|nent) (?:vers |au |à )?(?:le |la )?(?:centre|milieu)|y revenir|revenir (?:au|sur|à) (?:son |leur |le |la )?(?:plot|rep[èe]re|marque|base)/i.test(String(text || ""));
  if (!asks) return;
  const still = /sur place|sans (?:se )?d[ée]plac|sans bouger|statique|immobile|pieds fixes/i.test(String(text || ""));
  if (still) return;
  const actions = Array.isArray(scene.actions) ? scene.actions : [];
  // Déjà présent ENTRE les frappes (le moteur ou un gabarit l'a dessiné) : on
  // ne double pas. Un replacement TERMINAL seul (après la dernière frappe) ne
  // désamorce plus l'overlay — c'est lui qui privait PROD103 de ses
  // replacements intercalaires (chantier repli).
  {
    const ordres = actions.filter((a) => a.order != null && a.type !== "course").map((a) => a.order);
    const lastStrike = ordres.length ? Math.max(...ordres) : Infinity;
    if (actions.some((a) => a.type === "course" && a.order != null && a.order < lastStrike
      && /replac|retour au centre|revient au centre/i.test(a.label || ""))) return;
  }
  const byId = {}; (scene.players || []).forEach((p) => { byId[p.id] = p; });
  // POSITIONS ÉVOLUÉES — un replacement se juge depuis l'endroit où le joueur
  // se trouve VRAIMENT à cet instant, pas depuis sa position de départ (même
  // convention que le juge `replacement` de fidelity.js). Sans cela on ajoute
  // des flèches à des joueurs déjà rentrés chez eux (constaté au rendu).
  const pos = {}; (scene.players || []).forEach((p) => { pos[p.id] = { x: +p.x, y: +p.y }; });
  const chrono = actions.filter((a) => a.order != null).slice().sort((x, y) => x.order - y.order);
  if (chrono.filter((a) => a.type !== "course").length < 2) return;   // pas un échange
  // On replace APRÈS chaque frappe sauf la dernière (le point est fini). Le
  // replacement s'intercale : order fractionnaire, l'ordre de lecture est
  // conservé sans renuméroter les actions existantes (l'éditeur et la
  // projection lisent cet ordre tel quel).
  const derniereFrappe = chrono.filter((a) => a.type !== "course").slice(-1)[0];
  const ajouts = [];
  for (const a of chrono) {
    const p = byId[a.from];
    // SEULE une COURSE déplace le joueur. Une frappe vise la RETOMBÉE du
    // volant : la compter comme un déplacement faisait « voyager » le frappeur
    // jusqu'à sa propre cible, et l'on ajoutait alors un replacement de 1 unité
    // à un joueur qui n'avait pas bougé (constaté au rendu sur PROD50).
    if (p && (a.type === "course" || a.type === "dribble") && Number.isFinite(+a.tox))
      pos[a.from] = { x: +a.tox, y: +a.toy };
    if (a.type === "course" || a === derniereFrappe) continue;
    if (!p || !pos[a.from] || !Number.isFinite(pos[a.from].x)) continue;
    if (/observ|arbitre|enseign/i.test(p.role || "")) continue;   // le distributeur ne se replace pas
    const ici = pos[a.from];
    const side = +p.x < 50 ? "left" : "right";     // le CAMP se lit sur le poste de départ
    const base = replacementBase(sport, side, Math.abs(ici.x - 50) < 20);
    // SEUIL — un joueur DÉJÀ à sa base n'a rien à faire : lui ajouter une
    // flèche de 2 unités, c'est le défaut « flèche qui ne va nulle part » que
    // le dépôt combat par ailleurs, et ça n'apprend rien à l'élève.
    if (Math.hypot(base.x - ici.x, base.y - ici.y) < 6) continue;
    ajouts.push({ from: a.from, tox: base.x, toy: base.y, type: "course", style: "dashed",
      order: a.order + 0.5, label: "Replacement" });
    pos[a.from] = { x: base.x, y: base.y };
  }
  /* RIEN À INTERCALER, ET POURTANT LE TEXTE LE DEMANDE — le cas que la mesure
     a fini par désigner (9 signalements `replacement` restants sur la route
     template, tous ici). Deux règles se croisaient :
       • le correcteur SAUTE la dernière frappe (« le point est fini, une flèche
         de plus n'apprend rien ») ;
       • dans un échange court, le seul joueur qui a QUITTÉ sa base est
         justement celui qui frappe en dernier (A frappe depuis sa base, B court
         sur le volant puis frappe).
     Résultat : le correcteur tournait, sa boucle ne produisait rien, et le juge
     réclamait toujours. Le juge, lui, accepte un replacement TERMINAL — il
     demande qu'il y en ait un, pas qu'il soit intercalé.
     On n'ajoute donc le retour de la dernière frappe QUE si rien d'autre n'a
     pu l'être : l'échange long garde ses replacements intercalaires, l'échange
     court en obtient au moins un. Un retour dégénéré (joueur déjà à sa base)
     est de toute façon balayé par `dropStillMoves`, juste après. */
  if (!ajouts.length && derniereFrappe && byId[derniereFrappe.from]) {
    const p = byId[derniereFrappe.from];
    if (!/observ|arbitre|enseign/i.test(p.role || "") && pos[derniereFrappe.from]) {
      const ici = pos[derniereFrappe.from];
      const side = +p.x < 50 ? "left" : "right";
      const base = replacementBase(sport, side, Math.abs(ici.x - 50) < 20);
      if (Math.hypot(base.x - ici.x, base.y - ici.y) >= 6)
        ajouts.push({ from: derniereFrappe.from, tox: base.x, toy: base.y, type: "course",
          style: "dashed", order: derniereFrappe.order + 0.5, label: "Replacement" });
    }
  }
  if (!ajouts.length) return;
  scene.actions = actions.concat(ajouts);

}


/* Renumérotation en ENTIERS CONSÉCUTIFS, en préservant les ÉGALITÉS (deux
   actions simultanées partagent volontairement le même ordre — convention du
   SCENE_PROMPT). Ne s'exécute que si la numérotation en a besoin : une scène
   déjà propre est laissée au bit près. */
export function renumberOrders(scene) {
  const num = (scene.actions || []).filter((a) => a.order != null).sort((x, y) => x.order - y.order);
  if (!num.length) return;
  const propre = num.every((a, i) => Number.isInteger(a.order)
    && (i === 0 ? a.order === 1 : a.order === num[i - 1].order || a.order === num[i - 1].order + 1));
  if (propre) return;
  let rang = 0, precedent = null;
  for (const a of num) {
    if (precedent === null || a.order !== precedent) rang++;
    precedent = a.order;
    a.order = rang;
  }
}

/* ─────────────────────────── FAMILLE FILET ─────────────────────────────── */
/* Coup autorisé -> [libellé affiché, type EPS de la flèche]. */
const COUPS = {
  service:    ["Service", "renvoi"],
  remise:     ["Remise", "renvoi"],
  degagement: ["Dégagement", "renvoi"],
  amorti:     ["Amorti", "renvoi"],
  bloc:       ["Bloc", "renvoi"],
  poussette:  ["Poussette", "renvoi"],
  manchette:  ["Manchette", "renvoi"],
  echange:    ["Échange", "renvoi"],
  reception:  ["Réception", "renvoi"],
  passe:      ["Passe", "renvoi"],
  // Renvois GÉNÉRIQUES que l'IA emploie souvent (« renvoie », « relance ») : on les
  // accepte comme un renvoi neutre plutôt que de rejeter toute l'analyse.
  renvoi:     ["Renvoi", "renvoi"],
  retour:     ["Retour", "renvoi"],
  relance:    ["Relance", "renvoi"],
  lob:        ["Lob", "renvoi"],
  volee:      ["Volée", "tir"],
  smash:      ["Smash", "tir"],
  kill:       ["Kill", "tir"],
  topspin:    ["Top-spin", "tir"],
  attaque:    ["Attaque", "tir"],
};
// A..F : jusqu'à 6 joueurs par camp (volley à 6). Le moteur « équipes » anime les
// rôles A (service) / C (réception) / D (passe) / C (attaque) et place les autres
// (B, E, F…) en soutien — un acteur au-delà ne doit donc pas faire échouer l'analyse.
const ACTEURS = new Set(["A", "B", "C", "D", "E", "F", "adversaire"]);
/* Profondeur / direction -> vocabulaire CANONIQUE consommé par les moteurs de
   placement (mêmes mots-clés que le texte libre, mais contrôlés par nous —
   « long fond de table » couvre les regex badminton/tennis ET tennis de table). */
const PROFONDEUR = { fond: "long fond de table", median: "médian", avant: "court" };
const DIRECTION = { gauche: "gauche", droite: "droite", centre: "au centre", croise: "croisé", croisé: "croisé", diagonale: "croisé", diagonal: "croisé", parallele: "parallèle", "parallèle": "parallèle", longueligne: "parallèle", ligne: "parallèle" };
const SPORTS_FILET = new Set(["badminton", "tennis", "volleyball", "tennis_de_table"]);

/* « jeu en équipe SANS effectif chiffré » : le texte évoque deux équipes (« en
   équipe », « chaque équipe », « de chaque côté »…) mais ne précise AUCUN nombre
   de joueurs (« 4 contre 4 », « à 6 », « 3 joueurs »…). Sert à rééquilibrer un
   effectif volley asymétrique inventé par l'IA sur un prompt vague. */
function teamPlayNoCount(text) {
  const t = String(text || "").toLowerCase();
  const teamPlay = /en\s[ée]quipes?\b|par\s[ée]quipes?\b|chaque\s[ée]quipe|de chaque c[ôo]t[ée]/.test(t);
  if (!teamPlay) return false;
  const hasCount = /\d+\s*(?:contre|c\b|x|×|vs?\b|joueurs?|[ée]l[èe]ves?|par\s[ée]quipe)/.test(t)
    || /\b(?:un|deux|trois|quatre|cinq|six)\s+contre\b/.test(t)
    || /(?:^|[\s(])[àa]\s*\d+/.test(t);   // « à 6 » — pas de \b : « à » n'est pas un caractère \w
  return !hasCount;
}

function filetScene(analyse, sport, text) {
  if (!SPORTS_FILET.has(sport)) return null;
  let format = ["simple", "double", "equipes"].includes(analyse.format) ? analyse.format : null;
  if (!format) return null;
  // « equipes » à 1 CONTRE 1 : c'est un SIMPLE (l'IA étiquette parfois un
  // échange coopératif à deux en « equipes ») — rétrogradé plutôt que rejeté.
  {
    const e = analyse.effectif || {};
    if (format === "equipes" && parseInt(e.gauche, 10) === 1 && parseInt(e.droite, 10) === 1) format = "simple";
  }
  // DOUBLE EXPLICITE (« en double », « double mixte ») : l'IA étiquette parfois
  // un double « equipes » (2v2, 2v1) ou même « simple » — le texte fait foi
  // pour les sports de raquette (le double mixte devenait un drill 1 contre 2).
  // Même garde-fou « compte double » que la règle effectif de fidelity.js : un
  // point qui « compte double » ne fait pas de l'exercice un DOUBLE.
  const texteSansScore = String(text || "").replace(/(?:compte(?:nt)?|vaut|valent|rapporte(?:nt)?|marque(?:nt)?)\s+(?:un\s+point\s+)?doubles?|points?\s+doubles?|score\s+double|double\s+point/gi, " ");
  if (/\bdoubles?\b|\bmixte\b/i.test(texteSansScore) && sport !== "volleyball") {
    const e = analyse.effectif || {};
    const mx = Math.max(parseInt(e.gauche, 10) || 0, parseInt(e.droite, 10) || 0);
    if (format === "simple" || (format === "equipes" && mx <= 2)) format = "double";
  }
  // Double volley : moteur non adapté -> repli pipeline. (Le double TT a
  // désormais son moteur dédié à ALTERNANCE obligatoire, cf. buildTTDouble.)
  if (format === "double" && sport === "volleyball") return null;
  // Double VAGUE (« travail du double ») : un seul coup décrit, voire aucun —
  // échange par défaut où les DEUX partenaires touchent, plutôt qu'un rejet
  // (le repli local rendait un rally 1c1 dégénéré).
  if (format === "double" && (!Array.isArray(analyse.coups) || analyse.coups.length < 2)) {
    analyse = { ...analyse, coups: [
      { acteur: "A", coup: "service" }, { acteur: "adversaire", coup: "remise" }, { acteur: "B", coup: "renvoi" },
    ] };
  }
  // « equipes » (réception-passe-attaque) : volley uniquement. Jusqu'à 6 par côté
  // (système complet à 6 : réception en W, passeur en pénétration) — les joueurs
  // au-delà des acteurs sont placés en soutien par le moteur.
  // Badminton / tennis « equipes » = drill 1 CONTRE N (un joueur seul face à 2-3
  // distributeurs) : moteur dédié (le rallye 1c1 perdait un distributeur).
  let effectif = null, feederDrill = false;
  if (format === "equipes" && (sport === "badminton" || sport === "tennis")) {
    const e = analyse.effectif || {};
    const g = parseInt(e.gauche, 10), dr = parseInt(e.droite, 10);
    if (!(Math.min(g, dr) === 1 && Math.max(g, dr) >= 2 && Math.max(g, dr) <= 3)) return null;
    effectif = { gauche: g, droite: dr };
    feederDrill = true;
  } else if (format === "equipes") {
    if (sport !== "volleyball") return null;
    const e = analyse.effectif || {};
    let g = parseInt(e.gauche, 10), d = parseInt(e.droite, 10);
    // EFFECTIF CHIFFRÉ PAR LE TEXTE (« 6 contre 6 », « 4c4 ») : il PRIME sur
    // l'effectif déclaré par l'IA — elle met parfois toute l'équipe d'un côté
    // et le seul serveur en face (un « 6 contre 6 » rendu 1 contre 6).
    {
      const W6 = { un: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
      const cm = String(text || "").toLowerCase().replace(/(\d)\s*(?:vs|[cv])\s*(\d)/g, "$1 contre $2")
        .match(/\b(\d|un|deux|trois|quatre|cinq|six)\s+contre\s+(\d|un|deux|trois|quatre|cinq|six)\b/);
      if (cm) {
        const n1 = W6[cm[1]] || parseInt(cm[1], 10), n2 = W6[cm[2]] || parseInt(cm[2], 10);
        if (n1 >= 1 && n1 <= 6 && n2 >= 1 && n2 <= 6
          && !(Math.min(g, d) === Math.min(n1, n2) && Math.max(g, d) === Math.max(n1, n2))) {
          g = n1; d = n2;
        }
      }
    }
    if (!(g >= 1 && g <= 6) || !(d >= 1 && d <= 6) || g + d < 3) return null;
    // SYMÉTRIE « en équipe » : sur un prompt vague, l'IA met parfois le seul SERVEUR
    // d'un côté et toute l'équipe de l'autre (1 contre 6). Si le texte parle de JEU
    // EN ÉQUIPE et ne CHIFFRE PAS l'effectif, on rééquilibre les deux camps sur la
    // plus grande équipe (= l'effectif réel déduit). Les drills à distributeur, eux,
    // CHIFFRENT leur asymétrie (« 1 contre 2 ») et restent donc intacts.
    if (g !== d && teamPlayNoCount(text)) { g = d = Math.max(g, d); }
    effectif = { gauche: g, droite: d };
  }
  // Un SEUL coup est admis s'il est COMPRESSÉ (« 10 échanges » = echange
  // repetitions:10) : l'expansion redonne ≥ 2 frappes alternées — rejeter
  // l'analyse renvoyait ces textes au rally local, qui inventait des frappes.
  if (!Array.isArray(analyse.coups) || analyse.coups.length < 1 || analyse.coups.length > 20) return null;

  let shots = [];
  for (const c of analyse.coups) {
    const def = COUPS[String(c?.coup || "").toLowerCase()];
    if (!def) return null;
    let who = c.acteur == null ? null : String(c.acteur);
    if (who && !ACTEURS.has(who)) return null;
    if (format === "simple" && /^[C-F]$/.test(who || "")) return null;
    // « adversaire » : l'équipe d'en face s'en charge (résolu par le moteur
    // en double, distributeur dans le drill 1cN) ; en simple comme en équipes
    // volley, l'enchaînement naturel suffit.
    if (who === "adversaire") who = format === "double" ? "OPP" : feederDrill ? "F" : null;
    // Drill à distributeurs : tout acteur non-A est un distributeur (B, C…).
    if (feederDrill && who && who !== "A") who = "F";
    const segParts = [];
    // Direction/profondeur inconnues : on IGNORE le qualificatif (décoration) plutôt
    // que de rejeter TOUTE l'analyse — sinon un « diagonale » non prévu faisait tomber
    // un échange détaillé sur un rally générique.
    if (c.direction != null) { const d = DIRECTION[String(c.direction).toLowerCase()]; if (d) segParts.push(d); }
    // PROFONDEUR CANONIQUE DU COUP : certains coups portent une profondeur
    // INTRINSÈQUE — un dégagement/lob va TOUJOURS au fond, un amorti/contre
    // TOUJOURS au filet. Elle prime sur une profondeur contradictoire de l'IA
    // (« dégagement → filet » est un oxymore vu chez DeepSeek).
    const coupKey = String(c?.coup || "").toLowerCase();
    const CANONICAL_DEPTH = { degagement: "fond", lob: "fond", amorti: "avant", contre: "avant" };
    const prof = CANONICAL_DEPTH[coupKey] ?? c.profondeur;
    if (prof != null) { const p = PROFONDEUR[String(prof).toLowerCase()]; if (p) segParts.push(p); }
    const seg = segParts.join(" ");
    const reps = Math.max(1, Math.min(10, parseInt(c.repetitions, 10) || 1));
    for (let r = 0; r < reps; r++) shots.push({ type: def[1], name: def[0], seg, who });
  }
  if (shots.length < 2) return null;
  // COMPRESSION DES CYCLES — l'IA déplie parfois « 10 échanges alternés » en 20
  // coups énumérés (dégagement B / amorti A ×10) au lieu d'utiliser
  // "repetitions". Au-delà de 3 répétitions du même motif (période 1 ou 2),
  // les flèches se superposent sans rien raconter de plus : on garde 3 cycles
  // (le motif pédagogique est l'ALTERNANCE, pas le compte — le déroulé complet
  // reste dans la pédagogie).
  // Scène destinée à être DUPLIQUÉE en bandes (« 2 groupes en parallèle »,
  // « sur 3 tables », montante-descendante) : on garde 2 cycles au lieu de 3 —
  // chaque bande est étroite, et le total (cycles × bandes) devient illisible.
  const duplicated = parallelGroupsCount(text) >= 2
    || /(\d+|deux|trois|quatre)\s+(?:terrains?|tables?)/i.test(String(text || ""))
    || /montante.?descendante/i.test(String(text || ""));
  const keepCycles = duplicated ? 2 : 3;
  for (const p of [2, 1]) {
    if (shots.length < 4 * p) continue;
    const key = (s) => `${s.who}|${s.name}|${s.seg}`;
    const out = [];
    let i = 0;
    while (i < shots.length) {
      let j = i + p;
      while (j < shots.length && key(shots[j]) === key(shots[j - p])) j++;
      const cycles = Math.floor((j - i) / p);
      if (cycles >= 4) { out.push(...shots.slice(i, i + keepCycles * p)); i += cycles * p; }
      else { out.push(shots[i]); i++; }
    }
    shots = out;
  }

  if (feederDrill) return buildRacketFeederDrill(text || "", sport, shots, effectif);
  // DOUBLE TENNIS DE TABLE : l'alternance des frappeurs est OBLIGATOIRE
  // (A, adversaire, B, adversaire) et chacun PIVOTE pour laisser la place —
  // moteur dédié (le moteur double badminton/tennis ne connaît pas la table).
  if (format === "double" && sport === "tennis_de_table") return buildTTDouble(text || "", shots);
  const scene = racketFromAnalysis(text || "", sport, shots, format, effectif);
  if (scene && format === "double") normalizeDoubleFormation(scene, text, sport);
  return scene;
}

/* Normalisation FORMATIONS DE DOUBLE (badminton/tennis) — le placement par
   défaut (serveur fond + partenaire fond opposé) ignore les formations
   décrites : australienne, partenaire au filet, montée service-volée, tandem
   avant-arrière. On repositionne et on dessine les bascules. */
function normalizeDoubleFormation(scene, text, sport) {
  const t = String(text || "");
  const players = scene.players || [];
  const A = players.find((p) => p.id === "A"), B = players.find((p) => p.id === "B");
  if (!A || !B) return scene;
  const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const serve = acts.find((a) => /service/i.test(a.label || "")) || acts.find((a) => a.type !== "course");
  const server = serve && serve.from === "B" ? B : A;
  const partner = server === B ? A : B;
  const ord = (serve && serve.order) ?? 1;
  // AUSTRALIENNE (tennis) : le partenaire se place au filet du MÊME côté que
  // le serveur, puis BASCULE de l'autre côté juste après le service.
  if (/australienne/i.test(t)) {
    partner.x = 40; partner.y = server.y;
    scene.actions.push({ from: partner.id, tox: 40, toy: server.y >= 50 ? 28 : 72, type: "course", style: "dashed", order: ord + 0.5, label: "Bascule après le service" });
    return scene;
  }
  // Formation CLASSIQUE : le partenaire est posté AU FILET (côté opposé).
  if (/partenaire au filet|au filet c[ôo]t[ée] oppos|un joueur au filet|joueuse au filet/i.test(t)) partner.x = 40;
  // Le serveur MONTE AU FILET après son service (service-volée, tandem).
  if (/mont(?:e|[ée]e?)\s+(?:au|vers le)\s+filet|service[- ]vol[ée]e|monte [àa] son tour/i.test(t)
    && !acts.some((a) => a.type === "course" && a.from === server.id && a.tox > 32)) {
    scene.actions.push({ from: server.id, tox: 38, toy: server.y, type: "course", style: "dashed", order: ord + 0.6, label: "Monte au filet" });
  }
  // BADMINTON : formation AVANT-ARRIÈRE (tandem) en attaque. Le RÔLE de chacun
  // est lu DANS LE TEXTE (il varie) :
  //  - « le partenaire couvre LE FILET » (l'attaquant smashe DU FOND et y
  //    reste) -> le partenaire monte couvrir le filet ;
  //  - « le partenaire couvre LE FOND » / « le serveur monte au filet »
  //    (défaut) -> le serveur passe devant, le partenaire couvre le fond.
  if (sport === "badminton" && /avant[- ]arri[èe]re|tandem|couvre le (?:filet|fond)|\brotation\b/i.test(t)) {
    const maxOrd = Math.max(0, ...acts.map((a) => a.order ?? 0));
    const partnerCoversNet = /partenaire[^.]{0,40}(?:couvre|reste au|monte au|au)\s+filet|couvre le filet/i.test(t)
      && !/couvre le fond|serveur[^.]{0,30}monte au filet/i.test(t);
    if (partnerCoversNet) {
      // L'ATTAQUANT (celui qui smashe, sinon le serveur) reste AU FOND ; son
      // partenaire monte couvrir LE FILET.
      const smash = acts.find((a) => a.type === "tir" || /smash/i.test(a.label || ""));
      const attacker = (smash && players.find((p) => p.id === smash.from)) || server;
      const cover = attacker === A ? B : A;
      scene.actions.push({ from: cover.id, tox: 40, toy: cover.y, type: "course", style: "dashed", order: maxOrd + 1, label: "Couvre le filet (tandem)" });
    } else {
      if (!(scene.actions || []).some((a) => a.type === "course" && a.from === server.id && a.tox > 32))
        scene.actions.push({ from: server.id, tox: 40, toy: 50, type: "course", style: "dashed", order: maxOrd + 1, label: "Passe devant (tandem)" });
      scene.actions.push({ from: partner.id, tox: 12, toy: 50, type: "course", style: "dashed", order: maxOrd + 2, label: "Couvre le fond dans l'axe" });
    }
  }
  return scene;
}

/* ────────────────────────── FAMILLE INVASION ───────────────────────────── */
const SPORTS_INVASION = new Set(["football", "futsal", "handball", "basketball", "basket3x3", "rugby", "ultimate"]);
const ACTIONS_INVASION = new Set(["passe", "course", "dribble", "tir", "centre", "ecran"]);

/* Enums exposés au panneau « Ce que j'ai compris » (mêmes valeurs que la
   validation : ce que le panneau propose est exactement ce qui est accepté). */
export const ANALYSE_ENUMS = {
  coups: Object.keys(COUPS),
  acteursFilet: [...ACTEURS],
  profondeurs: Object.keys(PROFONDEUR),
  directions: Object.keys(DIRECTION),
  actionsInvasion: [...ACTIONS_INVASION],
  departs: ["camp_propre", "ligne_mediane", "camp_adverse"],
};

function invasionScene(analyse, sport, text) {
  if (!SPORTS_INVASION.has(sport)) return null;
  // Une FORMATION complète (4-4-2…) reste l'affaire du pipeline actuel.
  if (analyse.formation) return null;
  const eff = analyse.effectif || {};
  let nA = parseInt(eff.attaquants, 10), nD = parseInt(eff.defenseurs, 10);
  // RUGBY À XV COMPLET : « 15 contre 15 » dépasse le plafond de rendu (l'analyse
  // était rejetée, le repli rendait 2 joueurs). On ne dessine que la LIGNE
  // concernée : les acteurs nommés par leur NUMÉRO (« le 9 » -> A9, « le 15 »
  // -> A15) sont remappés en tête (A1..An, phase et acteurs compris), effectif
  // ramené à 8 max par camp.
  if (sport === "rugby" && (nA > 11 || nD > 11)) {
    const refd = [...new Set([
      ...(Array.isArray(analyse.phase) ? analyse.phase.flatMap((s) => [s && s.acteur, s && s.cible]) : []),
      ...(Array.isArray(analyse.acteurs) ? analyse.acteurs.map((a) => a && a.id) : []),
    ].filter((id) => /^A\d+$/i.test(String(id || ""))))].sort((a, b) => +String(a).slice(1) - +String(b).slice(1));
    const map = {}; refd.forEach((id, i) => { map[String(id).toUpperCase()] = "A" + (i + 1); });
    const re = (v) => map[String(v || "").toUpperCase()] || v;
    analyse = { ...analyse,
      phase: (Array.isArray(analyse.phase) ? analyse.phase : []).map((s) => ({ ...s, acteur: re(s && s.acteur), cible: re(s && s.cible) })),
      acteurs: (Array.isArray(analyse.acteurs) ? analyse.acteurs : []).map((a) => ({ ...a, id: re(a && a.id) })),
    };
    nA = Math.min(8, Math.max(refd.length + 2, 4));
    nD = Math.min(nA, nD);   // ligne défensive EN FACE, même largeur que l'attaque
  }
  if (!(nA >= 1 && nA <= 11) || !(nD >= 0 && nD <= 11)) return null;
  // LETTRES NUES (« A », « B » — échos d'un prompt « le joueur A/B ») : l'IA
  // nomme parfois les acteurs par leur seule lettre au lieu de A1/A2. On mappe
  // A->A1, B->A2, … (G exclu : c'est le gardien) au lieu de rejeter TOUTE
  // l'analyse (le repli local rendait un exercice hors sujet).
  // ÉQUIPES PAR COULEUR : « B1/B2 » (bleu 1/2) = l'équipe qui ATTAQUE (A),
  // « R1 » (rouge 1) = défenseur (D). « B# » n'est lu « bleu » que si AUCUN
  // A# n'existe déjà — sinon B# désigne un 2e GROUPE (vagues…) hors schéma,
  // qu'on laisse invalide (repli gabarit) plutôt que de deviner.
  const rawToks = [];
  if (Array.isArray(analyse.phase)) for (const st of analyse.phase) for (const t of [st && st.acteur, st && st.cible]) { if (t != null) rawToks.push(String(t).trim()); }
  if (Array.isArray(analyse.acteurs)) for (const a of analyse.acteurs) { if (a && a.id != null) rawToks.push(String(a.id).trim()); }
  const hasANum = rawToks.some((t) => /^A\d+$/i.test(t));
  // B#/R# qui CIBLE un A# = ADVERSAIRE qui alimente/défend l'équipe A (pull
  // d'ultimate, passe d'un distributeur, marquage) -> mappé DÉFENSEUR même si
  // des A# existent (sinon rejet total de l'analyse). Le cas « vagues » (B1/B2
  // 2e groupe avec leur PROPRE séquence course/tir) n'a pas de B#->A# et reste
  // donc invalide (repli gabarit), comportement préservé.
  const feederOpp = new Set();
  if (Array.isArray(analyse.phase)) for (const st of analyse.phase) {
    if (st && /^[BR]\d+$/i.test(String(st.acteur || "")) && /^A\d+$/i.test(String(st.cible || "")))
      feederOpp.add(String(st.acteur).toUpperCase());
  }
  // « P » = le PIVOT (l'IA le nomme par son initiale de poste) : mappé sur le
  // premier attaquant libre — sinon toute l'analyse était rejetée et le repli
  // rendait un exercice hors sujet.
  let pMap = null;
  if (rawToks.some((t) => /^P$/i.test(t))) {
    const usedA = new Set(rawToks.map((t) => (/^A(\d{1,2})$/i.exec(t) || [])[1]).filter(Boolean).map(Number));
    let k = 1; while (usedA.has(k)) k++;
    pMap = "A" + k;
  }
  const mapTok = (v) => {
    const s = v == null ? null : String(v).trim();
    if (!s) return v;
    if (pMap && /^P$/i.test(s)) return pMap;
    // ULTIMATE : « H/H1 » = handler (A1), « C1/C2… » = cutters (A2, A3…) —
    // le vocabulaire que l'IA emploie spontanément pour ce sport.
    if (sport === "ultimate" && !hasANum) {
      let mu = /^H(\d{0,2})$/i.exec(s);
      if (mu) return "A" + (parseInt(mu[1], 10) > 0 ? parseInt(mu[1], 10) : 1);
      mu = /^C(\d{1,2})$/i.exec(s);
      if (mu) return "A" + (parseInt(mu[1], 10) + 1);
    }
    if (/^[A-FH-K]$/i.test(s)) { const c = s.toUpperCase().charCodeAt(0) - 64; return "A" + (c > 7 ? c - 1 : c); }
    let m = /^B(\d{1,2})$/i.exec(s);
    if (m && !hasANum) return "A" + parseInt(m[1], 10);
    if (m && feederOpp.has(s.toUpperCase())) return "D" + parseInt(m[1], 10);   // adversaire alimentant l'équipe A
    m = /^R(\d{1,2})$/i.exec(s);
    if (m) return "D" + parseInt(m[1], 10);
    return v;
  };
  const phaseIn = Array.isArray(analyse.phase)
    ? analyse.phase.map((st) => st && typeof st === "object" ? { ...st, acteur: mapTok(st.acteur), cible: mapTok(st.cible) } : st)
    : analyse.phase;
  // NUMÉROS DE MAILLOT : l'IA numérote parfois les acteurs par leur maillot (« le 5 »
  // -> A5) au-delà de l'effectif déclaré (attaquants:3). On ÉTEND alors l'effectif
  // pour couvrir l'indice MAX utilisé (les joueurs intermédiaires restent au repos),
  // borné à 11 — plutôt que de rejeter toute l'analyse (repli = scène tronquée).
  {
    const maxIdx = (letter) => {
      const re = new RegExp(`^${letter}(\\d+)$`);
      let mx = 0;
      for (const st of phaseIn || []) for (const tok of [st && st.acteur, st && st.cible]) {
        const m = tok != null && re.exec(String(tok)); if (m) mx = Math.max(mx, +m[1]);
      }
      return mx;
    };
    nA = Math.min(11, Math.max(nA, maxIdx("A")));
    nD = Math.min(11, Math.max(nD, maxIdx("D")));
  }
  // ATTAQUE PLACÉE « DE L'ÉQUIPE » face à un SYSTÈME complet (6-0, zone…) :
  // l'IA ne déclare parfois que les 2-3 acteurs de la combinaison décrite —
  // l'équipe qui attaque est pourtant complète. On remonte l'effectif
  // attaquant au niveau du système défensif (handball/basket : postes placés),
  // SAUF si le texte chiffre lui-même l'attaque (« 2 contre 6 »).
  if (["handball", "basketball", "basket3x3"].includes(sport)
    && String(analyse.type) === "attaque_placee" && nD >= 5 && nA >= 1 && nA <= 3) {
    const core = coreSituationText(text);
    const teamAttack = /attaque\s+plac[ée]e?\s+de\s+l['’][ée]quipe|l['’][ée]quipe\s+[\wà-ÿ]*\s*attaque|attaque\s+de\s+l['’][ée]quipe/i.test(core);
    const counted = /\b\d+\s*(?:contre|c\b|vs)\s*\d+/i.test(core) || /\b(?:un|deux|trois)\s+contre\b/i.test(core);
    if (teamAttack && !counted) nA = Math.min(sport === "handball" ? 6 : 5, nD);
  }
  const depart = analyse.depart == null ? null : String(analyse.depart);
  if (depart != null && !["camp_propre", "ligne_mediane", "camp_adverse"].includes(depart)) return null;
  // TYPE de phase d'invasion déclaré par l'IA (enum fermé ; valeur hors liste -> ignorée).
  const type = ["attaque_placee", "contre_attaque", "transition"].includes(String(analyse.type)) ? String(analyse.type) : null;
  if (!Array.isArray(phaseIn) || phaseIn.length < 1 || phaseIn.length > 12) return null;

  const validActor = (a) => {
    const m = /^A(\d+)$/.exec(a) || /^D(\d+)$/.exec(a);
    if (m) return a[0] === "A" ? +m[1] >= 1 && +m[1] <= nA : +m[1] >= 1 && +m[1] <= nD;
    return a === "G" || a === "gardien";
  };
  const phase = [];
  for (const st of phaseIn) {
    if (!st || !ACTIONS_INVASION.has(st.action)) return null;
    let acteur = String(st.acteur || "");
    if (acteur === "gardien") acteur = "G";
    if (!validActor(acteur)) return null;
    if (acteur === "G" && !eff.gardien) return null;
    let cible = st.cible == null ? null : String(st.cible).trim();
    // ROBUSTESSE : l'IA émet parfois une sentinelle TEXTUELLE (« null », « none »,
    // « aucun »…) au lieu d'omettre la cible — à traiter comme une absence de
    // cible, sinon toute l'analyse serait rejetée (repli pipeline = scène tronquée).
    if (cible === "" || /^(null|none|undefined|nil|aucun|aucune)$/i.test(cible)) cible = null;
    if (cible === "gardien") cible = "G";
    if (cible != null && cible !== "but" && !validActor(cible)) return null;
    const note = st.note == null ? null : String(st.note).slice(0, 60);
    phase.push({ acteur, action: st.action, cible, note });
  }
  // PLACEMENT STRUCTURÉ OPTIONNEL (analyse.acteurs) — « l'IA comprend, le code
  // dessine » appliqué au PLACEMENT : poste / côté / repère / distance au but /
  // marquage par joueur. Validation STRICTEMENT consultative : enums fermés,
  // un champ invalide est IGNORÉ champ par champ — jamais de rejet d'analyse,
  // et AUCUN effet quand le champ est absent (les heuristiques texte du moteur
  // restent le filet de secours).
  const acteurs = [];
  {
    const strip = (v) => String(v).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/[\s-]+/g, "_");
    const POSTES = new Set(["arriere_gauche", "arriere_droit", "demi_centre", "ailier_gauche", "ailier_droit", "pivot", "meneur", "interieur", "avant_centre"]);
    const COTES = { gauche: "gauche", droite: "droite", droit: "droite", axe: "centre", centre: "centre", milieu: "centre" };
    const REPERES = new Set(["6m", "9m", "surface", "rond_central", "ligne_3pts", "tete_raquette", "milieu_terrain"]);
    if (Array.isArray(analyse.acteurs)) for (const a of analyse.acteurs.slice(0, 24)) {
      if (!a || typeof a !== "object") continue;
      let id = mapTok(a.id);
      id = id == null ? "" : String(id).trim();
      if (id === "gardien") id = "G";
      if (!validActor(id)) continue;
      const out = { id };
      if (a.poste != null && POSTES.has(strip(a.poste))) out.poste = strip(a.poste);
      if (a.cote != null && COTES[strip(a.cote)]) out.cote = COTES[strip(a.cote)];
      if (a.repere != null) {
        const r = strip(a.repere).replace(/^ligne_(?:des_)?(\d)_?m(?:etres)?$/, "$1m");
        if (REPERES.has(r)) out.repere = r;
      }
      const dist = a.distance_but_m == null ? NaN : parseFloat(a.distance_but_m);
      if (Number.isFinite(dist) && dist >= 1 && dist <= 60) out.distance_but_m = dist;
      const marque = a.marque == null ? null : String(mapTok(a.marque)).trim();
      if (/^D\d+$/.test(id) && marque && /^A\d+$/.test(marque) && validActor(marque)) out.marque = marque;
      if (Object.keys(out).length > 1 && !acteurs.some((x) => x.id === id)) acteurs.push(out);
    }
  }
  // « dribble interdit / sans dribble » : aucune conduite balle au sol ne doit
  // être tracée (ni la « fixation » fabriquée, ni la course-conduite du porteur).
  // (lu sur la SITUATION NOMINALE : une variable « si trop facile, interdire le
  // dribble » ne doit pas priver la situation de départ de ses conduites)
  const noDribble = /dribble\s+interdit|interdit.{0,12}dribble|sans\s+dribble|pas\s+de\s+dribble|aucun\s+dribble/i.test(coreSituationText(text));
  // DÉCISION PRODUIT — ÉCRAN SANS ADVERSAIRE : un écran DÉCRIT (action ecran, ou
  // course/conduite dont la note dit « pose un écran/bloc ») a besoin d'un défenseur
  // à écranter pour être DESSINÉ comme un bloc. Si l'écran ne cible pas déjà un
  // défenseur existant, on AJOUTE un défenseur implicite (ou on cible le gardien
  // si l'écran est « sur le gardien ») et on requalifie l'action en « ecran ».
  // On n'intervient QUE s'il n'y a AUCUN défenseur (texte sans adversaires) : sinon
  // le moteur (withScreensFromNotes) déduit déjà le bon défenseur en marquage
  // individuel — il ne faut pas le court-circuiter.
  if (nD === 0) {
    const SCREEN_NOTE = /[ée]cran|\bbloc\b|\bpick\b/i;
    const isScreenStep = (st) => st.action === "ecran"
      || ((st.action === "course" || st.action === "dribble") && SCREEN_NOTE.test(st.note || ""));
    for (const st of phase) {
      if (!isScreenStep(st)) continue;
      // Écran « sur le gardien » -> on cible le gardien existant (pas un défenseur).
      if (/gardien|gardienne|portier/i.test(st.note || "") && eff.gardien) { st.cible = "G"; st.action = "ecran"; continue; }
      nD = Math.min(11, nD + 1);
      st.cible = "D" + nD;
      st.action = "ecran";
    }
  }
  // CONTRE-ATTAQUE / TRANSITION : le texte décrit un fast break (rebond, contre-
  // attaque, transition, montée de balle) — le rendu se stage sur tout le terrain
  // (rebondeur dans sa raquette, relais échelonnés, finisseur lancé), et non en
  // attaque placée. Réutilisé par le moteur pour choisir le bon staging.
  let transition = type === "contre_attaque" || type === "transition"
    || (type == null && /rebond|contre[\s-]?attaque|transition|fast[\s-]?break|mont[ée]e de balle/i.test(String(text || "")));
  // BASKET 3X3 : tout se joue sur UN panier (demi-terrain) — jamais de staging
  // pleine longueur (une « ressortie après rebond » 3x3 n'est pas une
  // contre-attaque qui traverse : elle recule DERRIÈRE l'arc puis attaque).
  const half3x3 = sport === "basket3x3";
  if (half3x3) transition = false;
  const scene = invasionFromAnalysis({ attaquants: nA, defenseurs: nD, gardien: !!eff.gardien,
    depart: half3x3 ? "camp_adverse" : depart, phase, noDribble, transition,
    type: half3x3 ? "attaque_placee" : type, text, acteurs }, sport);
  if (scene && half3x3) normalize3x3(scene, text);
  // HANDBALL 7 CONTRE 6 (gardien-joueur) : le gardien REMPLACÉ est celui de
  // l'équipe qui attaque — son but est vide, mais le but ATTAQUÉ garde son
  // gardien (le texte « but vide » supprimait le gardien adverse).
  if (scene && sport === "handball"
    && /gardien[^.]{0,50}remplac|gardien[^.]{0,30}sort pour jouer|septi[èe]me joueur de champ|7e? joueur de champ|gardien[- ]joueur/i.test(String(text || ""))) {
    const players = scene.players || (scene.players = []);
    if (!players.some((p) => p.team === "k") && (scene.actions || []).some((a) => a.type === "tir"))
      players.push({ id: "G", role: "Gardien", team: "k", color: "#F2C200", x: 94, y: 50 });
    (scene.markers = scene.markers || []).push({ type: "consigne", x: 8, y: 44, text: "But vide" });
  }
  // CORNER (football/futsal) : le set-piece part du DRAPEAU de corner du but
  // attaqué (à droite), pas du milieu de terrain. On repositionne le tireur au
  // corner et les réceptionneurs DANS la surface (le centre suit les positions).
  if (scene && /\bcorner\b|coup\s+de\s+pied\s+de\s+coin/i.test(String(text || "")) && ["football", "futsal"].includes(sport)) {
    const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    const takerId = acts[0]?.from || "A1";
    const taker = (scene.players || []).find((p) => p.id === takerId);
    // CÔTÉ du drapeau : la locution de CÔTÉ (« depuis le côté droit », « corner
    // gauche ») prime — « frappé du PIED gauche » n'est pas un côté de terrain.
    const sideM = String(text || "").match(/(?:c[ôo]t[ée]|depuis (?:la|le c[ôo]t[ée])|corner)\s+(droite?|gauche)/i);
    const yCorner = sideM ? (/gauche/i.test(sideM[1]) ? 6 : 94)
      : /\bgauche\b/i.test(String(text || "").replace(/pied\s+gauche/gi, "")) ? 6 : 94;
    if (taker) {
      taker.x = 97; taker.y = yCorner;
      const ball = (scene.markers || []).find((m) => m.type === "ballon");
      if (ball) { ball.x = 95; ball.y = yCorner; }
      // Réceptionneurs (autres attaquants) groupés dans la surface, devant le but.
      const recv = (scene.players || []).filter((p) => p.team === "A" && p.id !== takerId);
      recv.forEach((p, i) => { p.x = 86 + (i % 2) * 5; p.y = Math.max(32, Math.min(68, 40 + i * 12)); });
    }
  }
  // COUP FRANC (football/futsal/handball) : le set-piece se joue au POINT DE
  // FAUTE (distance/repère lus du texte — « à 20 m du but », « aux 9 mètres »),
  // pas depuis le rond central ; et un « mur de N » aligne N défenseurs COMPACTS
  // entre le ballon et le but, sans course de sortie. Cf. normalizeFreeKick.
  else if (scene && /coup\s+franc|jet\s+franc/i.test(String(text || "")) && ["football", "futsal", "handball"].includes(sport)) {
    normalizeFreeKick(scene, sport, text);
  }
  // ENGAGEMENT RAPIDE après but (handball, football/futsal) : l'engagement part
  // du POINT CENTRAL (pas du fond du camp propre), les partenaires sont déjà
  // lancés au-delà de la médiane et la défense COURT SE REPLIER vers son but.
  if (scene && /engagement\s+rapide|engage\w*\s+(?:rapidement\s+)?(?:depuis|au|du)\s+(?:le\s+)?(?:point\s+|rond\s+)?centr/i.test(String(text || ""))
    && ["handball", "football", "futsal"].includes(sport)) {
    normalizeQuickRestart(scene);
  }
  return scene;
}

/* Normalisation COUP FRANC — repositionne le tireur au point de faute et forme
   le mur décrit. Conventions rendu : but attaqué à droite (x≈97), ~1 unité par
   mètre au football (surface 16,5 m -> x=80), repères handball figés (9 m -> 72). */
const NUM_FR = { un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
function normalizeFreeKick(scene, sport, text) {
  const t = String(text || "");
  const cl = (v, lo, hi) => Math.round(Math.max(lo, Math.min(hi, v)));
  const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const first = acts.find((a) => ["passe", "centre", "tir", "dribble"].includes(a.type) && a.from);
  const taker = first && (scene.players || []).find((p) => p.id === first.from);
  if (!taker) return;
  // Point du coup franc : distance au but (la distance DU MUR — « mur à 9,15 m » —
  // est neutralisée d'abord), sinon les 9 m au handball, ~22 m au football.
  const t2 = t.replace(/mur[^.,;]{0,40}?\d+(?:[.,]\d+)?\s*m(?:[èe]tres)?/gi, "mur");
  const dm = t2.match(/(?:[àa]|aux?|des?)\s*(\d{1,2})\s*m(?:[èe]tres)?\b/i);
  const x = sport === "handball" ? 72 : dm ? cl(97 - parseInt(dm[1], 10), 58, 90) : 75;
  const y = /c[ôo]t[ée] droit/i.test(t) ? 70 : /c[ôo]t[ée] gauche/i.test(t) ? 30 : taker.y;
  taker.x = x; taker.y = y;
  const ball = (scene.markers || []).find((m) => m.type === "ballon");
  if (ball) { ball.x = x + 3; ball.y = y; }
  // Receveur d'une passe courte du tireur (remise en retrait d'un CF indirect)
  // resté à l'autre bout du terrain : ramené en retrait du point de faute.
  if (first.type === "passe" && first.to) {
    const r = (scene.players || []).find((p) => p.id === first.to);
    if (r && Math.hypot(r.x - x, r.y - y) > 20) { r.x = cl(x - 6, 50, 94); r.y = cl(y + (y > 50 ? -10 : 10), 8, 92); }
  }
  if (!/\bmur\b/i.test(t)) return;
  // LE MUR : N défenseurs alignés perpendiculairement à la ligne ballon-but, à
  // distance réglementaire (9,15 m football ; ligne des 6 m au handball).
  const wm = t.match(/murs?\s+(?:de\s+|d['’]\s*)?(\d+|deux|trois|quatre|cinq)/i);
  const defs = (scene.players || []).filter((p) => p.team === "B" && !/gardien/i.test(p.role || ""));
  const n = Math.min(defs.length, wm ? (NUM_FR[wm[1].toLowerCase()] || parseInt(wm[1], 10) || 3) : (sport === "handball" ? 3 : 4));
  if (n < 1) return;
  const dx = 97 - x, dy = 50 - y, L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L;                       // ballon -> centre du but
  const wd = sport === "handball" ? 8 : 10;             // 3 m hand / 9,15 m foot
  const wx = x + ux * wd, wy = y + uy * wd;
  const wall = defs.slice(0, n);
  wall.forEach((d, i) => {
    const off = (i - (n - 1) / 2) * 3.5;                // épaule contre épaule
    d.x = cl(wx - uy * off, 8, 94); d.y = cl(wy + ux * off, 6, 94);
    d.role = "Mur";
  });
  // Un mur ne « sort » pas en courant : ses courses synthétiques sont retirées.
  const ids = new Set(wall.map((d) => d.id));
  scene.actions = (scene.actions || []).filter((a) => !(a.type === "course" && ids.has(a.from) && /sortie d[ée]fensive/i.test(a.label || "")));
}

/* Normalisation BASKET 3X3 — conventions du demi-terrain à un panier :
   - « ressortir le ballon derrière l'arc » (règle 3x3 après rebond défensif) :
     le porteur part de la RAQUETTE et un dribble de ressortie recule derrière
     l'arc (x≈70) avant le début de la phase ;
   - un « écart derrière l'arc » ne finit jamais DEVANT l'arc (tox ≤ 68) ;
   - le « joker » offensif est étiqueté. */
function normalize3x3(scene, text) {
  const t = String(text || "");
  const players = scene.players || [];
  // RESSORTIE APRÈS REBOND (règle 3x3) — UNIQUEMENT sur un contexte de
  // RÉCUPÉRATION (rebond, interception, « un pied dehors ») OU un « derrière
  // l'arc » explicite. « ressort le ballon AU partenaire / pour un tir » est une
  // passe de décharge (kick-out), PAS la ressortie réglementaire — à ne pas
  // préfixer d'une sortie de balle fantôme.
  if (/(?:rebond|r[ée]cup[ée]r\w*|interception|un pied dehors)[^.]{0,50}(?:ressort|sortir le ballon|derri[èe]re l['’]arc)|(?:ressort\w*|sortir le ballon)[^.]{0,40}derri[èe]re l['’]arc|derri[èe]re l['’]arc[^.]{0,40}avant/i.test(t)) {
    const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    const first = acts.find((a) => ["passe", "dribble", "tir"].includes(a.type) && a.from);
    const carrier = first && players.find((p) => p.id === first.from);
    if (carrier) {
      carrier.x = 87; carrier.y = 44;
      const ball = (scene.markers || []).find((m) => m.type === "ballon");
      if (ball) { ball.x = 89; ball.y = 44; }
      scene.actions.push({ from: carrier.id, tox: 64, toy: 50, type: "dribble", order: 0, label: "Ressort le ballon derrière l'arc" });
    }
  }
  // Un « écart / écartement » en 3x3 = spacing DERRIÈRE l'arc (x≈70) par
  // définition : la course ne finit jamais devant l'arc.
  for (const a of (scene.actions || [])) {
    if (a.type === "course" && a.tox != null && a.tox > 68 && /derri[èe]re l['’]arc|[ée]cart/i.test(a.label || "")) a.tox = 68;
  }
  if (/\bjoker\b/i.test(t)) {
    const A = players.filter((p) => p.team === "A");
    if (A.length >= 3) A[A.length - 1].role = "Joker";
  }
  return scene;
}

/* Normalisation ENGAGEMENT RAPIDE — l'engageur au point central (50,50), les
   partenaires déjà lancés juste au-delà de la médiane, les défenseurs (équipe
   qui vient de marquer) en course de REPLI vers leur but — leurs « sorties
   défensives » (contresens : elles avancent) sont remplacées. */
function normalizeQuickRestart(scene) {
  const cl = (v, lo, hi) => Math.round(Math.max(lo, Math.min(hi, v)));
  const acts = (scene.actions || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const first = acts.find((a) => (a.type === "passe" || a.type === "centre") && a.from);
  const eng = first && (scene.players || []).find((p) => p.id === first.from);
  if (!eng) return;
  eng.x = 50; eng.y = 50;
  const ball = (scene.markers || []).find((m) => m.type === "ballon");
  if (ball) { ball.x = 53; ball.y = 50; }
  // Partenaires « déjà lancés » restés dans le camp propre : avancés juste
  // au-delà de la médiane (leurs courses d'appel vers le but restent).
  (scene.players || []).filter((p) => p.team === "A" && p.id !== eng.id && p.x < 45)
    .forEach((p, i) => { p.x = 54 + i * 4; });
  // Défense en repli : depuis la médiane, course vers son but.
  const defs = (scene.players || []).filter((p) => p.team === "B" && !/gardien/i.test(p.role || ""));
  if (!defs.length) return;
  const ids = new Set(defs.map((d) => d.id));
  scene.actions = (scene.actions || []).filter((a) => !(a.type === "course" && ids.has(a.from) && /sortie d[ée]fensive/i.test(a.label || "")));
  defs.forEach((d, i) => {
    d.x = cl(56 + i * 5, 40, 70);
    scene.actions.push({ from: d.id, tox: cl(80 + i * 3, 60, 92), toy: cl(50 + (d.y - 50) * 0.5, 12, 88),
      type: "course", style: "dashed", label: i === 0 ? "Repli défensif" : "" });
  });
}

/* ────────────────────────── FAMILLE PARCOURS ───────────────────────────── */
function parcoursScene(analyse, sport, text) {
  if (sport !== "gymnastique") return null;   // athlétisme : routage regex fiable
  if (!Array.isArray(analyse.elements) || analyse.elements.length < 1 || analyse.elements.length > 12) return null;
  const elements = [];
  for (const e of analyse.elements) {
    const nom = String(e?.nom || "").trim();
    if (!nom || nom.length > 40) return null;
    const direction = e.direction == null ? null : String(e.direction);
    if (direction != null && direction !== "gauche" && direction !== "droite") return null;
    elements.push({ nom, direction });
  }
  return gymFromAnalysis(elements, text);
}

/* ────────────────────────── FAMILLE ATELIER ────────────────────────────── */
/* DISPOSITIFS d'organisation pédagogique pris en charge par un moteur dédié.
   « file/relais/duel/défi » : une ou deux files d'attente exécutant un geste
   chacun leur tour vers une cible (ex. basket : duel de tir en course). */
const RELAY_DISPOSITIFS = new Set(["file", "relais", "duel", "defi", "défi"]);
const clampInt = (v, lo, hi) => { const n = parseInt(v, 10); return (Number.isFinite(n) && n >= lo && n <= hi) ? n : null; };

function atelierScene(analyse, sport, text) {
  const disp = analyse.dispositif == null ? null : String(analyse.dispositif).toLowerCase().trim();
  const geste = String(analyse.geste || "").toLowerCase();
  // (a) LANCER SUR CIBLE : geste de lancer/visée OU dispositif "cible" -> atelier
  // de visée (files de lanceurs + cônes-cibles). MAIS une ROTATION avec une vraie
  // liste d'ateliers nommés n'est PAS un drill de cible (même si un atelier s'appelle
  // « lancer ») -> on ne déclenche la cible que pour dispositif "cible" ou un geste de
  // lancer SANS liste d'ateliers.
  const hasAteliers = Array.isArray(analyse.ateliers) && analyse.ateliers.length >= 2;
  // GESTE DE LOCOMOTION / DE PASSE classé « cible » par l'IA (conduite vers des
  // carrés de couleur, passes dans des portes…) : le gabarit « lancer sur cône »
  // serait un CONTRESENS -> repli (les templates locaux dédiés prennent la main).
  const locomotion = /conduite|dribble|navette|slalom|\bpasse|course/.test(geste) && !/lanc|tir/.test(geste);
  const tl = String(text || "").toLowerCase();
  // ÉCHANGE PAR BINÔMES face à face (échauffement de lancers) : deux partenaires
  // se renvoient l'engin — ni une cible à faire tomber, ni un relais/duel.
  if (/bin[ôo]mes?|par (?:deux|2)\b|deux par deux/.test(tl) && /face [àa] face|face-[àa]-face|l'un en face/.test(tl)
    && /lanc|passe|renvoi|[ée]chang/.test(geste + " " + tl)) return buildPairExchange(text, sport);
  // Un geste de LANCER sans cible réelle dans le texte (cône, quille, cible à
  // toucher/renverser) n'est PAS un drill de visée.
  const hasTarget = /cible|c[ôo]ne|quille|faire tomber|toucher|renverser|panier/.test(tl);
  // TIR AU BUT dans un sport à but : « dispositif cible » de l'IA est un
  // CONTRESENS (le gabarit de visée dessinait une cible d'archerie et des
  // « Lanceurs » pour des tirs au but avec gardien) -> repli (le gabarit local
  // tirs_multi_postes ou le pipeline prennent la main).
  const goalShot = GOAL_SPORTS.includes(sport) && /\bbuts?\b|gardien|\bcages?\b|panier/.test(tl);
  // SERVICE / FRAPPE DE FILET SUR CIBLES — « le serveur vise 2 zones cibles » :
  // ce n'est PAS un lancer sur cônes (le gabarit d'archerie rendait « Lancer
  // (revers) sur le cône »). Un geste de RAQUETTE au-dessus d'un filet vers des
  // cibles est un dispositif ZONES orienté filet (serveur derrière sa ligne,
  // cibles dans le camp adverse) — même rendu que « service dans 4 zones ».
  // Le GESTE (pas le seul texte) doit être un ENVOI de raquette (service,
  // dégagement, amorti, smash…) et non une locomotion (course, déplacement) ;
  // et la cible doit être EXPLICITE (dispositif « cible » ou « zones cibles »).
  const sendingStroke = /service|\bserv|d[ée]gag|amorti|smash|\bfrappe|coup droit|\brevers\b/.test(geste)
    && !/course|conduite|dribble|d[ée]placement|slalom|navette/.test(geste) && !/lanc/.test(geste);
  const explicitTargets = disp === "cible" || /zones? cibles?/.test(tl);
  if (SPORTS_FILET.has(sport) && sendingStroke && explicitTargets && !hasAteliers) {
    const nm = tl.match(/(\d+|deux|trois|quatre|cinq|six)\s+(?:zones?|cibles?|cerceaux?)/);
    const NW = { deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6 };
    const nz = clampInt(nm ? (NW[nm[1]] || parseInt(nm[1], 10)) : 3, 2, 6) ?? 3;
    return targetZonesFromAnalysis({ n: nz, labels: [], geste: /service|serv/.test(geste + " " + tl) ? "service" : geste || "frappe",
      actorRole: "serveur", layout: /fond|profondeur|long|court\b/.test(tl) ? "profondeur" : "largeur",
      casier: /casier|caisse|panier de ball/.test(tl), cerceaux: /cerceaux?/.test(tl), cerceauxParZone: 1,
      nActors: 1, box: false, aide: null, observer: observerFromText(tl) }, sport);
  }
  if ((disp === "cible" && !locomotion && !goalShot) || (/lanc|viser|vise|tir.*cible|faire tomber/.test(geste) && !hasAteliers && hasTarget && !goalShot)) {
    const eff = analyse.effectif || {};
    // « equipes » = nb de groupes/files (>= 2 pour un schéma lisible ; défaut 3).
    return buildTargetThrow("", sport, { lanes: clampInt(eff.equipes, 2, 5) ?? 3, per: clampInt(eff.parEquipe != null ? eff.parEquipe : eff.par_equipe, 2, 4) ?? 3 });
  }
  // (b) DISPOSITIF file/relais/duel -> moteur dédié (effectif + geste). Un
  // dispositif inconnu retombe sur le repli pipeline (null).
  // GESTE DE PASSE : une « file » dont le geste est la PASSE n'est pas un relais
  // de conduite-tir — repli vers les gabarits locaux de passes (progression…),
  // sinon l'atelier devenait une conduite (ou un duel) sans rapport. Exception :
  // le PASSE-ET-SUIT, que le moteur de files gère nativement (un ballon par file).
  if (disp && RELAY_DISPOSITIFS.has(disp) && /\bpasses?\b/.test(geste) && !/tir|frapp|lanc/.test(geste)
    && !/pass[ée]?[\s-]*(?:et[\s-]*)?sui(?:t|vent)/.test(String(text || "").toLowerCase())) return null;
  if (disp && RELAY_DISPOSITIFS.has(disp)) {
    const eff = analyse.effectif || {};
    // Un DUEL oppose 2 files ; une FILE/RELAIS peut en aligner plusieurs (« 3 couloirs,
    // un joueur par couloir ») -> jusqu'à 4 files au lieu de plafonner à 2.
    const isDuel = /duel|defi|défi/.test(disp);
    const teams = isDuel ? 2 : (clampInt(eff.equipes, 1, 4) ?? 1);
    const per = clampInt(eff.parEquipe != null ? eff.parEquipe : eff.par_equipe, 1, 6) ?? 3;
    const geste = analyse.geste == null ? null : String(analyse.geste).slice(0, 30);
    // Le TEXTE passe au moteur : il y lit le matériel (slalom entre N plots/
    // cerceaux, espacement coté) que l'analyse IA ne structure pas.
    return buildDribbleShootRelay(text || "", sport, { teams, per, geste });
  }
  // PONT vers la primitive « zones + rôles » : un atelier qui est en fait un
  // terrain découpé en ZONES d'entraînement à un GESTE (1 acteur par zone + son
  // opposé) — ex. 4 zones de service. On le rend comme un dispositif de zones.
  if (geste && Array.isArray(analyse.ateliers) && analyse.ateliers.length >= 2
    && analyse.ateliers.every((a) => /zone|couloir|bande/i.test((a && a.nom) || ""))) {
    const z = zonesDispositifScene(analyse, sport, text);
    if (z) return z;
  }
  // (b) Liste d'ateliers NOMMÉS (tous sports) : noms + rotation éventuelle. Une
  // SEULE station est admise (ex. « déplacement en étoile ») : la dégrader en repli
  // donnait un échange à 2 sans rapport — mieux vaut dessiner la station.
  if (!Array.isArray(analyse.ateliers) || analyse.ateliers.length < 1 || analyse.ateliers.length > 8) return null;
  const items = [];
  for (const a of analyse.ateliers) {
    const nom = String(a?.nom || "").trim();
    if (!nom) return null;
    items.push({ nom: nom.slice(0, 80) });   // nom détaillé : on TRONQUE plutôt que rejeter toute l'analyse
  }
  return ateliersFromList(items, !!analyse.rotation);
}

/* ───────────────────── FAMILLE DISPOSITIF (aménagement matériel) ─────────────
   Générique, toutes activités : un dispositif (couloir/aire) + des repères
   COTÉS (plots, cibles, lignes) + des rôles (coureur/lanceur/observateur/
   chrono/starter) + un geste mesuré. Validation à ENUMS FERMÉS : toute valeur
   hors liste -> null (repli pipeline), jamais de schéma bancal. */
const D_FORMES = new Set(["couloir", "aire", "demi_terrain", "libre"]);
const D_REPERES = new Set(["plot", "cible", "ligne", "zone", "haie"]);
const D_ROLES = new Set(["coureur", "lanceur", "observateur", "chronometreur", "starter"]);
const D_GESTES = new Set(["sprint", "lancer", "saut", "course"]);
const numOrNull = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 400) ? v : null;

/* PRIMITIVE « ZONES + RÔLES » (terrain découpé en N bandes, 1 rôle par bande +
   opposé + geste). Validation LÉGÈRE et tolérante : la structure exacte importe
   peu (zones/ateliers/labels acceptés), on dérive un défaut raisonnable depuis
   le geste + l'effectif. Réutilisable par la famille dispositif ET en pont depuis
   un atelier « zones d'entraînement à un geste ». */
function zonesDispositifScene(analyse, sport, text = "") {
  const labels = (Array.isArray(analyse.labels) ? analyse.labels
    : Array.isArray(analyse.zones) ? analyse.zones.map((z) => (typeof z === "string" ? z : z && z.label))
    : Array.isArray(analyse.ateliers) ? analyse.ateliers.map((a) => a && a.nom)
    : []).filter((x) => x != null).map((s) => String(s).slice(0, 40));
  const n = clampInt(analyse.zones, 2, 6) ?? clampInt(analyse.n, 2, 6) ?? (labels.length >= 2 ? Math.min(labels.length, 6) : null);
  if (n == null) return null;
  const sens = String(analyse.sens || analyse.orientation || "").toLowerCase();
  // SPORTS DE FILET : le filet coupe le terrain VERTICALEMENT (x=50) — des
  // « tranches » empilées en travers n'ont pas de sens (serveurs posés en haut
  // qui servent verticalement) : les zones y sont toujours des COULOIRS
  // (l'exécutant à gauche, la zone visée en face, le geste traverse le filet).
  const orientation = /tranche|profondeur|vertical/.test(sens) && !SPORTS_FILET.has(sport) ? "tranches" : "couloirs";
  const geste = analyse.geste == null ? null : String(analyse.geste).slice(0, 30);
  let roles = (Array.isArray(analyse.roles) ? analyse.roles : []).map((r) => ({
    role: String((r && r.role) || r || "joueur").slice(0, 24),
    cote: String((r && (r.cote || r.côté || r.position)) || "").slice(0, 24),
  })).filter((r) => r.role).slice(0, 4);
  if (!roles.length) {
    const g = String(geste || "").toLowerCase();
    const eff = analyse.effectif || {};
    const hasOpp = (clampInt(eff.parEquipe != null ? eff.parEquipe : eff.par_equipe, 1, 6) ?? 1) >= 2;
    if (/service|renvoi/.test(g)) { roles = [{ role: "serveur", cote: "depart" }]; if (hasOpp) roles.push({ role: "réceptionneur", cote: "oppose" }); }
    else if (/passe/.test(g)) { roles = [{ role: "passeur", cote: "depart" }]; if (hasOpp) roles.push({ role: "partenaire", cote: "oppose" }); }
    else if (/tir|frapp|lanc/.test(g)) roles = [{ role: "tireur", cote: "depart" }, { role: "cible", cote: "oppose" }];
    else { roles = [{ role: "joueur", cote: "depart" }]; if (hasOpp) roles.push({ role: "partenaire", cote: "oppose" }); }
  }
  // ── ZONES CIBLES visées par UN SEUL exécutant ──────────────────────────────
  // « un serveur/lanceur/tireur enchaîne N envois pour atteindre N zones cibles
  // du terrain (adverse) découpé en carrés/bandes/couloirs » : UN acteur + une
  // GRILLE de cibles en face + N flèches en éventail — et NON un exécutant PAR
  // bande. Signature : geste d'ENVOI, un seul exécutant au départ, AUCUN
  // exécutant en face (un rôle « cible »/observateur ne compte pas), et AUCUN
  // indice PER-LANE dans le texte (« dans chaque couloir », « un serveur par
  // zone », « chaque serveur », « N serveurs ») — ceux-là restent des postes
  // parallèles (zonesFromAnalysis). NB : « 1 point par zone » n'est PAS per-lane
  // (le mot devant « par » n'est pas un exécutant).
  const t = String(text || "").toLowerCase();
  const sending = /service|renvoi|lanc|tir|frapp|shoot|smash|attaq|passe|mise en jeu|engage|pied|bott|kick|d[ée]gag/.test(String(geste || "").toLowerCase());
  const isExec = (role) => !/cible|observ|chrono|starter|arbitre|juge|marqueur|secr[ée]taire|coach|entra[îi]neur|plot|cerceau/i.test(role);
  const isOppose = (cote) => /oppos|avant|recep|récep|face|but|cible|retour|adverse/i.test(String(cote || "").toLowerCase());
  // Un PARTENAIRE-AIDE (« déplace/replace le cerceau ») n'est pas un exécutant :
  // il ne doit ni bloquer le mode cible ni être répliqué par bande.
  const aideMateriel = helperPartnerInText(t);
  const isExecHere = (role) => isExec(role) && !(aideMateriel && /partenaire/i.test(role));
  const departExec = roles.filter((r) => isExecHere(r.role) && !isOppose(r.cote));
  const opposeExec = roles.filter((r) => isExecHere(r.role) && isOppose(r.cote));
  // PER-LANE seulement si un EXÉCUTANT est DISTRIBUÉ (un X par/dans chaque
  // couloir, chaque serveur, N serveurs) — « LE serveur sert dans chaque
  // couloir » (un seul acteur qui vise tour à tour) reste une VISÉE de cibles.
  const EXEC = "serveur|tireur|lanceur|passeur|joueur|[ée]l[èe]ve|bin[ôo]me|groupe|[ée]quipe";
  const LANE = "couloir|zone|bande|carr[ée]|table";
  const perLane = new RegExp(
    `(?:un|une|1|deux|trois|quatre|cinq|six)\\s+(?:${EXEC})\\s+(?:par|dans chaque)\\s+(?:${LANE})`
    + `|dans chaque\\s+(?:${LANE})[^.]{0,20}(?:un|une|1|le|deux|trois|quatre)\\s+(?:${EXEC})`
    + `|chaque\\s+(?:${EXEC})`
    + `|(?:${EXEC})s\\s+par\\s+(?:${LANE})`
    + "|(?:\\d+|deux|trois|quatre|cinq|six)\\s+(?:serveurs|tireurs|lanceurs|passeurs)",
    "i").test(t);
  // SIGNAL TEXTE décisif : « terrain (adverse) découpé/coupé/divisé en N
  // couloirs/carrés/bandes/zones » avec un SUJET UNIQUE qui vise (« le serveur »,
  // « un joueur ») — ou l'expression « zones cibles ». Il FORCE le mode cible
  // même si l'IA hallucine un exécutant en face (un réceptionneur par couloir).
  const decoupeN = /(?:d[ée]coup|coup[ée]|divis|partag|s[ée]par)[^.]{0,24}\ben\b[^.]{0,6}(?:\d+|deux|trois|quatre|cinq|six)\s*(?:couloirs?|carr[ée]s?|bandes?|zones?|tranches?|espaces?|parties?)/i.test(t)
    || /(?:\d+|deux|trois|quatre|cinq|six)\s+(?:couloirs?|carr[ée]s?|bandes?|zones?)\s+(?:cibles?|du terrain|d['e ]en face|adverse)/i.test(t);
  const aimingSubject = /\b(?:le|la|un|une)\s+(?:serveur|serveuse|lanceur|tireur|joueur|joueuse|passeur)\b/i.test(t);
  const targetSignal = /zones? cibles?|\bcibles?\b/.test(t) || (decoupeN && aimingSubject);
  // Disposition lue dans le TEXTE (l'IA étiquette mal : « 4 carrés » -> souvent
  // « tranches »). couloirs -> bandes en largeur ; bandes/tranches/avant-arrière
  // -> échelonnées en profondeur ; carrés/quadrillage -> grille.
  const layout = /couloirs?/.test(t) ? "largeur"
    : /bandes?|tranches?|profondeur|avant[^.]{0,24}arri[èe]re|court[^.]{0,18}long/.test(t) ? "profondeur"
    : /carr[ée]s?|quadrill|damier|grille/.test(t) ? "grille"
    : n <= 2 ? "largeur" : "grille";
  // Observateur / juge / chronométreur : lu dans le TEXTE UNIQUEMENT (un rôle
  // IA sans trace dans le texte est une hallucination — on ne le dessine pas).
  const observer = observerFromText(t);

  // ── TIR AU BUT DEPUIS DES ZONES (≠ viser des cibles) ───────────────────────
  // Sport à but/panier + geste de tir + on MARQUE (but/panier) : les zones sont
  // des ESPACES d'où l'on tire, la cible est le BUT. On tire de chaque zone vers
  // le but (buildShootingZones), tout le terrain est découpé. Sauf si le texte
  // dit explicitement que les ZONES sont les cibles (« zones cibles », « viser
  // les zones », « vers les zones »).
  const GOAL_SPORTS = ["football", "futsal", "handball", "basketball", "basket3x3"];
  const shotGeste = /tir|frapp|shoot|lay|dunk|marqu/.test(String(geste || "").toLowerCase());
  const zonesAreTargets = /zones? cibles?|\bvise[rz]?\b|atteindre|toucher\s+(?:chaque|les|toutes|\d)|vers\s+(?:les|des|la|une|chaque)\s+zones?/.test(t);
  // !perLane : « dans chaque couloir un tireur » = N tireurs en postes
  // parallèles (zonesFromAnalysis), pas un seul tireur qui parcourt les zones.
  // Défi/duel à N joueurs (« défi entre 2 joueurs ») : N exécutants au départ.
  const nActors = defiActorsCount(t);
  const goalShooting = GOAL_SPORTS.includes(sport) && shotGeste && !perLane
    && /marqu|\bpanier\b|\bbut\b|\bcage\b|\bfilet\b/.test(t) && !zonesAreTargets;
  if (goalShooting) return buildShootingZones({ n, labels, geste, layout, nActors, observer }, sport);

  if (sending && !perLane && departExec.length <= 1 && (opposeExec.length === 0 || targetSignal)) {
    const casier = /casier|caisse|panier de ball|r[ée]serve de ball|chariot|seau de ball/.test(t);
    const cerceaux = /cerceaux?/.test(t);
    const box = /carr[ée]s? de service/.test(t);
    return targetZonesFromAnalysis({ n, labels, geste, actorRole: departExec[0] || null, layout, casier, cerceaux,
      cerceauxParZone: cerceauxPerZoneCount(t), nActors, box, aide: helperPartnerInText(t), observer }, sport);
  }
  // Repli PER-LANE : les rôles d'observation (juge/arbitre/chrono…) et le
  // partenaire-aide ne sont pas des postes à répliquer par bande.
  const laneRoles = roles.filter((r) => isExecHere(r.role) || /cible/i.test(r.role));
  if (n * Math.max(1, laneRoles.length) > 24) return null;
  const laneScene = zonesFromAnalysis({ n, orientation, labels, geste, roles: laneRoles }, sport);
  if (laneScene && observer) pushObserver(laneScene.players, laneScene.markers, observer);
  // ZONES SANS EXÉCUTANT (filet) — « l'entraîneur frappe des balles dans la
  // zone qu'il annonce, le défenseur concerné joue la manchette » : les bandes
  // n'avaient AUCUNE action (personne au départ). L'entraîneur-frappeur est
  // ajouté EN FACE (un seul, pas un par bande) avec un éventail de frappes
  // vers chaque joueur posté.
  if (laneScene && SPORTS_FILET.has(sport) && !(laneScene.actions || []).length
    && /entra[iî]neur|enseignant|coach/.test(t) && /frappe|envoie|lance|smashe/.test(t)) {
    const posted = (laneScene.players || []).filter((p) => p.team === "B");
    if (posted.length) {
      const fx = (posted.reduce((s, p) => s + p.x, 0) / posted.length) < 50 ? 70 : 30;
      laneScene.players.push({ id: "COACH", num: 1, role: "Entraîneur", team: "A", color: "#0072B2", x: fx, y: 50 });
      (laneScene.markers || (laneScene.markers = [])).push({ type: "ballon", x: fx + (fx < 50 ? 4 : -4), y: 46 });
      posted.slice(0, 4).forEach((p, i) => (laneScene.actions || (laneScene.actions = [])).push(
        { from: "COACH", tox: p.x + (fx < p.x ? -5 : 5), toy: p.y, type: "renvoi", order: i + 1, label: i === 0 ? "Frappe dans la zone annoncée" : null }));
    }
  }
  // Lignes de séparation MATÉRIALISÉES (« couloirs matérialisés par des lignes
  // de coupelles ») : des plots jalonnent chaque séparation dessinée.
  if (laneScene && /coupelles?|plots?|c[ôo]nes?/.test(t) && /mat[ée]rialis|lignes? de|d[ée]limit|balis|s[ée]par[ée]/.test(t)) {
    const mk = laneScene.markers || (laneScene.markers = []);
    for (const z of laneScene.zones || []) {
      if (z.label || z.style !== "dashed") continue;
      if (z.h === 0 && z.w > 0) for (let k = 0; k <= 4; k++) mk.push({ type: "plot", x: Math.round(z.x + (z.w * k) / 4), y: z.y });
      else if (z.w === 0 && z.h > 0) for (let k = 0; k <= 4; k++) mk.push({ type: "plot", x: z.x, y: Math.round(z.y + (z.h * k) / 4) });
    }
  }
  return laneScene;
}

function dispositifScene(analyse, sport, text) {
  const forme = analyse.forme == null ? "couloir" : String(analyse.forme).toLowerCase().trim();
  // CIRCUIT EN BOUCLE (course de durée) : un « circuit de 200 m » n'est pas un
  // couloir linéaire — anneau de plots + coureur qui boucle + compteur de tours.
  // (Exclut les « circuits d'ateliers », qui restent des rotations.)
  const tl = String(text || "").toLowerCase();
  if (/circuit|en boucle/.test(tl) && !/ateliers?/.test(tl)
    && /course|courir|sprint/.test(String(analyse.geste || "") + " " + tl)) return buildCircuitLoop(text);
  if (forme === "zones") return zonesDispositifScene(analyse, sport, text);
  if (!D_FORMES.has(forme)) return null;
  const geste = analyse.geste == null ? null : String(analyse.geste).toLowerCase().trim();
  // SERVICE / FRAPPE DE FILET SUR CIBLES (forme aire + reperes cibles) : un
  // geste de RAQUETTE au-dessus du filet n'est pas un aménagement d'athlétisme
  // (« service » n'est pas dans D_GESTES) — c'est un service-sur-zones orienté
  // filet (serveur derrière sa ligne, cibles dans le camp adverse), même rendu
  // que l'atelier « cible » de filet (cf. atelierScene).
  if (SPORTS_FILET.has(sport) && geste && /service|\bserv|d[ée]gag|amorti|smash|\bfrappe/.test(geste)) {
    const cibles = (Array.isArray(analyse.reperes) ? analyse.reperes : []).filter((r) => /cible/i.test(String(r?.type || "")));
    const nz = Math.max(2, Math.min(6, cibles.length || 3));
    return targetZonesFromAnalysis({ n: nz, labels: cibles.map((r) => r.label).filter(Boolean),
      geste: "service", actorRole: "serveur", layout: "profondeur", casier: false,
      cerceaux: /cerceaux?/.test(tl), cerceauxParZone: 1, nActors: 1,
      box: /carr[ée]s? de service/.test(tl), aide: null, observer: observerFromText(tl) }, sport);
  }
  if (geste != null && !D_GESTES.has(geste)) return null;

  const reperes = [];
  for (const r of (Array.isArray(analyse.reperes) ? analyse.reperes : [])) {
    // Type inconnu -> repère générique (plot) plutôt que rejeter TOUTE l'analyse
    // (un repère exotique ne doit pas faire tomber sur un mauvais repli local).
    const raw = String(r?.type || "").toLowerCase().trim();
    const type = D_REPERES.has(raw) ? raw : "plot";
    const label = r.label == null ? null : String(r.label).slice(0, 40);
    const de = numOrNull(r.de_m), a = numOrNull(r.a_m), pas = numOrNull(r.pas_m), d1 = numOrNull(r.distance_m);
    if (type === "zone" && de != null && a != null && a >= de) {
      // UNE ZONE EST UN INTERVALLE CONTINU, pas une série de repères : la
      // « zone de transmission de 10 à 30 m » se dessine en UNE bande. Le
      // ré-échantillonnage par pas ci-dessous vaut pour des repères RÉPÉTÉS
      // (haies tous les 5 m, plots échelonnés) — appliqué à une zone il
      // produisait CINQ rectangles empilés portant le même libellé (cas réel
      // remonté par l'enseignant : relais 4×40 avec zone de transmission).
      reperes.push({ type, dist: Math.round(de), dist2: Math.round(a), label });
    } else if (de != null && a != null && a >= de) {
      const step = pas && pas > 0 ? pas : Math.max(1, Math.round((a - de) / 4) || 1);
      for (let d = de; d <= a + 0.001 && reperes.length < 24; d += step) reperes.push({ type, dist: Math.round(d), label });
    } else {
      reperes.push({ type, dist: d1, label });
    }
    if (reperes.length > 24) return null;
  }

  const roles = [];
  let people = 0;
  for (const r of (Array.isArray(analyse.roles) ? analyse.roles : [])) {
    // Rôle inconnu (« joueur », « élève »…) -> mover générique (coureur) plutôt que
    // de REJETER toute l'analyse, ce qui faisait tomber sur un mauvais repli local
    // (ex. badminton « déplacement en étoile » rendu en échange à 2 joueurs).
    const raw = String(r?.role || "").toLowerCase().trim();
    const role = D_ROLES.has(raw) ? raw : "coureur";
    const n = clampInt(r.n, 1, 8) ?? 1;
    people += n;
    if (people > 16) return null;
    // Le libellé D'ORIGINE est conservé (« attaquant », « défenseur »…) : le
    // moteur colore l'équipe et étiquette le rôle — deux groupes normalisés en
    // « coureur » se superposaient au même point de départ.
    roles.push({ role, n, au: Number.isInteger(r.au_repere) ? r.au_repere : null,
      label: raw && raw !== role ? raw.slice(0, 20) : null });
  }
  if (!reperes.length && !roles.length) return null;   // rien à dessiner -> repli

  const signal = analyse.signal == null ? null : String(analyse.signal).slice(0, 80);
  const mesure = analyse.mesure == null ? null : String(analyse.mesure).slice(0, 80);
  return dispositifFromAnalysis({ forme, reperes, roles, geste, signal, mesure, texte: text }, sport);
}

/* Garde-fou appliqué à TOUTE scène avant rendu : borne les coordonnées et
   sépare les entités superposées (corrige les retours IA dégénérés). */
export function sanitizeScene(data) {
  const ps = Array.isArray(data?.players) ? data.players : [];
  // EXCEPTION : une scène POSÉE sur la piste (relais — chaque acteur est sur la
  // ligne médiane de SON couloir, et deux couloirs voisins ne sont distants que
  // de 4 unités) serait détruite par l'écartement à 9 unités ci-dessous : les
  // équipes quitteraient leur couloir. Le gabarit garantit déjà le placement.
  // `stadePose` : même raison, pour une scène projetée sur les couloirs par
  // applyStadeTerrain (route scène directe). Sans cette exception, l'écartement
  // DÉFAISAIT la projection — mesuré sur la salve athlé nº2 : 46 joueurs
  // déplacés sur 6 des 13 scènes, et TROIS scènes voyaient la pelouse se
  // repeupler après coup. Entre « dans son couloir » et « écarté de 9 unités »,
  // c'est le couloir qui gagne : c'est le reproche nº1 de l'enseignant.
  if (data?.terrainOpts?.stadePlace || data?.terrainOpts?.stadePose) {
    ps.forEach((p) => {
      if (!Number.isFinite(+p.x)) p.x = 50;
      if (!Number.isFinite(+p.y)) p.y = 50;
    });
    /* …MAIS L'EXCEPTION ÉTAIT TOUT-OU-RIEN, et elle épargnait aussi les pions
       posés EXACTEMENT au même point. Trouvé à l'inspection visuelle d'une salve
       de lancement : « relais navette par équipes de 4 » sortait avec
       A1=A2=A3=A4=(11.8, 86) — quatre pastilles ET quatre étiquettes empilées,
       parfaitement illisibles, et SERVIES (la lisibilité ne bloque jamais). Le
       juge, lui, les signalait : 12 paires. Une règle qui signale sans
       correcteur en face, alors que le correcteur existait à trois lignes.
       Des pions au même point ne sont JAMAIS un choix du gabarit. On les
       échelonne LE LONG du couloir — x seul, y intact : c'est le couloir que
       cette exception protège, et il est préservé au millimètre.
       Mesuré : 3 scènes concernées (2 au repli, 1 en salve) sur 1155 + 946
       scènes archivées ; tout le reste est déjà réparé par l'écartement
       général ci-dessous. */
    const PAS = 6;
    const poses = [];
    for (const p of ps) {
      let garde = 0;
      while (poses.some((q) => Math.hypot(+q.x - +p.x, +q.y - +p.y) < 1) && garde++ < 12)
        p.x = Math.max(3, Math.min(97, +p.x + PAS));
      poses.push(p);
    }
    return data;
  }
  ps.forEach((p) => {
    p.x = Math.max(3, Math.min(97, Number(p.x)));
    p.y = Math.max(5, Math.min(95, Number(p.y)));
    if (!Number.isFinite(p.x)) p.x = 50;
    if (!Number.isFinite(p.y)) p.y = 50;
  });
  const MIN = 9;
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) {
      const a = ps[i], b = ps[j];
      let dx = a.x - b.x, dy = a.y - b.y, d = Math.hypot(dx, dy);
      if (d < MIN) {
        moved = true;
        const ang = d > 0.01 ? Math.atan2(dy, dx) : (i * 2.39996);
        const push = (MIN - d) / 2 + 0.4;
        a.x += Math.cos(ang) * push; a.y += Math.sin(ang) * push;
        b.x -= Math.cos(ang) * push; b.y -= Math.sin(ang) * push;
      }
    }
    if (!moved) break;
  }
  ps.forEach((p) => { p.x = Math.max(3, Math.min(97, p.x)); p.y = Math.max(5, Math.min(95, p.y)); });
  return data;
}
